/**
 * `/members/:id` — a member's dedicated page.
 *
 * Reached by clicking a member's id or name in the `/data/members` browser.
 * Renders one read-only snapshot of everything about that person: account
 * details, their member-memory markdown, past + upcoming appointments, past +
 * upcoming payments, their credit grants (memberships + packages), their
 * WhatsApp communication thread, and which marketing flow(s) they're enrolled
 * in. All sourced from `GET /api/members/:id` (one aggregate fetch).
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
  type UIEvent as ReactUIEvent,
} from "react";
import { createPortal } from "react-dom";
import { ProfileEditRow } from "../components/ProfileEditRow";
import { ExistingMemberLink } from "../components/ExistingMemberLink";
import { ErasedNotice, RecordLock } from "../components/MemberErased";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  type AiEnrichment,
  type CalendarSlot,
  type LastEmailEvent,
  type MemberChannelAi,
  type MemberDeliverability,
  type MemberConversationResponse,
  type MemberDetailAppointment,
  type MemberDetailConsultationForm,
  type MemberDetailFlow,
  type MemberDetailJourneyEvent,
  type MemberDetailMembership,
  type MemberDetailMemory,
  type MemberDetailMessage,
  type MemberDetailPackage,
  type MemberDetailPayment,
  type MemberDetailResponse,
  type MemberDetailSessionNote,
  type MemberDetailTag,
  type MemberDetailUpcomingPayment,
  type MemberDetailAccount,
  type MemberLateChangeWaiver,
  type MemberNewsletterStanding,
} from "../lib/api";
import { CollapsibleJson } from "../components/json-tree";
import { MEMBER_AI_UNSET_DEFAULT_OFF_CHANNELS } from "@layer/canonical-data/connectors";
import { STRIPE_COUPON_NAME_MAX_LENGTH } from "@layer/canonical-data/stripe-coupon";
import { useBackdropDismiss } from "../lib/use-backdrop-dismiss";
import { useDispatch } from "../lib/use-dispatch";
import { failureText } from "../lib/field-errors";
import {
  describeExpiryConsequence,
  initialExpiryDraft,
  isExpiryUnchanged,
  parseExpiryDraft,
  type ExpiryDraft,
} from "../lib/package-expiry-edit";
import {
  availableReplyChannels,
  defaultReplyChannel,
  type ReplyChannel,
} from "../lib/reply-channels";
import { dispatchAction } from "../lib/dispatch";
import { bookPreflightFindsNoCredit } from "../lib/book-credit-preflight";
import { sessionNoteDeleteConfirmMessage } from "../lib/session-note-edit";
import { channelAiConfirmMessage } from "../lib/channel-ai-confirm";
import { useSagaRun } from "../lib/use-saga-run";
import { SessionNoteEditor } from "../components/SessionNoteEditor";
import { PurchaseCreditsModal } from "../components/PurchaseCreditsModal";
import {
  MembershipCreditsList,
  PackageCreditsList,
} from "../components/CreditsList";
import { TransferCreditsModal } from "../components/TransferCreditsModal";
import {
  MergeMemberModal,
  type MergeMemberCounts,
} from "../components/MergeMemberModal";
import { SwapCreditModal } from "../components/SwapCreditModal";
import { CancelBookingModal } from "../components/CancelBookingModal";
import { buildCalendarDeepLink } from "../lib/calendar-deep-link";
import {
  ActionModal,
  ActionModalButton,
  type ActionField,
} from "../components/ActionModalButton";
import { parseMultiSelectValue } from "../components/MultiSelectField";
import { PopMenu } from "../components/PopMenu";
// The Book picker draws a day through the SAME timeline the Calendar page uses.
import {
  CalendarTimeline,
  localDateKey,
  slotStartMin,
  slotDurationMin,
} from "../components/CalendarTimeline";
// 2026-07-20 — the member page's conversation card IS this shared component; it
// previously carried a byte-for-byte copy of the same render inline.
import { ConversationView } from "../components/ConversationView";
import { PictureIntakePhotoButton } from "../components/PictureIntakePhoto";
import {
  consultationFormEndedLine,
  consultationFormSentLine,
  consultationFormStatusLabel,
} from "../lib/picture-intakes";
import { joinedLine } from "../lib/newsletter-subscribers";
import { membersListHref } from "../lib/browse-filters";
import { useCachedResource } from "../lib/resource-cache";
import {
  studioFormat,
  studioTimeZone,
  isoToZonedDateKey,
  zonedDatetimeInputToIso,
} from "../lib/studio-tz";
import {
  cleanProfileFieldValue,
  MEMBER_GENDER_OPTIONS,
  normalizeMemberGender,
} from "@layer/canonical-data/member-profile-gaps";

/** Read the staff watch flag off a member's free-form metadata, defensively. */
function readStaffFlag(metadata: Record<string, unknown>): string {
  const v = metadata["staff_flag"];
  return typeof v === "string" ? v : "";
}

/**
 * Per-member session-feedback switch off the member metadata. Default ON since
 * 2026-10-21 (session-feedback-cadence; strict On/Off with no "inherit studio
 * default" state since 2026-07-27): ONLY an explicit `false` is Off, and unset
 * reads On — mirrors the Mem6 saga's `resolveMemberFeedbackOptIn`.
 */
export function readSessionFeedbackOn(metadata: Record<string, unknown>): boolean {
  return metadata["session_feedback_opt_in"] !== false;
}

/**
 * The staff "watch flag" editor — a free-text caution the studio sets (e.g.
 * "knee surgery in Aug — go gentle"), stored at `members.metadata.staff_flag`
 * via `update_member`. Either the value (click to edit) or an inline input +
 * Save when empty/editing.
 *
 * This used to be the 4th KPI card; the tags took that slot, so it moved into
 * the Profile-details field list beside Tags. It is deliberately NOT
 * read-only-ised in the move: the flag is what the top alert banner and the
 * trainer morning brief read, so losing the only way to SET one would quietly
 * break both.
 */
function StaffFlagEditor({
  memberId,
  initial,
  onSaved,
}: {
  readonly memberId: string;
  readonly initial: string;
  readonly onSaved: () => void;
}): JSX.Element {
  const flag = initial.trim();
  const [value, setValue] = useState<string>(flag);
  const [editing, setEditing] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch<
    { readonly member_id: string; readonly patch: Record<string, unknown> },
    Record<string, unknown>
  >("update_member");

  const save = async (next: string): Promise<void> => {
    if (dispatch.loading || memberId === "") return;
    // Shallow metadata merge in `update_member` preserves vest/trouser/
    // consultation; an empty string clears the flag (the brief treats blank as
    // "no flag").
    const r = await dispatch.run({
      member_id: memberId,
      patch: { metadata: { staff_flag: next } },
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  // Same two-step as the tag row: the first click reveals the field, the
  // second commits.
  const onButton = (): void => {
    if (!editing) {
      setValue(flag);
      setEditing(true);
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    void save(value.trim());
  };

  return (
    <dd className="flag-field">
      <div className="member-tags-chips">
        {flag.length > 0 ? (
          <span className="member-tag-chip flag-chip" title={flag}>
            {flag}
            <button
              type="button"
              className="member-tag-remove"
              title="Clear this flag"
              disabled={dispatch.loading}
              onClick={() => void save("")}
            >
              ×
            </button>
          </span>
        ) : (
          // A dash, like every other empty value in the list — "No flag" was
          // the only row that spelled its emptiness out.
          <span className="member-tags-empty">—</span>
        )}
      </div>
      <div className="member-tags-add">
        {editing ? (
          <input
            ref={inputRef}
            className="member-tag-input flag-input"
            value={value}
            placeholder="Flag an issue…"
            disabled={dispatch.loading}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
              if (value.trim() === flag) setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void save(value.trim());
              }
              if (e.key === "Escape") {
                setValue(flag);
                setEditing(false);
              }
            }}
          />
        ) : null}
        <button
          type="button"
          className="action-dispatch-btn member-tag-add-btn"
          title={flag.length > 0 ? "Edit this flag" : "Add a flag"}
          disabled={dispatch.loading}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onButton}
        >
          {dispatch.loading ? "…" : flag.length > 0 ? "Edit" : "Add"}
        </button>
      </div>
      {dispatch.error !== null ? (
        <div className="flag-card-err">{dispatch.error.message}</div>
      ) : null}
    </dd>
  );
}

const CHIPS_COMPACT = "tags-card-chips--compact";
const CHIPS_CLAMPED = "tags-card-chips--clamped";

/**
 * How many wrapped rows a flex-wrap container's children occupy — one entry per
 * distinct child `offsetTop`. Works while the container is clipped, because
 * `overflow: hidden` still lays every child out; it only stops painting them.
 */
function countChipRows(el: HTMLElement): number {
  const tops = new Set<number>();
  for (const child of Array.from(el.children)) {
    tops.add(Math.round((child as HTMLElement).offsetTop));
  }
  return tops.size;
}

/**
 * The 4th KPI card: every tag on this member, all in one chip format.
 *
 * Read-only by design — `TagsEditor` in Profile details owns add/remove. This
 * card is the at-a-glance answer to "what kind of member is this?", sat beside
 * credits / next session / next payment, and a remove "×" on a KPI card would
 * make a stray click destructive.
 *
 * Chips get one of three treatments, decided by measurement rather than by tag
 * count (a count can't know how wide the labels are, or how wide the card is):
 *
 *   1 row  — full-size chips, matching the profile-details card, vertically
 *            centred so they sit on the same line as the sibling KPIs' values.
 *   2 rows — shrunk, because two rows of full-size chips do not fit the box the
 *            other KPI cards spend on a single number, and the KPI strip must
 *            stay one height.
 *   3+     — still shrunk, clipped to two rows, with a "…" bottom-right.
 *
 * Applied imperatively in a layout effect rather than through state. The
 * decision is a pure function of rendered geometry, and geometry isn't known
 * until after layout — routing it through `useState` would need a second render
 * pass per measurement, which is a visible flash of the wrong size on every
 * resize. `useLayoutEffect` runs before paint, so the user only ever sees the
 * settled answer. React never writes these two classes itself (the JSX
 * `className` is a constant), so it has nothing to diff them against and won't
 * fight the effect.
 */
function TagsStatCard({
  tags,
}: {
  readonly tags: ReadonlyArray<MemberDetailTag>;
}): JSX.Element {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const chipsRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const chips = chipsRef.current;
    const card = cardRef.current;
    if (chips === null || card === null) return undefined;

    const measure = (): void => {
      // Re-measure from FULL density every time, so the answer depends only on
      // the current width. Measuring while already compact can't tell you
      // whether full size would now fit, so the card could shrink on a narrow
      // window and never grow back when it widened again.
      chips.classList.remove(CHIPS_COMPACT, CHIPS_CLAMPED);
      if (countChipRows(chips) > 1) {
        chips.classList.add(CHIPS_COMPACT);
        if (countChipRows(chips) > 2) chips.classList.add(CHIPS_CLAMPED);
      }
    };

    measure();
    // Observe the CARD, not the chip box: the chip box has a fixed height, so
    // it never reports a resize of its own, and observing an element the
    // callback restyles is how you get a resize loop. The card's width is the
    // input that actually matters.
    const ro = new ResizeObserver(measure);
    ro.observe(card);
    return () => ro.disconnect();
  });

  return (
    <div className="stat-card tags-card" ref={cardRef}>
      <div className="label">Tags</div>
      {tags.length === 0 ? (
        <div className="value member-stat-value">—</div>
      ) : (
        <div className="tags-card-chips" ref={chipsRef}>
          {tags.map((t) => (
            <span
              key={t.tag_id}
              className="tag tags-card-chip"
              title={`${t.slug} · added ${isoToZonedDateKey(t.added_at)}`}
            >
              {t.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tag chip row (Profile "Tags" field). Shows each attached tag as a chip with
// a remove "×"; an input + Add attaches a new tag, auto-creating the catalog
// tag on first use. Dispatches `add_member_tag` / `remove_member_tag` and
// calls `onChanged` to silently refetch the page.
// ---------------------------------------------------------------------------

/** Slugify free text into a tag slug: lowercase, hyphenate, strip invalid. */
function toTagSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

function TagsEditor({
  memberId,
  tags,
  onChanged,
}: {
  readonly memberId: string;
  readonly tags: ReadonlyArray<MemberDetailTag>;
  readonly onChanged: () => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The field is revealed by the first Add click; the second one submits, so
   *  the row is just a button until someone actually wants to add a tag. */
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const add = async (): Promise<void> => {
    if (!adding) {
      setAdding(true);
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    const label = value.trim();
    const slug = toTagSlug(label);
    if (slug === "" || busy) return;
    setBusy(true);
    setError(null);
    try {
      await dispatchAction("add_member_tag", {
        member_id: memberId,
        slug,
        label,
      });
      setValue("");
      setAdding(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "add failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await dispatchAction("remove_member_tag", {
        member_id: memberId,
        slug,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <dd className="member-tags">
      <div className="member-tags-chips">
        {tags.length === 0 ? (
          <span className="member-tags-empty">No tags</span>
        ) : (
          tags.map((t) => (
            <span
              key={t.tag_id}
              className="member-tag-chip"
              title={`${t.slug} · added ${isoToZonedDateKey(t.added_at)}`}
            >
              {t.label}
              <button
                type="button"
                className="member-tag-remove"
                title={`Remove ${t.label}`}
                disabled={busy}
                onClick={() => void remove(t.slug)}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
      <div className="member-tags-add">
        {adding ? (
          <input
            ref={inputRef}
            className="member-tag-input"
            type="text"
            value={value}
            placeholder="Add a tag"
            disabled={busy}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
              // Close again if they clicked away without typing anything, so an
              // abandoned field doesn't sit there open.
              if (value.trim() === "") setAdding(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void add();
              }
              if (e.key === "Escape") {
                setValue("");
                setAdding(false);
              }
            }}
          />
        ) : null}
        <button
          type="button"
          className="action-dispatch-btn member-tag-add-btn"
          title={adding ? "Add this tag" : "Add a tag"}
          // Only the SUBMIT click can be disabled — disabling the reveal click
          // would make the button permanently dead, since the field it needs
          // can't be typed into until it exists.
          disabled={busy || (adding && toTagSlug(value) === "")}
          // The input's blur fires before this click, so a mousedown-time guard
          // keeps the field from closing out from under the button.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void add()}
        >
          {busy ? "…" : "Add"}
        </button>
      </div>
      {error !== null ? <div className="flag-card-err">{error}</div> : null}
    </dd>
  );
}

/**
 * A Profile-details row in the tag row's shape: the current value as chip(s) on
 * the left, an "Edit" button pinned to the row's right edge, and the actual
 * control revealed only once Edit is clicked.
 *
 * The point is that every row in the section reads the same way — a settled
 * value, and one affordance in one place to change it — instead of a segmented
 * control here, a bare select there and a pair of loose checkboxes below. The
 * live controls still do the writing; this only governs when they are on
 * screen.
 */
function ChipEditRow({
  className,
  chips,
  children,
  busy,
  error,
}: {
  readonly className?: string;
  /** The settled value, as chips. */
  readonly chips: ReactNode;
  /** The live control, rendered only while editing. */
  readonly children: ReactNode;
  readonly busy?: boolean;
  readonly error?: string | null;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  return (
    <dd className={`chip-field${className !== undefined ? ` ${className}` : ""}`}>
      <div className="member-tags-chips">{chips}</div>
      <div className="member-tags-add">
        {editing ? <div className="chip-field-editor">{children}</div> : null}
        <button
          type="button"
          className="action-dispatch-btn member-tag-add-btn"
          title={editing ? "Done editing" : "Edit"}
          disabled={busy === true}
          onClick={() => setEditing((v) => !v)}
        >
          {busy === true ? "…" : editing ? "Done" : "Edit"}
        </button>
      </div>
      {error !== null && error !== undefined ? (
        <div className="flag-card-err">{error}</div>
      ) : null}
    </dd>
  );
}

/** An on/off state as a chip — green for on, plain for off. */
function StateChip({
  on,
  onLabel,
  offLabel,
}: {
  readonly on: boolean;
  readonly onLabel?: string;
  readonly offLabel?: string;
}): JSX.Element {
  return (
    <span className={`member-tag-chip state-chip${on ? " is-on" : ""}`}>
      {on ? onLabel ?? "On" : offLabel ?? "Off"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Session-feedback per-member toggle (Profile "Session feedback" field). A
// strict On/Off switch over `members.metadata.session_feedback_opt_in`
// (2026-07-27 — the "Default / inherit studio" state was retired with the
// studio-config flag; 2026-10-21 — default ON):
//   • On  — this member gets the post-session 1–3 rating WhatsApp after the
//           sessions the studio's schedule names (Config → Marketing flows),
//           provided the `session_feedback_request` flow is on in /flows.
//           Unset counts as On.
//   • Off — this member never gets it.
// Persists via `update_member` (shallow metadata merge, writes true/false
// only), then refetches. Mirrors the Mem6 saga's `resolveMemberFeedbackOptIn`.
// ---------------------------------------------------------------------------

function SessionFeedbackToggle({
  memberId,
  metadata,
  onChanged,
}: {
  readonly memberId: string;
  readonly metadata: Record<string, unknown>;
  readonly onChanged: () => void;
}): JSX.Element {
  const current = readSessionFeedbackOn(metadata);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (next: boolean): Promise<void> => {
    if (busy || next === current) return;
    setBusy(true);
    setError(null);
    try {
      await dispatchAction("update_member", {
        member_id: memberId,
        patch: { metadata: { session_feedback_opt_in: next } },
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "update failed");
    } finally {
      setBusy(false);
    }
  };

  const opts: ReadonlyArray<{ key: boolean; label: string }> = [
    { key: true, label: "On" },
    { key: false, label: "Off" },
  ];

  return (
    <ChipEditRow chips={<StateChip on={current} />} busy={busy} error={error}>
      <div
        className="member-feedback-seg"
        role="group"
        aria-label="Session feedback"
      >
        {opts.map((o) => (
          <button
            key={o.label}
            type="button"
            className={
              "member-feedback-opt" + (current === o.key ? " is-active" : "")
            }
            disabled={busy}
            aria-pressed={current === o.key}
            onClick={() => void set(o.key)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="subtle" style={{ margin: "0.35rem 0 0" }}>
        On for every member unless switched Off here. When On, they are asked
        to rate a session after the sessions set on{" "}
        <Link to="/config?section=marketing-flows">Config → Marketing flows</Link>.
      </p>
    </ChipEditRow>
  );
}

// ---------------------------------------------------------------------------
// Post-session capture per-member toggle (Profile "Post-session capture"
// field). A completed session only prompts the trainer (flow T3) to collect a
// member's MISSING profile info when this is ON. Default ON — only an explicit
// `false` at `members.metadata.send_post_session_message` disables it. A member
// switched OFF is never prompted for, even if their trainer's switch is ON.
// ---------------------------------------------------------------------------

/** Default-ON per-member post-session capture switch; only `false` disables. */
function readSendPostSessionMessage(metadata: Record<string, unknown>): boolean {
  return metadata["send_post_session_message"] !== false;
}

/**
 * C2 (2026-07-29) — how the studio may contact this member.
 *
 * Mario: *"add two checkboxes to every member's profile — if the email is not
 * checked then only WhatsApp, if WhatsApp is not checked then only email, if
 * both are not checked then none."*
 *
 * Half of this already existed: `metadata.whatsapp_opt_in` has been read by six
 * sagas for months with no way to see or set it. `email_opt_in` is new; before
 * it, email sends had no per-member gate at all, so an "email" checkbox would
 * have been a lie the UI told.
 *
 * NOTIFICATIONS ONLY. Verification codes, magic links and Stripe receipts are
 * dispatched straight from the member-API handlers and never pass through the
 * flow engine or the transactional-WhatsApp sagas, so they are exempt by
 * STRUCTURE rather than by a carve-out list that would rot.
 *
 * Both default ON when absent — a member carrying no metadata is reachable, not
 * silent. Defaulting the other way would have muted every existing member on
 * deploy.
 */
function ContactPreferenceToggles({
  memberId,
  metadata,
  onChanged,
}: {
  readonly memberId: string;
  readonly metadata: Record<string, unknown>;
  readonly onChanged: () => void;
}): JSX.Element {
  const emailOn = metadata["email_opt_in"] !== false;
  const whatsappOn = metadata["whatsapp_opt_in"] !== false;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (key: string, next: boolean): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await dispatchAction("update_member", {
        member_id: memberId,
        patch: { metadata: { [key]: next } },
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChipEditRow
      busy={busy}
      error={error}
      chips={
        emailOn || whatsappOn ? (
          <>
            {emailOn ? <StateChip on onLabel="Email" /> : null}
            {whatsappOn ? <StateChip on onLabel="WhatsApp" /> : null}
          </>
        ) : (
          /* Both off is a state worth SEEING, not an empty dash: this member
             now receives nothing on any channel. */
          <span className="member-tag-chip state-chip is-off">
            No notifications
          </span>
        )
      }
    >
      <label className="member-postsession-check">
        <input
          type="checkbox"
          checked={emailOn}
          disabled={busy}
          onChange={(e) => void set("email_opt_in", e.target.checked)}
        />{" "}
        Email
      </label>
      <label className="member-postsession-check">
        <input
          type="checkbox"
          checked={whatsappOn}
          disabled={busy}
          onChange={(e) => void set("whatsapp_opt_in", e.target.checked)}
        />{" "}
        WhatsApp
      </label>
    </ChipEditRow>
  );
}

/**
 * The member's marketing unsubscribes, as the senders read them:
 * `members.metadata.flow_opt_outs`, where only a real `true` counts (the
 * readers compare `= 'true'::jsonb`). `all` is the global switch
 * (`all_marketing`); `flows` are the per-flow keys (a flow's `opt_out_check`
 * or its name), sorted.
 */
export function readMarketingOptOuts(metadata: Record<string, unknown>): {
  readonly all: boolean;
  readonly flows: ReadonlyArray<string>;
} {
  const raw = metadata["flow_opt_outs"];
  const outs =
    raw !== null && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  return {
    all: outs["all_marketing"] === true,
    flows: Object.keys(outs)
      .filter((k) => k !== "all_marketing" && outs[k] === true)
      .sort(),
  };
}

/**
 * "Marketing" (flow-loops-whatsapp, 2026-10-01) — whether this member gets
 * marketing at all, and the one place staff can switch it back on.
 *
 * Since 2026-10-01 the member site's "Receive marketing updates" switch turned
 * Off unsubscribes the member from ALL marketing, and turning it back On does
 * not resubscribe them (Mario: "Off = unsubscribed" — the site's chat sign-in
 * posts On by itself, so an On is not proof of the member's choice). Staff
 * resubscribe here, through `set_marketing_opt_out` — the action the
 * unsubscribe page's "all" option writes. Clearing it leaves the per-flow
 * unsubscribes in place: those are the member's answer to one series, and
 * lifting one is a conversation with the member, not a side effect.
 *
 * NOT the "Contact via" switches above: those pick the channels for every
 * notification; this is the member's consent to marketing on any channel.
 */
function MarketingOptOutRow({
  memberId,
  metadata,
  onChanged,
}: {
  readonly memberId: string;
  readonly metadata: Record<string, unknown>;
  readonly onChanged: () => void;
}): JSX.Element {
  const { all, flows } = readMarketingOptOuts(metadata);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (receives: boolean): Promise<void> => {
    if (busy || receives === !all) return;
    setBusy(true);
    setError(null);
    try {
      const r = await dispatchAction("set_marketing_opt_out", {
        member_id: memberId,
        opted_out: !receives,
      });
      // A refused write answers `{ok:false}` rather than throwing; it used to
      // pass here as a success and the switch simply did not move.
      if (!r.ok) {
        setError(r.message);
        return;
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChipEditRow
      busy={busy}
      error={error}
      chips={
        <>
          {all ? (
            <span className="member-tag-chip state-chip is-off">
              Unsubscribed from all marketing
            </span>
          ) : (
            // Not "Subscribed": this is only the stop-all switch, and the
            // newsletter has its own row below (newsletter-resubscribe).
            <StateChip on onLabel="Marketing on" />
          )}
          {flows.map((f) => (
            <span
              key={f}
              className="member-tag-chip state-chip is-off"
              title={`Unsubscribed from ${f} (flow_opt_outs.${f})`}
            >
              {`Off: ${f}`}
            </span>
          ))}
        </>
      }
    >
      <label className="member-postsession-check">
        <input
          type="checkbox"
          checked={!all}
          disabled={busy}
          onChange={(e) => void set(e.target.checked)}
        />{" "}
        Receives marketing
      </label>
      {flows.length > 0 ? (
        <span className="member-note">
          The per-flow unsubscribes stay as they are.
        </span>
      ) : null}
    </ChipEditRow>
  );
}

/** What the "Newsletter" row shows for a member's {@link MemberNewsletterStanding}. */
export interface NewsletterRowView {
  readonly chip: { readonly label: string; readonly on: boolean; readonly title: string | null };
  /**
   * When and how they joined (newsletter-subscribers, 2026-10-24): for someone
   * it reaches, always — the newsletter is opt-out, so "Gets the newsletter"
   * alone does not say whether they ever signed up; for anyone else, only a
   * sign-up of theirs. A test account the test editions reach is among the
   * rest (newsletter-test-resubscribe, 2026-10-30): "on it as a member" is the
   * real list's opt-out, and a real edition leaves it out.
   */
  readonly joined: string | null;
  /** The button, when signing up again would bring them back; its confirmation first. */
  readonly resubscribe: { readonly label: string; readonly confirm: string } | null;
}

/** Each withdrawal a re-subscribe clears, in words. */
const NEWSLETTER_CLEAR_WORDS: Readonly<Record<string, string>> = {
  newsletter_opt_out: "the newsletter",
  all_marketing_opt_out: "all marketing",
  site_consent_withdrawn: "the website's marketing switch",
};

/** Why a sign-up can never bring this member back, in words. */
const NEWSLETTER_BLOCKED_WORDS: Readonly<Record<string, string>> = {
  member_inactive: "Not an active member",
  internal_or_test: "Staff, a test account or do-not-contact",
  channel_opted_out: "Contact via Email is off",
  no_address: "No email address the newsletter can use",
  placeholder_email: "No real email address on file",
  // deliverability-ladder (2026-10-31): a bounce keeps them out only while its
  // pause runs; a dead account or a spam complaint, for good.
  bounced_or_complained: "Their address is bouncing (paused for now) or gone, or they marked our email as spam",
};

/**
 * The "Newsletter" row (newsletter-resubscribe, 2026-10-23) — the member's
 * standing with the email newsletter as its own send reads it, and the
 * Re-subscribe button only when that would bring them back. The button emails
 * the member first, so its confirmation says so, and says plainly when their
 * other marketing switches back on too. A test account's newsletter is the
 * test editions (newsletter-test-resubscribe, 2026-10-30), and the row says so
 * rather than "Gets the newsletter".
 */
export function newsletterRowView(
  standing: MemberNewsletterStanding,
  join: MemberDetailAccount["newsletter_join"] = null,
  day: (iso: string) => string = fmtDate,
): NewsletterRowView {
  const signUp = join !== null && join.via !== "member" ? joinedLine(join, day) : null;
  switch (standing.state) {
    case "subscribed":
      return standing.test_editions_only
        ? {
            chip: { label: "Gets test editions", on: true, title: "Tagged testing: only test editions reach it, never a real one" },
            joined: signUp,
            resubscribe: null,
          }
        : {
            chip: { label: "Gets the newsletter", on: true, title: null },
            joined: join === null ? null : joinedLine(join, day),
            resubscribe: null,
          };
    case "resubscribable":
      return {
        joined: signUp,
        chip: {
          label: standing.test_editions_only ? "Unsubscribed from test editions" : "Unsubscribed",
          on: false,
          title: `Off: ${standing.clears.map((c) => NEWSLETTER_CLEAR_WORDS[c] ?? c).join(" · ")}`,
        },
        resubscribe: {
          label: "Re-subscribe",
          confirm:
            (standing.test_editions_only
              ? "Put this test account back on the test editions? Clavis emails it now to say it's back on, with a link to undo it."
              : "Put this member back on the newsletter? Clavis emails them now to say they're back on, with a link to undo it.") +
            (standing.other_marketing_resumes
              ? " Their other marketing emails and WhatsApp messages switch back on too."
              : "") +
            (standing.test_editions_only ? "" : " Only do this when they asked."),
        },
      };
    case "blocked":
      return {
        joined: signUp,
        chip: {
          label: "Can't get it",
          on: false,
          title: NEWSLETTER_BLOCKED_WORDS[standing.reason] ?? standing.reason,
        },
        resubscribe: null,
      };
    case "unknown":
      return {
        joined: null,
        chip: { label: "Couldn't read", on: false, title: "The newsletter state could not be read just now" },
        resubscribe: null,
      };
  }
}

function NewsletterRow({
  memberId,
  standing,
  join,
  onChanged,
}: {
  readonly memberId: string;
  readonly standing: MemberNewsletterStanding;
  readonly join: MemberDetailAccount["newsletter_join"];
  readonly onChanged: () => void;
}): JSX.Element {
  const view = newsletterRowView(standing, join);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resubscribe = async (): Promise<void> => {
    if (busy || view.resubscribe === null) return;
    if (!window.confirm(view.resubscribe.confirm)) return;
    setBusy(true);
    setError(null);
    try {
      const r = await dispatchAction("resubscribe_newsletter", { member_id: memberId, via: "staff" });
      if (!r.ok) {
        setError(r.message);
        return;
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "re-subscribe failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <dd className="chip-field">
      <div className="member-tags-chips">
        <span
          className={`member-tag-chip state-chip${view.chip.on ? " is-on" : " is-off"}`}
          {...(view.chip.title !== null ? { title: view.chip.title } : {})}
        >
          {view.chip.label}
        </span>
        {view.chip.title !== null ? <span className="member-note">{view.chip.title}</span> : null}
      </div>
      {view.joined !== null ? <div className="member-note">{view.joined}</div> : null}
      {view.resubscribe !== null ? (
        <div className="member-tags-add">
          <button
            type="button"
            className="action-dispatch-btn member-tag-add-btn"
            disabled={busy}
            onClick={() => void resubscribe()}
          >
            {busy ? "…" : view.resubscribe.label}
          </button>
        </div>
      ) : null}
      {error !== null ? <div className="flag-card-err">{error}</div> : null}
    </dd>
  );
}

function PostSessionCaptureToggle({
  memberId,
  metadata,
  onChanged,
}: {
  readonly memberId: string;
  readonly metadata: Record<string, unknown>;
  readonly onChanged: () => void;
}): JSX.Element {
  const current = readSendPostSessionMessage(metadata);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (next: boolean): Promise<void> => {
    if (busy || next === current) return;
    setBusy(true);
    setError(null);
    try {
      await dispatchAction("update_member", {
        member_id: memberId,
        patch: { metadata: { send_post_session_message: next } },
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChipEditRow chips={<StateChip on={current} />} busy={busy} error={error}>
      <label className="member-postsession-check">
        <input
          type="checkbox"
          checked={current}
          disabled={busy}
          onChange={(e) => void set(e.target.checked)}
        />{" "}
        Send post-session capture messages
      </label>
    </ChipEditRow>
  );
}

// ---------------------------------------------------------------------------
// Member gender editor (Profile "Gender" field). The member's own gender, one
// of the essential profile fields the post-session capture flow collects.
// Stored at `members.metadata.gender` via `update_member`. Distinct from the
// onboarding "Trainer gender" PREFERENCE and the AI-enrichment inferred gender.
// ---------------------------------------------------------------------------

// Canonicalize the stored value for display so a legacy/free-form value like
// "female" (e.g. written by the WhatsApp agent before it was normalized on
// write) still selects the matching "Female" option instead of falling back to
// "— not set —". Unrecognised values read as "" (the not-set option).
function readGender(metadata: Record<string, unknown>): string {
  return normalizeMemberGender(metadata["gender"]) ?? "";
}

function GenderEditor({
  memberId,
  metadata,
  onChanged,
}: {
  readonly memberId: string;
  readonly metadata: Record<string, unknown>;
  readonly onChanged: () => void;
}): JSX.Element {
  const current = readGender(metadata);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = async (next: string): Promise<void> => {
    if (busy || next === current) return;
    setBusy(true);
    setError(null);
    try {
      await dispatchAction("update_member", {
        member_id: memberId,
        patch: { metadata: { gender: next } },
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChipEditRow
      className="member-gender-editor"
      busy={busy}
      error={error}
      chips={
        current.length > 0 ? (
          <span className="member-tag-chip">{current}</span>
        ) : (
          <span className="member-tags-empty">—</span>
        )
      }
    >
      <select
        value={current}
        disabled={busy}
        onChange={(e) => void set(e.target.value)}
      >
        <option value="">— not set —</option>
        {MEMBER_GENDER_OPTIONS.map((g) => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
      </select>
    </ChipEditRow>
  );
}

// ---------------------------------------------------------------------------
// Inline-edit fields for the "Profile details" block. Each renders the read
// value with a "Edit" affordance; on edit it swaps to an input/select + Save /
// Cancel and dispatches a canonical action. `display_name` / `phone` /
// `status` go through `update_member` (the same patch action the data-row edit
// uses); `email` goes through the dedicated `change_member_email` action
// (staff-authoritative, with per-studio uniqueness enforcement).
// ---------------------------------------------------------------------------

type UpdateMemberPatch =
  | { readonly display_name: string }
  | { readonly phone: string | null }
  | { readonly status: "active" | "suspended" };

/**
 * Inline editor for `display_name` / `phone` (text) and `status`
 * (enum select), all dispatched via `update_member`. `phone` may be cleared
 * to `null` by submitting an empty string.
 *
 * `status` offers `active` and `suspended` only (box 17, 2026-10-31): a member
 * is deleted by `delete_member`, never by this dropdown. A row already
 * `deleted` (a merged profile's absorbed row) shows that value as a disabled
 * choice, so the select reads true, and Save does nothing until another is
 * picked.
 */
/**
 * field-errors (2026-11-01) — the profile rows' labels, which a refusal of the
 * field is told under ("Phone: use the international format …").
 */
const MEMBER_FIELD_LABELS: Readonly<Record<"display_name" | "phone" | "status", string>> = {
  display_name: "Name",
  phone: "Phone",
  status: "Status",
};

function MemberFieldEditor({
  memberId,
  field,
  value,
  options,
  placeholder,
  onSaved,
}: {
  readonly memberId: string;
  readonly field: "display_name" | "phone" | "status";
  readonly value: string;
  /** Enum choices for `status`; omitted for free-text fields. */
  readonly options?: ReadonlyArray<string>;
  readonly placeholder?: string;
  readonly onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(value);
  const dispatch = useDispatch<
    { readonly member_id: string; readonly patch: UpdateMemberPatch },
    Record<string, unknown>
  >("update_member");

  const begin = (): void => {
    setDraft(value);
    dispatch.reset();
    setEditing(true);
  };
  const cancel = (): void => {
    dispatch.reset();
    setEditing(false);
  };
  const save = async (): Promise<void> => {
    if (dispatch.loading || memberId === "") return;
    const trimmed = draft.trim();
    let patch: UpdateMemberPatch;
    if (field === "display_name") {
      if (trimmed === "") return; // display_name requires 1+ chars
      patch = { display_name: trimmed };
    } else if (field === "phone") {
      patch = { phone: trimmed === "" ? null : trimmed };
    } else {
      if (options !== undefined && !options.includes(trimmed)) return;
      patch = { status: trimmed as "active" | "suspended" };
    }
    const r = await dispatch.run({ member_id: memberId, patch });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  const editor =
    options !== undefined ? (
      <select
        className="profile-field-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      >
        {options.includes(value) ? null : (
          <option value={value} disabled>
            {value}
          </option>
        )}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    ) : (
      <input
        className="profile-field-input"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") cancel();
        }}
      />
    );

  return (
    <ProfileEditRow
      editing={editing}
      onEdit={begin}
      display={
        value.trim().length === 0 ? (
          "—"
        ) : options !== undefined ? (
          // An enum (status) is a state, so it reads as a chip like the other
          // state rows; free text stays plain text.
          <span className="member-tag-chip">{value}</span>
        ) : (
          value
        )
      }
      editor={editor}
      onSave={() => void save()}
      onCancel={cancel}
      loading={dispatch.loading}
      // field-errors (2026-11-01) — "Phone: use the international format …".
      error={
        dispatch.error === null
          ? null
          : failureText(dispatch.error, { [`patch.${field}`]: MEMBER_FIELD_LABELS[field] })
      }
      // update-member-refs (2026-11-01) — a number already on another member
      // names that member (the oldest, when several hold it).
      errorDetail={
        <ExistingMemberLink refs={dispatch.error?.refs} label="Open the other member →" />
      }
    />
  );
}

/**
 * #17 (2026-07-15) — inline editor for the member's Instagram handle, stored at
 * `members.metadata.instagram_handle` (the SAME key `enrich_member` reads/writes)
 * via `update_member`'s shallow metadata merge. Strips a leading `@` + whitespace
 * on save (handles are stored bare; the display adds the `@`); an empty value
 * clears it to `null`. The not-editing display keeps the clickable IG link.
 * (IG-identity uniqueness is deferred with the #2 IG index — noted there.)
 */
function MemberInstagramEditor({
  memberId,
  value,
  onSaved,
}: {
  readonly memberId: string;
  readonly value: string | null;
  readonly onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(value ?? "");
  const dispatch = useDispatch<
    {
      readonly member_id: string;
      readonly patch: {
        readonly metadata: { readonly instagram_handle: string | null };
      };
    },
    Record<string, unknown>
  >("update_member");

  const begin = (): void => {
    setDraft(value ?? "");
    dispatch.reset();
    setEditing(true);
  };
  const cancel = (): void => {
    dispatch.reset();
    setEditing(false);
  };
  const save = async (): Promise<void> => {
    if (dispatch.loading || memberId === "") return;
    const cleaned = draft.trim().replace(/^@+/, "").trim();
    const r = await dispatch.run({
      member_id: memberId,
      patch: { metadata: { instagram_handle: cleaned === "" ? null : cleaned } },
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  const hasHandle = value !== null && value.trim() !== "";
  // Honesty hint (2026-08-14): the handle is display-only — Meta mints the
  // routable Instagram identity (IGSID) only when the person DMs the studio,
  // and no handle→IGSID lookup exists. Without this line the field reads as
  // "type a handle, Instagram messaging works". Rendered INSIDE the value
  // node: the profile card is a strict <dt>/<dd> grid, and a sibling element
  // outside the <dd> shifts every later label onto the wrong value (shipped
  // broken for a few hours on 2026-08-14).
  const display: ReactNode = (
    <>
      {hasHandle ? (
        <a
          href={`https://instagram.com/${encodeURIComponent(value)}`}
          target="_blank"
          rel="noreferrer"
          className="data-member-link"
        >
          @{value}
        </a>
      ) : (
        "—"
      )}
      <div className="subtle" style={{ fontSize: "0.8em", marginTop: 2 }}>
        Display only — Instagram messaging activates when this member DMs the
        studio (their Instagram ID is captured automatically; a typed handle
        cannot start a conversation).
      </div>
    </>
  );

  // A handle is unique per member — surface the `member_instagram_conflict`
  // guard (update_member) as a plain-language line instead of the raw code.
  const errorText: string | null =
    dispatch.error === null
      ? null
      : dispatch.error.code === "member_instagram_conflict"
        ? "That Instagram handle is already linked to another member."
        : dispatch.error.message;

  return (
    <>
      <ProfileEditRow
        editing={editing}
        onEdit={begin}
        display={display}
        editor={
          <input
            className="profile-field-input"
            value={draft}
            placeholder="handle (without @; blank to clear)"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
              if (e.key === "Escape") cancel();
            }}
          />
        }
        onSave={() => void save()}
        onCancel={cancel}
        loading={dispatch.loading}
        error={errorText}
        // update-member-refs (2026-11-01) — and names that member.
        errorDetail={
          <ExistingMemberLink refs={dispatch.error?.refs} label="Open the other member →" />
        }
      />
    </>
  );
}

/**
 * Inline editor for `email`, dispatched via the dedicated
 * `change_member_email` action. Surfaces the `email_already_in_use`
 * conflict cleanly.
 */
function MemberEmailEditor({
  memberId,
  value,
  onSaved,
}: {
  readonly memberId: string;
  readonly value: string | null;
  readonly onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(value ?? "");
  const dispatch = useDispatch<
    { readonly member_id: string; readonly new_email: string },
    { readonly changed: boolean }
  >("change_member_email");

  const begin = (): void => {
    setDraft(value ?? "");
    dispatch.reset();
    setEditing(true);
  };
  const cancel = (): void => {
    dispatch.reset();
    setEditing(false);
  };
  const save = async (): Promise<void> => {
    if (dispatch.loading || memberId === "") return;
    const trimmed = draft.trim();
    if (trimmed === "") return; // email is required by the schema
    const r = await dispatch.run({ member_id: memberId, new_email: trimmed });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  return (
    <ProfileEditRow
      editing={editing}
      onEdit={begin}
      display={value ?? "—"}
      editor={
        <input
          className="profile-field-input"
          type="email"
          value={draft}
          placeholder="name@example.com"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") cancel();
          }}
        />
      }
      onSave={() => void save()}
      onCancel={cancel}
      loading={dispatch.loading}
      // field-errors (2026-11-01) — "Email: use a full email address …".
      error={dispatch.error === null ? null : failureText(dispatch.error, { new_email: "Email" })}
    />
  );
}

// ---------------------------------------------------------------------------
// Consultation-form OCR uploader + review-before-save panel.
//
// Staff snap/upload a photo of the paper consultation form → it's downscaled
// client-side to a small JPEG (keeps the base64 payload well under the action
// cap + normalises phone photo formats) → dispatched to
// `extract_consultation_form` (Claude vision, extract-only) → the proposed
// fields render as an editable review list. Nothing is saved until staff hit
// "Save to profile", which sends the ticked rows to
// `apply_consultation_extraction` — the one write path for a form's answers,
// shared with the WhatsApp confirm. It locks the member and merges the answers
// into the STORED consultation object, so an answer written since this page
// loaded survives (until 2026-10-14 the page sent back the whole object as it
// had loaded it, through `update_member`, and silently deleted such answers).
// The name, postcode and source rows that start ticked because the member had
// none fill only if still blank at save time; ticking one the member already
// has overwrites it. An OCR misread can never silently overwrite good data.
// ---------------------------------------------------------------------------

interface ExtractedField {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly confidence: number | null;
}

interface ExtractConsultationResponse {
  readonly member_id: string | null;
  readonly model: string;
  readonly overall_confidence: number | null;
  readonly notes_for_human_review: string | null;
  readonly proposed_fields: ReadonlyArray<ExtractedField>;
  readonly raw: Record<string, unknown>;
}

interface ReviewRow extends ExtractedField {
  readonly include: boolean;
  /**
   * Write only if the member still has no answer at save time — set for the
   * name, postcode and source rows when the member had none at extraction, so
   * one filled in meanwhile is kept, not overwritten.
   */
  readonly onlyIfBlank: boolean;
}

/** What `apply_consultation_extraction` answers — field keys, never values. */
interface ApplyConsultationResponse {
  readonly fields_applied: ReadonlyArray<string>;
  readonly fields_kept: ReadonlyArray<string>;
}

/** Longest edge (px) the uploaded image is downscaled to before OCR. */
const OCR_MAX_EDGE = 1600;

/**
 * Read a user-selected image File, downscale it to <= OCR_MAX_EDGE on its long
 * edge, and re-encode as a JPEG. Returns base64 (no data-URI prefix). Throws on
 * a format the browser can't decode (e.g. HEIC on a non-Safari browser).
 */
async function fileToDownscaledJpegBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error("file_read_failed"));
    fr.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () =>
      reject(new Error("image_decode_failed — try exporting as JPG or PNG"));
    el.src = dataUrl;
  });
  const longest = Math.max(img.width, img.height);
  const scale = longest > OCR_MAX_EDGE ? OCR_MAX_EDGE / longest : 1;
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("canvas_unsupported");
  ctx.drawImage(img, 0, 0, w, h);
  const jpeg = canvas.toDataURL("image/jpeg", 0.85);
  const base64 = jpeg.split(",")[1] ?? "";
  if (base64.length === 0) throw new Error("image_encode_failed");
  return base64;
}

/** Existing `metadata.consultation` object, defensively. */
function readConsultation(
  metadata: Record<string, unknown>,
): Record<string, unknown> {
  const c = metadata["consultation"];
  return c !== null && typeof c === "object" && !Array.isArray(c)
    ? (c as Record<string, unknown>)
    : {};
}

/** A top-level `metadata.<key>` string, trimmed; "" when absent or non-string. */
function readTopLevelMetaString(
  metadata: Record<string, unknown>,
  key: string,
): string {
  const v = metadata[key];
  return typeof v === "string" ? v.trim() : "";
}

/** The canonical `metadata.postcode` (top-level), trimmed; "" when absent. */
function readPostcode(metadata: Record<string, unknown>): string {
  return readTopLevelMetaString(metadata, "postcode");
}

/** The AI-enrichment block at `metadata.ai_enrichment`, defensively; null if absent. */
function readAiEnrichment(
  metadata: Record<string, unknown>,
): AiEnrichment | null {
  const v = metadata["ai_enrichment"];
  return v !== null && typeof v === "object" && !Array.isArray(v)
    ? (v as unknown as AiEnrichment)
    : null;
}

function ConfidenceBadge({ value }: { readonly value: number | null }): JSX.Element {
  if (value === null) return <span className="cf-conf cf-conf-na">—</span>;
  const pct = Math.round(value * 100);
  const tone = value >= 0.8 ? "cf-conf-hi" : value >= 0.6 ? "cf-conf-mid" : "cf-conf-lo";
  return <span className={`cf-conf ${tone}`}>{pct}%</span>;
}

function ConsultationFormUploader({
  memberId,
  metadata,
  hasName,
  onSaved,
}: {
  readonly memberId: string;
  readonly metadata: Record<string, unknown>;
  readonly hasName: boolean;
  /** After a save, with the labels of the rows left as the member had them. */
  readonly onSaved: (keptLabels: ReadonlyArray<string>) => void;
}): JSX.Element {
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const [meta, setMeta] = useState<{
    readonly model: string;
    readonly overall: number | null;
    readonly notes: string | null;
    readonly raw: Record<string, unknown>;
    /** When the form was read — the provenance's `extracted_at`. */
    readonly extractedAt: string;
  } | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);

  const extract = useDispatch<
    {
      readonly member_id: string;
      readonly image_base64: string;
      readonly mime_type: string;
    },
    ExtractConsultationResponse
  >("extract_consultation_form");
  const save = useDispatch<
    {
      readonly member_id: string;
      readonly fields: ReadonlyArray<{
        readonly key: string;
        readonly value: string;
        readonly only_if_blank: boolean;
      }>;
      readonly provenance: {
        readonly model: string;
        readonly overall_confidence: number | null;
        readonly extracted_at: string;
        readonly raw: Record<string, unknown>;
      };
    },
    ApplyConsultationResponse
  >("apply_consultation_extraction");

  const onFile = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    setClientError(null);
    setRows(null);
    setMeta(null);
    setProcessing(true);
    let imageBase64: string;
    try {
      imageBase64 = await fileToDownscaledJpegBase64(file);
    } catch (e) {
      setClientError((e as Error).message);
      setProcessing(false);
      return;
    }
    setProcessing(false);
    const r = await extract.run({
      member_id: memberId,
      image_base64: imageBase64,
      mime_type: "image/jpeg",
    });
    if (!r.ok) return;
    const resp = r.response;
    const extractedAt = new Date().toISOString();
    // Name + postcode + source default to UNCHECKED when the member already
    // has one (don't clobber an existing value), and when they have none they
    // start ticked to fill only a blank; everything else defaults to included.
    const hasPostcode = readPostcode(metadata).length > 0;
    // Cleaned, not raw — `source` is the one top-level facet the gap checker
    // reads through `cleanProfileFieldValue`, so a `skipped` sentinel counts as
    // NO source on that side. Reading it raw here would leave the OCR row
    // unticked ("they already have one") while the gate still counts it
    // missing — the single path that could overwrite the sentinel, defaulted
    // off.
    const hasSource =
      cleanProfileFieldValue(metadata["source"]) !== undefined;
    setRows(
      resp.proposed_fields.map((f) => {
        // `null` for every row but the three a member may already hold.
        const blank =
          f.key === "display_name"
            ? !hasName
            : f.key === "postcode"
              ? !hasPostcode
              : f.key === "source"
                ? !hasSource
                : null;
        return { ...f, include: blank ?? true, onlyIfBlank: blank === true };
      }),
    );
    setMeta({
      model: resp.model,
      overall: resp.overall_confidence,
      notes: resp.notes_for_human_review,
      raw: resp.raw,
      extractedAt,
    });
  };

  const setRowValue = (key: string, value: string): void => {
    setRows((prev) =>
      prev === null
        ? prev
        : prev.map((r) => (r.key === key ? { ...r, value } : r)),
    );
  };
  const toggleRow = (key: string): void => {
    setRows((prev) =>
      prev === null
        ? prev
        : prev.map((r) => (r.key === key ? { ...r, include: !r.include } : r)),
    );
  };

  const includedCount =
    rows?.filter((r) => r.include && r.value.trim().length > 0).length ?? 0;

  const onSave = async (): Promise<void> => {
    if (rows === null || meta === null || includedCount === 0 || save.loading) {
      return;
    }
    // Every row key is one the action writes — `proposed_fields` and the
    // action's target set are the same list (`CONSULTATION_FIELD_TARGET_KEYS`).
    const fields = rows
      .filter((row) => row.include && row.value.trim().length > 0)
      .map((row) => ({
        key: row.key,
        value: row.value.trim(),
        only_if_blank: row.onlyIfBlank,
      }));
    // Provenance — the full extraction + model, so a low-confidence save is
    // auditable; stored under metadata.consultation_extraction by the action.
    const r = await save.run({
      member_id: memberId,
      fields,
      provenance: {
        model: meta.model,
        overall_confidence: meta.overall,
        extracted_at: meta.extractedAt,
        raw: meta.raw,
      },
    });
    if (r.ok) {
      const kept = rows
        .filter((row) => r.response.fields_kept.includes(row.key))
        .map((row) => row.label);
      setRows(null);
      setMeta(null);
      setClientError(null);
      onSaved(kept);
    }
  };

  const busy = processing || extract.loading;

  return (
    <div className="cf-uploader">
      <div className="cf-upload-row">
        <label className="action-dispatch-btn cf-upload-btn">
          {busy ? "Reading form…" : "Upload consultation form"}
          <input
            type="file"
            accept="image/*"
            disabled={busy}
            style={{ display: "none" }}
            onChange={(e) => {
              void onFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        <span className="cf-hint">
          Photo of the paper form → auto-extract → review → save.
        </span>
      </div>
      {clientError !== null && <p className="cf-error">{clientError}</p>}
      {extract.error !== null && (
        <p className="cf-error">
          Couldn't read the form ({extract.error.code}): {extract.error.message}
        </p>
      )}

      {rows !== null && meta !== null && (
        <div className="cf-review">
          <div className="cf-review-head">
            <strong>Review extracted fields</strong>
            <span className="cf-review-meta">
              {meta.model} · overall <ConfidenceBadge value={meta.overall} />
            </span>
          </div>
          {meta.notes !== null && (
            <p className="cf-notes">⚠ {meta.notes}</p>
          )}
          {rows.length === 0 ? (
            <p className="empty">Nothing readable was extracted from this image.</p>
          ) : (
            <table className="cf-table table-cards">
              <thead>
                <tr>
                  <th>Save</th>
                  <th>Field</th>
                  <th>Value</th>
                  <th>Conf.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key} className={row.include ? "" : "cf-row-off"}>
                    <td data-label="Save">
                      <input
                        type="checkbox"
                        checked={row.include}
                        onChange={() => toggleRow(row.key)}
                      />
                    </td>
                    <td className="cf-field-label table-card-title">{row.label}</td>
                    <td data-label="Value">
                      <input
                        type="text"
                        className="cf-value-input"
                        value={row.value}
                        disabled={!row.include}
                        onChange={(e) => setRowValue(row.key, e.target.value)}
                      />
                    </td>
                    <td data-label="Conf.">
                      <ConfidenceBadge value={row.confidence} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="cf-review-actions">
            <button
              type="button"
              className="action-dispatch-btn"
              disabled={includedCount === 0 || save.loading}
              onClick={() => void onSave()}
            >
              {save.loading
                ? "Saving…"
                : `Save ${includedCount} field${includedCount === 1 ? "" : "s"} to profile`}
            </button>
            <button
              type="button"
              className="cf-cancel-btn"
              disabled={save.loading}
              onClick={() => {
                setRows(null);
                setMeta(null);
              }}
            >
              Discard
            </button>
            {save.error !== null && (
              <span className="cf-error">{save.error.message}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** One label/value row in the enrichment list — omitted when the value is empty. */
function EnrichRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null;
}): JSX.Element | null {
  if (value === null || value.trim().length === 0) return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/**
 * AI enrichment — the inferred profile mined by the `enrich_member` action and
 * stored at `metadata.ai_enrichment`. The action persists server-side, so this
 * panel just dispatches `enrich_member` and re-fetches (no review step — the
 * inferred data renders directly; staff re-run if it's off). Empty state shows
 * the "Enrich member" button + an explainer; populated state shows the inferred
 * fields, a confidence pill, persona summary, suggested next action,
 * LinkedIn/Instagram links, and collapsible research citations.
 */
/**
 * Coerce a stored enrichment blob to the shape the panel renders.
 *
 * `AiEnrichment` describes what `enrich_member` WRITES, but the value is
 * untyped JSON read straight out of `members.metadata.ai_enrichment` — the type
 * is a claim about the data, not a guarantee about it. A blob written before a
 * field existed carries `undefined` where the type promises `string | null`,
 * and `x !== null && x.trim()` then throws and takes the WHOLE member page down
 * with it (there is no per-card error boundary), not just this section.
 *
 * Normalising once here keeps every access below total, instead of scattering
 * `?.` through the JSX and still missing one. The honest long-term home for
 * this is the backend read that lifts the blob out of `metadata`.
 */
function normaliseEnrichment(e: AiEnrichment): AiEnrichment {
  const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
  return {
    ...e,
    inferred_gender: str(e.inferred_gender),
    inferred_age_band: str(e.inferred_age_band),
    inferred_primary_goal: str(e.inferred_primary_goal),
    inferred_objection: str(e.inferred_objection),
    inferred_job: str(e.inferred_job),
    inferred_employer: str(e.inferred_employer),
    inferred_location: str(e.inferred_location),
    linkedin_url: str(e.linkedin_url),
    instagram_handle: str(e.instagram_handle),
    summary: str(e.summary),
    suggested_next_action: str(e.suggested_next_action),
    social_links: e.social_links ?? null,
    confidence_per_field: e.confidence_per_field ?? {},
    overall_confidence:
      typeof e.overall_confidence === "number" ? e.overall_confidence : null,
    research_citations: e.research_citations ?? [],
    model: str(e.model) ?? "unknown model",
    enriched_at: str(e.enriched_at) ?? "",
    cost_usd: typeof e.cost_usd === "number" ? e.cost_usd : 0,
    source: str(e.source) ?? "",
  };
}

function AiEnrichmentPanel({
  memberId,
  enrichment,
  onEnriched,
}: {
  readonly memberId: string;
  readonly enrichment: AiEnrichment | null;
  readonly onEnriched: () => void;
}): JSX.Element {
  const enrich = useDispatch<
    { readonly member_id: string },
    { readonly member_id: string }
  >("enrich_member");
  const busy = enrich.loading;
  const onReEnrich = async (): Promise<void> => {
    if (busy || memberId.length === 0) return;
    const r = await enrich.run({ member_id: memberId });
    if (r.ok) onEnriched();
  };

  // Empty state — no enrichment yet.
  if (enrichment === null) {
    return (
      <div className="cf-uploader">
        <p className="member-note">
          No AI enrichment yet. Run research to infer this member's likely goal,
          objection, role and persona by combining their studio data with public
          signals — nothing is sent to the member.
        </p>
        <div className="cf-upload-row">
          <button
            type="button"
            className="action-dispatch-btn"
            disabled={busy}
            onClick={() => void onReEnrich()}
          >
            {busy ? "Researching…" : "Enrich member"}
          </button>
        </div>
        {enrich.error !== null && (
          <p className="cf-error">
            Couldn't enrich ({enrich.error.code}): {enrich.error.message}
          </p>
        )}
      </div>
    );
  }

  const e = normaliseEnrichment(enrichment);
  return (
    <div className="cf-uploader">
      <div className="cf-review-head">
        <span className="cf-review-meta">
          {e.model} · overall <ConfidenceBadge value={e.overall_confidence} />
          {e.enriched_at.length > 0 ? ` · ${fmtDate(e.enriched_at)}` : ""}
        </span>
        <button
          type="button"
          className="action-dispatch-btn"
          disabled={busy}
          onClick={() => void onReEnrich()}
          title="Re-run the research. Overwrites the stored enrichment."
        >
          {busy ? "Researching…" : "Re-enrich"}
        </button>
      </div>
      {enrich.error !== null && (
        <p className="cf-error">
          Couldn't enrich ({enrich.error.code}): {enrich.error.message}
        </p>
      )}

      <dl className="kvs">
        <EnrichRow label="Inferred gender" value={e.inferred_gender} />
        <EnrichRow label="Age band" value={e.inferred_age_band} />
        <EnrichRow label="Primary goal" value={e.inferred_primary_goal} />
        <EnrichRow label="Objection" value={e.inferred_objection} />
        <EnrichRow label="Job" value={e.inferred_job} />
        <EnrichRow label="Employer" value={e.inferred_employer} />
        <EnrichRow label="Location" value={e.inferred_location} />
        {e.linkedin_url !== null && e.linkedin_url.length > 0 ? (
          <>
            <dt>LinkedIn</dt>
            <dd>
              <a href={e.linkedin_url} target="_blank" rel="noreferrer">
                {e.linkedin_url}
              </a>
            </dd>
          </>
        ) : null}
        {e.instagram_handle !== null && e.instagram_handle.length > 0 ? (
          <>
            <dt>Instagram</dt>
            <dd>{e.instagram_handle}</dd>
          </>
        ) : null}
      </dl>

      {e.summary !== null && e.summary.trim().length > 0 ? (
        <blockquote className="enrich-summary">{e.summary}</blockquote>
      ) : null}

      {e.suggested_next_action !== null &&
      e.suggested_next_action.trim().length > 0 ? (
        <p className="enrich-next">
          <strong>Suggested next action:</strong> {e.suggested_next_action}
        </p>
      ) : null}

      {e.research_citations.length > 0 ? (
        <details className="meta-raw">
          <summary>Research citations ({e.research_citations.length})</summary>
          <ul className="enrich-citations">
            {e.research_citations.map((c, i) => (
              <li key={i}>
                {c.url === "first_party" ? (
                  <span>{c.title ?? "studio record"}</span>
                ) : (
                  <a href={c.url} target="_blank" rel="noreferrer">
                    {c.title ?? c.url}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
};

const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtTime(iso: string | null): string {
  if (iso === null) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : studioFormat(TIME_OPTS).format(d);
}

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : studioFormat(DATETIME_OPTS).format(d);
}

function fmtDate(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATE_OPTS).format(d);
}

/**
 * When the monthly memory recycler last folded this member's rolling daily
 * entries into long-term memory (`member_memory.last_recycled_at`, migration
 * 036 — the recycler's own idempotency key).
 *
 * THREE states, and the two empty ones are not the same fact, which is why
 * this is a function rather than `fmtDateTime(...)` at the call site:
 *
 *  - no `member_memory` row at all → "—", the page's normal "nothing here".
 *    2890 of 2923 live members (2026-09-23) — the agent has never written
 *    memory for them, so there is nothing to recycle and nothing is wrong.
 *  - a row that has never been recycled → "Never", which is a REAL signal: the
 *    member HAS memory accumulating and the monthly job has not yet folded it.
 *    19 members are in this state; rendering them "—" would hide them among
 *    the 2890 who simply have no memory.
 *  - recycled → the timestamp (14 members; most recent 2026-09-01).
 */
export function memoryRecycledLabel(
  memory: MemberDetailMemory | null,
): string {
  if (memory === null) return "—";
  if (memory.last_recycled_at === null) return "Never";
  return fmtDateTime(memory.last_recycled_at);
}

/** Human label for a message channel — falls back to the raw value for future channels. */
function channelLabel(channel: string): string {
  switch (channel) {
    case "whatsapp":
      return "WhatsApp";
    case "email":
      return "Email";
    case "instagram":
      return "Instagram";
    default:
      return channel;
  }
}

/**
 * What the member's most-recent Resend event MEANS, spelled out — the chip
 * shows one word and its tone, and one word is not enough to act on.
 *
 * `bounced` and `complained` share the red tone but are opposite facts, and
 * this chip's own comment used to conflate them ("RED means the member did not
 * get the mail (bounced / complained)"). A complaint means the mail arrived and
 * was marked as spam; the address still works, and what is suppressed is
 * MARKETING, not everything. Saying so here is the difference between staff
 * re-typing a correct address and staff correctly leaving it alone.
 */
function describeEmailEvent(type: string): string {
  switch (type) {
    case "bounced":
      return "The address rejected this message. A bounce can pause further sends for 7, then 30, then 90 days (a full mailbox still gets booking and receipt emails) — the Deliverability row below says whether a pause is in force.";
    case "complained":
      return "Delivered, then marked as spam by the recipient. The address works; marketing email to it is suppressed for good.";
    case "delivered":
      return "The message reached the recipient's mail server.";
    case "opened":
      return "The recipient opened the message.";
    case "clicked":
      return "The recipient clicked a link in the message.";
    default:
      return "";
  }
}

/**
 * The member's most-recent Resend email event, rendered as a coloured pill —
 * clicked/opened read positive (green), delivered neutral (blue), and
 * bounced/complained negative (red). Backs the "Email engagement" profile row
 * and mirrors the `last_email_event` an agent reads from `list_members`. Renders
 * "—" when the member has no matched Resend event.
 *
 * ENGAGEMENT HISTORY, not current reachability: it is the newest event of any
 * type, so it answers "what happened last time" and never "will the next send
 * go out". The Deliverability row / title chips answer that, from the two
 * predicates the send path itself asks.
 */
function EmailEngagementChip({
  event,
}: {
  event: LastEmailEvent | null;
}): ReactNode {
  if (event === null) return <>—</>;
  // TWO tones only, wearing the same greens and reds as the status tag beside
  // it. RED marks the two events staff must act on — a rejected address and a
  // spam complaint; everything else — delivered, opened, clicked, and the
  // in-flight states — is green. `delivered` in particular used to take a
  // third, amber tone, which read as a warning sat next to a green "active"
  // status when it is actually the good outcome: the mail reached the inbox.
  const negative = event.type === "bounced" || event.type === "complained";
  const tone = negative ? "is-negative" : "is-positive";
  const label = event.type.charAt(0).toUpperCase() + event.type.slice(1);
  const meaning = describeEmailEvent(event.type);
  return (
    // Wears `.tag` itself, so its size, weight, letter-spacing and casing are
    // the status chip's BY CONSTRUCTION rather than hand-matched values that
    // drift the next time the tag styling moves.
    <span
      className={`tag email-event-chip ${tone}`}
      title={
        meaning === ""
          ? fmtDateTime(event.at)
          : `${fmtDateTime(event.at)} — ${meaning}`
      }
    >
      {label}
      <span className="email-event-when">· {fmtDate(event.at)}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Deliverability (2026-09-15, `deliverability-profile-visibility`)
//
// Mario: *"which email and phone numbers are shielded right now? Is there a way
// to see that on the member profile page on the dashboard? Eg a tag or flag or
// sth"*
//
// `deliverability-recipient-drop` refuses a send at READ time — no column is
// written when a recipient goes bad, so before this there was no way to see the
// verdict short of reproducing its SQL by hand. The server now asks the very
// same two exported predicates the send path asks, and the two components below
// render the answer: a red chip beside the status tag (seen without scrolling)
// and a Profile-details row carrying the dates (what makes it actionable).
//
// Deliberately NOT a banner: the refusal lapses by itself — since
// `deliverability-ladder` (2026-10-31) 7, 30, then 90 days after the most
// recent failure, growing with each one — so it is state to notice, not an
// incident to clear.
// ---------------------------------------------------------------------------

/** "once", "twice", "3 times" — how often a number or address has failed. */
function failedTimes(step: number | null): string {
  if (step === null || step <= 1) return "once";
  return step === 2 ? "twice" : `${step} times`;
}

/** The refusal is per channel; both may be in force at once. */
function refusedChannels(
  d: MemberDeliverability,
): ReadonlyArray<{
  readonly key: "whatsapp" | "email";
  readonly label: string;
  /** The chip's word, for the row: a pause lapses; a dead account stays refused. */
  readonly state: "paused" | "refused";
  readonly since: string;
  readonly lapsesAt: string | null;
  readonly why: string;
}> {
  const out: Array<{
    readonly key: "whatsapp" | "email";
    readonly label: string;
    readonly state: "paused" | "refused";
    readonly since: string;
    readonly lapsesAt: string | null;
    readonly why: string;
  }> = [];
  if (d.whatsapp_undeliverable_since !== null) {
    out.push({
      key: "whatsapp",
      label: "WhatsApp paused",
      state: "paused",
      since: d.whatsapp_undeliverable_since,
      lapsesAt: d.whatsapp_refusal_lapses_at,
      why: `Meta answered 131026 (undeliverable) — the number has failed ${failedTimes(
        d.whatsapp_failure_step,
      )} since it last worked`,
    });
  }
  if (d.email_undeliverable_since !== null) {
    out.push({
      key: "email",
      label: d.email_account_gone ? "Email refused" : "Email paused",
      state: d.email_account_gone ? "refused" : "paused",
      since: d.email_undeliverable_since,
      lapsesAt: d.email_refusal_lapses_at,
      why: d.email_account_gone
        ? "The receiving mail server said this account does not exist or is disabled"
        : `The address has bounced ${failedTimes(d.email_failure_step)} since it last took a delivery`,
    });
  }
  return out;
}

/**
 * Title-row chips for a member whose number or address is being refused right
 * now, shown where staff already look for a member's state.
 *
 * Only a refusal renders. There is no green "deliverable" counterpart on
 * purpose: "no evidence this address is dead" is the ordinary case for nearly
 * every member, and a chip saying so on all of them is noise that trains the
 * eye to skip the one row where it matters. `checked: false` renders nothing
 * here too — the uncertainty is stated once, in the profile row, rather than
 * shouted beside the member's name.
 */
function DeliverabilityChips({
  deliverability,
}: {
  readonly deliverability: MemberDeliverability;
}): ReactNode {
  const refused = refusedChannels(deliverability);
  if (refused.length === 0) return null;
  return (
    <>
      {refused.map((r) => (
        // Same `.tag` + `.email-event-chip.is-negative` as the engagement chip,
        // so this is the SAME red as the status tag beside it by construction.
        <span
          key={r.key}
          className="tag email-event-chip is-negative"
          title={`${r.why}, at ${fmtDateTime(r.since)}. Sends are refused before the vendor is called${
            r.lapsesAt === null ? "" : `, until ${fmtDateTime(r.lapsesAt)}`
          }.`}
        >
          {r.label}
          {r.lapsesAt !== null ? (
            <span className="email-event-when">· until {fmtDate(r.lapsesAt)}</span>
          ) : null}
        </span>
      ))}
    </>
  );
}

/**
 * The "Deliverability" row in Profile details — the same verdict the title
 * chips carry, with the dates spelled out and the mechanism named, because a
 * refusal that lapses on its own is only actionable if staff can see WHEN, and
 * only safe if they know there is nothing for them to clear.
 *
 * Three states, all of them stated: refused (per channel, with dates), clear,
 * and — when the lookup itself failed — unknown. That last one exists because
 * the fields arrive as four nulls either way, and printing "nothing is being
 * refused" off a lookup that never ran is the same defect as a digest printing
 * advice for codes it never saw.
 */
function DeliverabilityRow({
  deliverability,
}: {
  readonly deliverability: MemberDeliverability;
}): JSX.Element {
  if (!deliverability.checked) {
    return (
      <dd className="member-note">
        Couldn't check just now — this is a live lookup, not a stored flag, so
        reload to ask again.
      </dd>
    );
  }
  const refused = refusedChannels(deliverability);
  if (refused.length === 0) {
    return (
      <dd className="member-note">
        No block on record. Neither channel is paused.
      </dd>
    );
  }
  return (
    <dd>
      <ul className="member-deliverability-list">
        {refused.map((r) => (
          <li key={r.key}>
            <strong>{r.key === "whatsapp" ? "WhatsApp" : "Email"}</strong> ·
            {` ${r.state} since `}{fmtDateTime(r.since)}
            {r.lapsesAt === null ? null : <> · lapses {fmtDateTime(r.lapsesAt)}</>}
            <br />
            <span className="member-note">{r.why}.</span>
          </li>
        ))}
      </ul>
      <p className="member-note">
        Sends are refused before the vendor is called, on every send over that
        channel including transactional ones — except that a full mailbox or an
        unreachable mail server never holds back a booking or receipt email.
        Nothing is stored and there is nothing to clear: a pause lapses by itself
        7 days after the first failure, 30 after the second and 90 after the
        third and later, counted from the latest, and lifts at once when the
        member writes to us on WhatsApp or a later message gets through. An
        email account that does not exist stays refused. To restore the channel
        sooner, correct the address or number above — the check keys on the
        identifier, so a corrected one carries no failure history and sends
        resume immediately.
      </p>
    </dd>
  );
}

/**
 * Default subject for an email reply: `Re: <last inbound email subject>` when
 * the member has previously emailed, else a neutral generic. `messages` is
 * newest-first, so the first matching inbound email is the most recent. Inbound
 * email bodies are stored as `"subject: <subject>\n<snippet>"` (the shape the
 * Gmail scanner / send_email preview write). The composer keeps this editable.
 */
function deriveEmailReplySubject(
  messages: ReadonlyArray<MemberDetailMessage>,
): string {
  const GENERIC = "Re: your recent message";
  const PREFIX = "subject: ";
  const lastInboundEmail = messages.find(
    (m) =>
      m.channel === "email" &&
      m.dir === "in" &&
      (m.body ?? "").toLowerCase().startsWith(PREFIX),
  );
  if (lastInboundEmail === undefined) return GENERIC;
  const firstLine = (lastInboundEmail.body ?? "").split("\n")[0] ?? "";
  const subject = firstLine.slice(PREFIX.length).trim();
  if (subject.length === 0) return GENERIC;
  return subject.toLowerCase().startsWith("re:") ? subject : `Re: ${subject}`;
}

/**
 * Sessions "left / total" for the credit tables (e.g. 0/1, 24/24, 4/4).
 * `left === null` → unlimited ("∞"); `total === null` → just the left count.
 */
function fmtLeftOfTotal(left: number | null, total: number | null): string {
  if (left === null) return "∞";
  return total === null ? String(left) : `${left}/${total}`;
}

interface SafetyFlag {
  readonly type: string;
  readonly summary: string;
}

/**
 * Pull `structured.flags` (set by the chat agent for injuries / medical /
 * caution items) off a session note, defensively — the column is free-form
 * jsonb, so every shape is validated before use.
 */
function extractFlags(structured: Record<string, unknown>): SafetyFlag[] {
  const raw = structured["flags"];
  if (!Array.isArray(raw)) return [];
  const out: SafetyFlag[] = [];
  for (const item of raw) {
    if (item !== null && typeof item === "object") {
      const o = item as Record<string, unknown>;
      const type = typeof o["type"] === "string" ? o["type"] : "flag";
      const summary = typeof o["summary"] === "string" ? o["summary"] : "";
      if (summary.length > 0) out.push({ type, summary });
    }
  }
  return out;
}

/**
 * The "⚠ Safety flags" banner — every injury / medical / caution flag across
 * this member's session notes, surfaced up top so a trainer sees it before the
 * next session. Each flag carries its own "×" that dispatches
 * `remove_session_note_flag` (session_note_id + the flag's type + summary),
 * removing that entry from the note's `structured.flags` and silently
 * refetching. Renders nothing when there are no flags.
 */
function SafetyFlagsBanner({
  notes,
  onChanged,
}: {
  readonly notes: ReadonlyArray<MemberDetailSessionNote>;
  readonly onChanged: () => void;
}): JSX.Element | null {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = notes.flatMap((n) =>
    extractFlags(n.structured).map((f) => ({
      noteId: n.id,
      type: f.type,
      summary: f.summary,
    })),
  );
  if (items.length === 0) return null;
  const remove = async (it: {
    noteId: string;
    type: string;
    summary: string;
  }): Promise<void> => {
    if (busyKey !== null) return;
    const key = `${it.noteId}:${it.type}:${it.summary}`;
    setBusyKey(key);
    setError(null);
    try {
      const r = await dispatchAction<
        { session_note_id: string; type: string; summary: string },
        { removed_count: number }
      >("remove_session_note_flag", {
        session_note_id: it.noteId,
        type: it.type,
        summary: it.summary,
      });
      if (!r.ok) {
        setError(r.message);
        return;
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "remove failed");
    } finally {
      setBusyKey(null);
    }
  };
  return (
    <div className="member-flags" role="alert">
      <strong>⚠ Safety flags</strong>
      <ul>
        {items.map((it, i) => (
          <li key={`${it.noteId}:${i}`}>
            <span className="member-flag-text">
              <Tag value={it.type} /> {it.summary}
            </span>
            <button
              type="button"
              className="member-flag-remove"
              title="Remove this safety flag"
              disabled={busyKey !== null}
              onClick={() => void remove(it)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      {error !== null ? <div className="member-flags-err">{error}</div> : null}
    </div>
  );
}

/**
 * The "⚑ Flag" banner — the free-text staff watch flag off
 * `members.metadata.staff_flag`, surfaced up top AND in the trainer morning
 * brief. The corner "×" clears it by dispatching `update_member` with an empty
 * `staff_flag` (the brief treats blank as "no flag"); the shallow metadata
 * merge preserves the rest of `metadata`. Renders nothing when unset.
 */
function StaffFlagBanner({
  memberId,
  flag,
  onChanged,
}: {
  readonly memberId: string;
  readonly flag: string;
  readonly onChanged: () => void;
}): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = flag.trim();
  if (trimmed.length === 0) return null;
  const clear = async (): Promise<void> => {
    if (busy || memberId === "") return;
    setBusy(true);
    setError(null);
    try {
      const r = await dispatchAction<
        { member_id: string; patch: Record<string, unknown> },
        Record<string, unknown>
      >("update_member", {
        member_id: memberId,
        patch: { metadata: { staff_flag: "" } },
      });
      if (!r.ok) {
        setError(r.message);
        return;
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "clear failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="member-flags" role="alert">
      <button
        type="button"
        className="member-flag-remove member-flag-remove-corner"
        title="Remove this flag"
        disabled={busy}
        onClick={() => void clear()}
      >
        ×
      </button>
      <strong>⚑ Flag</strong>
      <p>{trimmed}</p>
      {error !== null ? <div className="member-flags-err">{error}</div> : null}
    </div>
  );
}

function fmtMoney(minor: number, currency: string | null): string {
  const major = minor / 100;
  if (currency !== null && currency.length === 3) {
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(major);
    } catch {
      // Unknown currency code — fall through to a plain figure.
    }
  }
  return `${major.toFixed(2)}${currency ? ` ${currency}` : ""}`;
}

/** Map a status-ish string to a tag tone (green = good, red = bad). */
function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (
    ["active", "completed", "attended", "paid", "succeeded"].includes(s)
  ) {
    return "tag-success";
  }
  if (
    [
      "cancelled",
      "canceled",
      "failed",
      "no_show",
      "past_due",
      "opted_out",
      "deleted",
      "refund",
      "exited_by_trigger",
    ].includes(s)
  ) {
    return "tag-error";
  }
  return "";
}

function Tag({ value }: { readonly value: string }): JSX.Element {
  return <span className={`tag ${statusTone(value)}`}>{value}</span>;
}

// ---------------------------------------------------------------------------
// Metadata cards — render `members.metadata` as labelled cards instead of a raw
// JSON blob. Short scalars pack many per row; long strings + nested objects get
// a wider / full-width card. The consultation + consultation_extraction objects
// render as friendly-labelled sub-grids.
// ---------------------------------------------------------------------------

const FRIENDLY_META_KEY: Record<string, string> = {
  city: "City",
  vest: "Vest",
  trouser: "Trouser",
  // Without an entry the derived label is the raw key title-cased — "Dob",
  // which reads as a typo rather than a field. 417 members carry one.
  dob: "Date of birth",
  source: "Source",
  country: "Country",
  feedback: "Feedback",
  klaviyo_id: "Klaviyo ID",
  codexfit_id: "Codex ID",
  injury_flag: "Injury flag",
  staff_flag: "Staff flag",
  klaviyo_tags: "Klaviyo tags",
  klaviyo_status: "Klaviyo status",
  referral_code: "Referral code",
  initial_source: "Initial source",
  import_source: "Import source",
  onboarding: "Onboarding questionnaire",
  consultation: "Consultation form",
  consultation_extraction: "Consultation extraction (OCR)",
};

function prettyMetaKey(k: string): string {
  return (
    FRIENDLY_META_KEY[k] ??
    k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** Friendly labels for the consultation `CF_*` keys. */
const CF_FIELD_LABEL: Record<string, string> = {
  CF_3_Goal: "Goal",
  CF_3_Goal_1: "Goal (2)",
  CF_3_Goal_2: "Goal (3)",
  CF_3_Goal_3: "Goal (4)",
  CF_5_FocusArea: "Focus area 1",
  CF_5_FocusArea_1: "Focus area 2",
  CF_5_FocusArea_2: "Focus area 3",
  CF_8_Injuries: "Injuries",
  CF_8_Injuries_Text: "Injury details",
  CF_11_TrainingPlan_WeeklySessions: "Weekly sessions",
  CF_11_TrainingPlan_SpecificGoal: "Specific goal",
};

function scalarToString(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

function isScalar(v: unknown): boolean {
  return v === null || (typeof v !== "object" && typeof v !== "function");
}

/** A sub-grid of label/value items (the body of the consultation cards). */
function MetaSubGrid({
  obj,
  labels,
}: {
  readonly obj: Record<string, unknown>;
  readonly labels?: Record<string, string>;
}): JSX.Element {
  const entries = Object.entries(obj)
    .filter(([, v]) => isScalar(v) && scalarToString(v).trim().length > 0 && v !== null)
    .sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) {
    return <div className="meta-card-val">—</div>;
  }
  return (
    <div className="meta-sub">
      {entries.map(([k, v]) => (
        <div className="meta-sub-item" key={k}>
          <div className="meta-sub-key">{labels?.[k] ?? prettyMetaKey(k)}</div>
          <div className="meta-sub-val">{scalarToString(v)}</div>
        </div>
      ))}
    </div>
  );
}

/** Friendly labels for the OCR lifestyle extras (postcode is promoted to the
 *  canonical top-level `metadata.postcode`, so it's not shown here). */
const LIFESTYLE_LABEL: Record<string, string> = {
  sleep_hours: "Sleep",
  alcohol: "Alcohol",
  smoking: "Smoking",
  diet: "Diet",
};

/** The OCR `fitness_routine` array rendered as a readable list. */
function FitnessRoutine({
  rows,
}: {
  readonly rows: ReadonlyArray<unknown>;
}): JSX.Element | null {
  const lines = rows
    .filter((r): r is Record<string, unknown> => r !== null && typeof r === "object")
    .map((r) =>
      [r["activity"], r["frequency"], r["duration"]]
        .map((x) => (typeof x === "string" && x.trim().length > 0 ? x.trim() : null))
        .filter((x): x is string => x !== null)
        .join(" · "),
    )
    .filter((l) => l.length > 0);
  if (lines.length === 0) return null;
  return (
    <div className="meta-routine">
      <div className="meta-sub-key">Fitness routine</div>
      <ul className="meta-routine-list">
        {lines.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The `consultation_extraction` card body — surfaces the useful extras the OCR
 * captured beyond the main consultation fields (lifestyle + fitness routine),
 * with the provenance (model / confidence / date + the full raw blob) tucked
 * into a footnote + collapsible. Postcode is NOT shown here: it's promoted to
 * the canonical top-level `metadata.postcode` (its own always-present row in
 * Profile details + the member frontend) so it's never displayed/stored as a
 * second, divergent copy. That row is what makes this exclusion safe, and it
 * only became true on 2026-09-23 — until then this comment pointed at a row
 * that existed for the 30 members who already had a postcode and nobody else.
 */
function ConsultationExtractionBody({
  data,
}: {
  readonly data: Record<string, unknown>;
}): JSX.Element {
  const raw =
    data["raw"] !== null &&
    typeof data["raw"] === "object" &&
    !Array.isArray(data["raw"])
      ? (data["raw"] as Record<string, unknown>)
      : {};
  const lifestyle =
    raw["lifestyle"] !== null &&
    typeof raw["lifestyle"] === "object" &&
    !Array.isArray(raw["lifestyle"])
      ? (raw["lifestyle"] as Record<string, unknown>)
      : {};
  // Postcode is intentionally excluded — it's promoted to the canonical
  // top-level `metadata.postcode` (shown as its own always-present row in
  // Profile details + shared with the member frontend), so it isn't duplicated
  // in this OCR-extras card.
  const facts: Record<string, unknown> = { ...lifestyle };
  const routine = Array.isArray(raw["fitness_routine"]) ? raw["fitness_routine"] : [];
  const model = typeof data["model"] === "string" ? data["model"] : null;
  const extractedAt =
    typeof data["extracted_at"] === "string" ? data["extracted_at"] : null;
  const conf =
    typeof data["overall_confidence"] === "number"
      ? data["overall_confidence"]
      : null;
  // `apply_consultation_extraction` / the WhatsApp intake stamp the channel the
  // answers were confirmed through, and the intake a WhatsApp photo came from.
  const fromWhatsAppPhoto = typeof data["picture_intake_id"] === "string";
  const channelNote =
    data["channel"] === "whatsapp"
      ? " · confirmed on WhatsApp"
      : data["channel"] === "dashboard" && fromWhatsAppPhoto
        ? " · from a WhatsApp photo, filed on the dashboard"
        : "";
  return (
    <div>
      <MetaSubGrid obj={facts} labels={LIFESTYLE_LABEL} />
      <FitnessRoutine rows={routine} />
      <p className="meta-provenance">
        {model !== null ? `Extracted by ${model}` : "OCR extraction"}
        {extractedAt !== null ? ` · ${fmtDate(extractedAt)}` : ""}
        {conf !== null ? ` · ${Math.round(conf * 100)}% confidence` : ""}
        {channelNote}
      </p>
      <details className="meta-raw">
        <summary>Full raw extraction</summary>
        <CollapsibleJson value={data} />
      </details>
    </div>
  );
}

/** Friendly labels for the Hercie-app onboarding questionnaire answers. */
const ONBOARDING_LABEL: Record<string, string> = {
  genderPreference: "Trainer gender",
  personality: "Trainer personality",
  motivation: "Motivation style",
  intensity: "Workout intensity",
  communication: "Communication style",
  fitnessLevel: "Fitness level",
  activities: "Activities",
  goals: "Goals",
  medicalConditions: "Medical conditions",
};

/** Display order — mirrors Hercie's "Preferences" screen. */
const ONBOARDING_ORDER = [
  "genderPreference",
  "personality",
  "motivation",
  "intensity",
  "communication",
  "fitnessLevel",
  "activities",
  "goals",
  "medicalConditions",
];

/** An onboarding answer counts as answered when it isn't blank / "skipped". */
function onboardingAnswered(v: unknown): boolean {
  if (Array.isArray(v)) {
    return v.filter(isScalar).map(scalarToString).some((x) => x.trim().length > 0);
  }
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s.length > 0 && s !== "skipped";
  }
  return v !== null && v !== undefined;
}

function onboardingValueText(v: unknown): string {
  if (Array.isArray(v)) {
    return v
      .filter(isScalar)
      .map((x) => scalarToString(x).trim())
      .filter((x) => x.length > 0)
      .join(", ");
  }
  return scalarToString(v).trim();
}

/**
 * The `metadata.onboarding` card — the member's Hercie-app onboarding
 * questionnaire answers (trainer preferences, motivation style, activities,
 * goals, medical conditions…), rendered with friendly labels in the
 * Preferences-screen order. Unanswered / "skipped" questions are omitted.
 */
function OnboardingBody({
  data,
  memberId,
  onSaved,
}: {
  readonly data: Record<string, unknown>;
  readonly memberId: string;
  readonly onSaved: () => void;
}): JSX.Element {
  const keys = [
    ...ONBOARDING_ORDER.filter((k) => k in data),
    ...Object.keys(data).filter((k) => !ONBOARDING_ORDER.includes(k)),
  ];
  const answered = keys
    .filter((k) => onboardingAnswered(data[k]))
    .map((k) => [k, onboardingValueText(data[k])] as const)
    .filter(([, text]) => text.length > 0);
  if (answered.length === 0) {
    return <p className="empty">No questions answered yet.</p>;
  }
  // The same label/value table Profile details uses — these ARE profile fields,
  // just ones the member filled in rather than staff. Rendering them as a grid
  // of little cards made the same kind of fact look like a different kind.
  // `kvs` is what carries `display: grid`; without it the template columns are
  // inert and every label stacks above its value.
  return (
    <dl className="kvs member-profile-grid">
      {answered.map(([k]) => (
        <Fragment key={k}>
          <dt title={ONBOARDING_LABEL[k] ?? prettyMetaKey(k)}>
            {ONBOARDING_LABEL[k] ?? prettyMetaKey(k)}
          </dt>
          <OnboardingFieldEditor
            memberId={memberId}
            answers={data}
            fieldKey={k}
            onSaved={onSaved}
          />
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * One editable onboarding answer — the Profile-details row, backed by the
 * nested `metadata.onboarding` object.
 *
 * Cannot reuse `MetadataFieldEditor`: that one patches a TOP-LEVEL metadata
 * key, and `update_member` merges metadata shallowly (`metadata || patch`), so
 * sending `{onboarding: {goals: "…"}}` would replace the whole answer set with
 * a single key. This spreads the current answers and overwrites one.
 */
function OnboardingFieldEditor({
  memberId,
  answers,
  fieldKey,
  onSaved,
  metaKey = "onboarding",
  valueText = onboardingValueText,
}: {
  readonly memberId: string;
  readonly answers: Record<string, unknown>;
  readonly fieldKey: string;
  readonly onSaved: () => void;
  /**
   * Which top-level metadata object the answers live in. Legacy Hercie-app
   * answers sit in `onboarding`; native webchat answers sit in `preferences`
   * under `"Onboarding - <Field>"` keys — same shallow-merge rule either way,
   * so the editor spreads the WHOLE object and overwrites one key.
   */
  readonly metaKey?: "onboarding" | "preferences";
  /** Display formatter — preferences values need the FE-sentinel cleaner. */
  readonly valueText?: (v: unknown) => string;
}): JSX.Element {
  const raw = answers[fieldKey];
  const wasArray = Array.isArray(raw);
  const shown = valueText(raw);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(shown);
  const dispatch = useDispatch<
    { readonly member_id: string; readonly patch: Record<string, unknown> },
    Record<string, unknown>
  >("update_member");

  const save = async (): Promise<void> => {
    if (dispatch.loading || memberId === "") return;
    const trimmed = draft.trim();
    const next: unknown =
      trimmed === ""
        ? null
        : wasArray
          ? trimmed
              .split(",")
              .map((x) => x.trim())
              .filter((x) => x.length > 0)
          : trimmed;
    const r = await dispatch.run({
      member_id: memberId,
      // Whole object, one key changed — see the note above on shallow merge.
      patch: { metadata: { [metaKey]: { ...answers, [fieldKey]: next } } },
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  return (
    <ProfileEditRow
      editing={editing}
      onEdit={() => {
        setDraft(shown);
        dispatch.reset();
        setEditing(true);
      }}
      display={shown.length > 0 ? shown : "—"}
      editor={
        <input
          className="profile-field-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") {
              dispatch.reset();
              setEditing(false);
            }
          }}
        />
      }
      onSave={() => void save()}
      onCancel={() => {
        dispatch.reset();
        setEditing(false);
      }}
      loading={dispatch.loading}
      error={dispatch.error?.message ?? null}
    />
  );
}

/** A modal popup — matches the dashboard's standard `.modal-*` classes. */
function MetaModal({
  title,
  onClose,
  children,
  actions,
  hideFooter,
}: {
  /**
   * OMIT for a modal whose content already announces itself — the Book picker
   * leads with a date header, and "Book <name> into a session" above it just
   * restated the button that had been pressed a moment earlier.
   */
  readonly title?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /**
   * The modal's primary action(s), rendered on the SAME row as Close and to its
   * right. Passing them here rather than inside `children` is what lets the
   * footer be one row: a child-rendered `.modal-actions` block is a second row
   * by construction, which left Close stranded underneath.
   */
  readonly actions?: ReactNode;
  /**
   * Suppress the modal's own Close footer — for content that owns a commit
   * button of its own and must sit on the SAME row as Close. The Buy form is
   * the case: its confirm button belongs to the payment section (it changes
   * with the chosen method), so it can't be hoisted to `actions`, and a
   * modal-level Close underneath would be a second, redundant footer row.
   * Such content renders its own Close and is handed `onClose` to wire it.
   */
  readonly hideFooter?: boolean;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Portaled to <body> so the fixed overlay isn't clipped/mispositioned when the
  // trigger lives inside a draggable member-card (which establishes a transform
  // containing block).
  // Press AND release on the backdrop — a scrollbar press on a tall modal's
  // overlay, or a text-selection drag-out, must not close it (shared hook).
  const backdrop = useBackdropDismiss(onClose);
  return createPortal(
    <div className="modal-backdrop" role="presentation" {...backdrop}>
      <div className="modal-card" role="dialog" aria-modal="true">
        {title !== undefined ? <h3 className="modal-title">{title}</h3> : null}
        {children}
        {/* Close first (left), primary action last (right). With both present
            the row splits to the edges so the dismissive and the committing
            action are not adjacent — a mis-click either way is cheap to make
            and annoying to undo. */}
        {hideFooter === true ? null : (
          <div
            className={`modal-actions${actions !== undefined ? " modal-actions-split" : ""}`}
          >
            <button
              type="button"
              className="action-dispatch-btn"
              onClick={onClose}
            >
              Close
            </button>
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Header action buttons — Book + Purchase. Both reuse the calendar's machinery:
// Purchase opens the shared `PurchaseCreditsModal` (no slot context); Book
// opens an availability picker (`list_available_slots`), lets staff pick a
// slot, dispatches `book_session({member_id, slot_id})`, and on a no-credit
// outcome surfaces the Purchase modal wired to auto-book the picked slot.
// ---------------------------------------------------------------------------

/** book_session failure codes that mean "this member has no usable credit". */
const MEMBER_CREDIT_FAIL_RE =
  /insufficient_credits|credit_source_drained|package_slug_invalid/i;

/** Local-midnight carrier for a day (the convention `localDateKey` expects). */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addLocalDays(d: Date, n: number): Date {
  const out = startOfLocalDay(d);
  out.setDate(out.getDate() + n);
  return out;
}
/** "Wed, 5 Aug 2026" — the Calendar toolbar's day format. */
function fmtBookDay(d: Date): string {
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
/**
 * A slot staff cannot book this member into: inactive, or every seat taken.
 * Hidden slots stay bookable — `hidden` means "off the CLIENT-facing booking
 * site", which is precisely the case where staff book on someone's behalf.
 */
function isSlotUnbookable(s: CalendarSlot): boolean {
  return s.status !== "active" || s.taken_count >= s.capacity;
}

/** How far ahead the picker loads availability, in days. */
const BOOK_PICKER_DAYS = 60;
/** Fallback timeline window when a day has no slots to derive one from. */
const BOOK_WIN_FALLBACK_START = 7 * 60;
const BOOK_WIN_FALLBACK_END = 21 * 60;
/** Padding above the first and below the last slot, so blocks aren't flush. */
const BOOK_WIN_PAD_MIN = 30;

/**
 * The Book availability picker — one DAY at a time, drawn by the same
 * `<CalendarTimeline>` the Calendar page uses, with ‹ › to step between days.
 *
 * A flat list of "the next 100 open slots" answered the wrong question. Staff
 * booking someone in are working from a day — "can they do Tuesday morning?" —
 * and a list can neither show that 09:00 is taken while 09:45 is free, nor that
 * there is a three-hour gap after lunch. The timeline shows the day's real
 * shape, and it is the shape staff already know from `/calendar`.
 *
 * Reads `GET /api/calendar`, not `list_available_slots`: it returns EVERY slot
 * with `capacity` / `taken_count` / `trainer_name`, so full slots can be drawn
 * greyed-out for context instead of silently vanishing and leaving a hole where
 * an 09:00 obviously exists. One fetch covers the whole window, so stepping
 * between days is instant and never re-hits the network.
 */
function BookSlotPicker({
  memberId,
  memberName,
  onClose,
  onBooked,
}: {
  readonly memberId: string;
  readonly memberName: string;
  readonly onClose: () => void;
  readonly onBooked: () => void;
}): JSX.Element {
  const [slots, setSlots] = useState<ReadonlyArray<CalendarSlot> | null>(null);
  const [anchor, setAnchor] = useState<Date>(() => startOfLocalDay(new Date()));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pickedSlotId, setPickedSlotId] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // No-credit remedy → the shared purchase modal, wired to auto-book.
  const [noCredit, setNoCredit] = useState(false);
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);
  const run = useSagaRun(pendingRunId);

  // Bucket by STUDIO day — `isoToZonedDateKey`, never the browser's date, or a
  // 07:00 studio slot lands on the previous day for staff working abroad.
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarSlot[]>();
    for (const s of slots ?? []) {
      const k = isoToZonedDateKey(s.starts_at);
      const list = m.get(k);
      if (list === undefined) m.set(k, [s]);
      else list.push(s);
    }
    return m;
  }, [slots]);

  const anchorKey = localDateKey(anchor);
  const daySlots = byDay.get(anchorKey) ?? [];

  /**
   * Fit the hour rail to the day rather than always drawing 07:00–21:00: a
   * modal is short, and E-Pulsive's slots cluster in a few morning hours, so a
   * fixed window would spend most of the grid on empty evening.
   */
  const win = useMemo(() => {
    if (daySlots.length === 0) {
      return { start: BOOK_WIN_FALLBACK_START, end: BOOK_WIN_FALLBACK_END };
    }
    let lo = Infinity;
    let hi = -Infinity;
    for (const s of daySlots) {
      const start = slotStartMin(s.starts_at);
      lo = Math.min(lo, start);
      hi = Math.max(hi, start + slotDurationMin(s));
    }
    return {
      start: Math.max(0, Math.floor((lo - BOOK_WIN_PAD_MIN) / 60) * 60),
      end: Math.min(24 * 60, Math.ceil((hi + BOOK_WIN_PAD_MIN) / 60) * 60),
    };
  }, [daySlots]);

  /** Nearest LATER day that has any slot — powers the empty day's shortcut. */
  const nextDayWithSlots = useMemo(() => {
    const keys = [...byDay.keys()].filter((k) => k > anchorKey).sort();
    const first = keys[0];
    if (first === undefined) return null;
    const [y, m, d] = first.split("-").map(Number);
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  }, [byDay, anchorKey]);

  // One read covers the whole window; day-stepping is then pure client work.
  // `trainer_name` arrives resolved on each slot, so there is no roster lookup.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const from = startOfLocalDay(new Date());
        const to = new Date(from);
        to.setDate(to.getDate() + BOOK_PICKER_DAYS);
        const res = await api.calendar(from.toISOString(), to.toISOString());
        if (cancelled) return;
        setSlots(res.slots);
        // Open on the first day that HAS availability rather than on today.
        // E-Pulsive publishes availability a month out in blocks, so "today"
        // is usually empty and opening there looks like a broken picker.
        const firstOpen = res.slots
          .filter((s) => s.status === "active" && s.taken_count < s.capacity)
          .map((s) => Date.parse(s.starts_at))
          .sort((a, b) => a - b)[0];
        if (firstOpen !== undefined) {
          setAnchor(startOfLocalDay(new Date(firstOpen)));
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(`Couldn't load availability: ${(e as Error).message}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh once the book_session saga reaches a terminal state.
  useEffect(() => {
    if (pendingRunId === null || run.state === "in_progress") return;
    setPendingRunId(null);
    setBooking(false);
    if (run.state === "done") {
      onBooked();
      return;
    }
    if (run.state === "failed") {
      const err = run.last_error ?? "unknown error";
      if (MEMBER_CREDIT_FAIL_RE.test(err)) {
        setNoCredit(true);
        setActionError(null);
      } else {
        setActionError(`Booking failed: ${err}`);
      }
      return;
    }
    if (run.state === "forbidden") {
      // The booking was dispatched and runs regardless — only the status
      // READ was refused. Do not call it a failure or a timeout.
      setActionError(
        `The booking was dispatched, but this account was refused permission to check its progress (${run.last_error ?? "forbidden"}). Refresh to see whether it landed.`,
      );
      return;
    }
    setActionError("Timed out waiting for the booking — refresh to check.");
  }, [run.state, run.last_error, pendingRunId, onBooked]);

  const book = useCallback(async () => {
    if (pickedSlotId === null) {
      setActionError("Pick a slot first.");
      return;
    }
    setBooking(true);
    setActionError(null);
    setNoCredit(false);
    try {
      // Pre-flight credit check — a doomed book_session saga can take minutes to
      // surface insufficient_credits, so jump straight to the remedy when the
      // member definitively holds no usable credit (`book-credit-preflight`,
      // shared with the calendar's Book modal).
      if (await bookPreflightFindsNoCredit(memberId, dispatchAction)) {
        setBooking(false);
        setNoCredit(true);
        return;
      }
      const res = await dispatchAction<
        Record<string, unknown>,
        { run_id?: string }
      >("book_session", { slot_id: pickedSlotId, member_id: memberId });
      if (res.ok) {
        const rid = res.response?.run_id;
        if (typeof rid === "string") {
          setPendingRunId(rid);
        } else {
          setBooking(false);
          onBooked();
        }
        return;
      }
      setBooking(false);
      if (MEMBER_CREDIT_FAIL_RE.test(`${res.code} ${res.message}`)) {
        setNoCredit(true);
      } else {
        setActionError(`Booking failed: ${res.message || res.code}`);
      }
    } catch (e) {
      setBooking(false);
      setActionError((e as Error).message);
    }
  }, [pickedSlotId, memberId, onBooked]);

  return (
    <MetaModal
      onClose={onClose}
      // No title: the date header below already says what this is, and the
      // primary action rides in the footer beside Close rather than in its own
      // row above it.
      actions={
        noCredit ? undefined : (
          <button
            type="button"
            className="action-dispatch-btn"
            disabled={pickedSlotId === null || booking}
            onClick={() => void book()}
          >
            {booking ? "Booking…" : "Book in"}
          </button>
        )
      }
    >
      {noCredit ? (
        <PurchaseCreditsModal
          memberId={memberId}
          headline={`${memberName} has no available credits for this session. Sell or comp a package or membership:`}
          {...(pickedSlotId !== null
            ? {
                autoBook: {
                  slotId: pickedSlotId,
                  onBookStarted: (rid: string) => {
                    setNoCredit(false);
                    setBooking(true);
                    setPendingRunId(rid);
                  },
                  onBookedSync: () => {
                    setNoCredit(false);
                    onBooked();
                  },
                },
              }
            : {})}
          onPurchased={() => {
            /* paid link only — staff send it, then book once paid */
          }}
        />
      ) : (
        <>
          {loadError !== null ? (
            <p className="cal-action-error">{loadError}</p>
          ) : slots === null ? (
            <p className="member-note">Loading availability…</p>
          ) : (
            <>
              <div className="book-day-nav">
                <button
                  type="button"
                  aria-label="Previous day"
                  onClick={() => setAnchor((d) => addLocalDays(d, -1))}
                >
                  ‹
                </button>
                <h4 className="book-day-label">{fmtBookDay(anchor)}</h4>
                <button
                  type="button"
                  aria-label="Next day"
                  onClick={() => setAnchor((d) => addLocalDays(d, 1))}
                >
                  ›
                </button>
              </div>
              {daySlots.length === 0 ? (
                <p className="book-day-empty">
                  No slots on this day
                  {nextDayWithSlots !== null ? (
                    <>
                      {" · "}
                      <button
                        type="button"
                        className="book-day-jump"
                        onClick={() => setAnchor(nextDayWithSlots)}
                      >
                        jump to {fmtBookDay(nextDayWithSlots)}
                      </button>
                    </>
                  ) : null}
                </p>
              ) : (
                <div className="book-day-grid">
                  <CalendarTimeline
                    days={[anchor]}
                    slotsForDay={(key) => byDay.get(key) ?? []}
                    winStart={win.start}
                    winEnd={win.end}
                    selectedSlotId={pickedSlotId}
                    isSlotDisabled={isSlotUnbookable}
                    onOpen={(s) => {
                      if (isSlotUnbookable(s)) return;
                      setPickedSlotId((cur) => (cur === s.id ? null : s.id));
                    }}
                    // No onReschedule / onCreateAt: this grid is for CHOOSING.
                  />
                </div>
              )}
            </>
          )}
          {actionError !== null ? (
            <p className="cal-action-error">{actionError}</p>
          ) : null}
        </>
      )}
    </MetaModal>
  );
}

/**
 * The member header's action cluster, sat at the right of the title row.
 *
 * The two everyday jobs (Book, Buy) keep a labelled button of their own; the
 * occasional ones (consultation / enrichment / transfer / merge / export /
 * coupon / GDPR delete) collapse into the same "+" `<PopMenu>` the calendar
 * toolbar uses, so a many-button bar doesn't compete with the member's name
 * for the row.
 */
function MemberActions({
  memberId,
  memberName,
  displayName,
  metadata,
  packages,
  memberships,
  mergeCounts,
  consultationForms,
  erased,
  onChanged,
}: {
  readonly memberId: string;
  readonly memberName: string;
  readonly displayName: string | null;
  /** Backs the enrichment + consultation panels the "+" menu opens. */
  readonly metadata: Record<string, unknown>;
  /** Consultation forms photographed on WhatsApp about this member — the consultation panel lists them. */
  readonly consultationForms: ReadonlyArray<MemberDetailConsultationForm>;
  /** This member's packages — threaded to the Transfer modal (no extra fetch). */
  readonly packages: ReadonlyArray<MemberDetailPackage>;
  /** …and memberships, a transfer source since 2026-10-29 (same modal, same fetch). */
  readonly memberships: ReadonlyArray<MemberDetailMembership>;
  /** Money/schedule counts — the Merge modal's "before you do this" warning. */
  readonly mergeCounts: MergeMemberCounts;
  /**
   * Erased by `delete_member` (box 17): the three tools that write the
   * member's record — the consultation form, AI enrichment and the erasure
   * itself — are switched off, since the server refuses each.
   */
  readonly erased: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const erasedTitle = erased
    ? "This member was erased (GDPR Art. 17), so their record can't be changed"
    : undefined;
  // The members list this profile was opened from (`membersListState`).
  const location = useLocation();
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Set once delete_member succeeds: the profile under the modal is now a
  // scrubbed shell, so Close leaves for the members list instead of
  // refetching a page of NULLs.
  const deletedRef = useRef(false);
  // The coupon modal's "Applies to" lists every catalog item, so the catalog
  // must be IN HAND before the ActionModal mounts (its field state is a lazy
  // useState — options that arrive later never reach it). Fetched on first
  // open, kept across reopens; the modal render gates on it below.
  const [couponCatalog, setCouponCatalog] = useState<CouponCatalog | null>(
    null,
  );
  const [couponCatalogError, setCouponCatalogError] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!couponOpen || couponCatalog !== null) return undefined;
    let cancelled = false;
    setCouponCatalogError(null);
    void Promise.all([
      api.lookupOptions("packages"),
      api.lookupOptions("memberships"),
    ]).then(
      ([p, m]) => {
        if (cancelled) return;
        setCouponCatalog({
          packages: p.options.map((o) => ({ value: o.value, label: o.label })),
          memberships: m.options.map((o) => ({
            value: o.value,
            label: o.label,
          })),
        });
      },
      (e: unknown) => {
        if (cancelled) return;
        setCouponCatalogError(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [couponOpen, couponCatalog]);
  return (
    <div className="member-actions">
      <button
        type="button"
        className="action-dispatch-btn member-action-book"
        onClick={() => setBookOpen(true)}
      >
        Book
      </button>
      <button
        type="button"
        className="action-dispatch-btn member-action-purchase"
        onClick={() => setPurchaseOpen(true)}
      >
        Buy
      </button>
      <PopMenu
        label="More member tools"
        triggerClassName="action-dispatch-btn member-action-more"
        trigger="+"
      >
        {(close) => (
          <>
            <button
              type="button"
              disabled={erased}
              title={erasedTitle}
              onClick={() => {
                close();
                setConsultOpen(true);
              }}
            >
              Consultation form
            </button>
            <button
              type="button"
              disabled={erased}
              title={erasedTitle}
              onClick={() => {
                close();
                setEnrichOpen(true);
              }}
            >
              AI enrichment
            </button>
            <button
              type="button"
              onClick={() => {
                close();
                setTransferOpen(true);
              }}
            >
              Transfer credits
            </button>
            <button
              type="button"
              onClick={() => {
                close();
                setMergeOpen(true);
              }}
            >
              Merge profiles
            </button>
            {/* These two open <ActionModal> from MemberDetail's own state, NOT
                <ActionModalButton>: closing the PopMenu unmounts everything it
                renders, which would take a self-hosted modal with it. */}
            <button
              type="button"
              onClick={() => {
                close();
                setExportOpen(true);
              }}
            >
              Export data
            </button>
            <button
              type="button"
              onClick={() => {
                close();
                setCouponOpen(true);
              }}
            >
              Create coupon
            </button>
            <button
              type="button"
              className="pop-menu-danger"
              disabled={erased}
              title={erasedTitle}
              onClick={() => {
                close();
                setDeleteOpen(true);
              }}
            >
              Delete member (GDPR)
            </button>
          </>
        )}
      </PopMenu>
      {/* No title, no lead sentence: the Buy button that opened this said
          both, and the three sections below name themselves. */}
      {purchaseOpen ? (
        <MetaModal hideFooter onClose={() => setPurchaseOpen(false)}>
          <PurchaseCreditsModal
            memberId={memberId}
            onPurchased={() => onChanged()}
            onClose={() => setPurchaseOpen(false)}
          />
        </MetaModal>
      ) : null}
      {transferOpen ? (
        <MetaModal
          title={`Transfer credits — from ${memberName}`}
          onClose={() => setTransferOpen(false)}
        >
          <TransferCreditsModal
            fromMemberId={memberId}
            fromMemberName={memberName}
            sourcePackages={packages}
            sourceMemberships={memberships}
            onDone={() => onChanged()}
          />
        </MetaModal>
      ) : null}
      {mergeOpen ? (
        <MetaModal
          title={`Merge profiles — ${memberName}`}
          onClose={() => setMergeOpen(false)}
        >
          <MergeMemberModal
            memberId={memberId}
            memberName={memberName}
            counts={mergeCounts}
            onDone={(survivorMemberId) => {
              // The absorbed profile is soft-deleted, so staying on it would
              // show a closed account. Land on the survivor instead — unless
              // that IS this page, in which case a refresh is enough.
              if (survivorMemberId === memberId) {
                onChanged();
                return;
              }
              // The survivor's "← Members" keeps returning to the same list.
              navigate(`/members/${survivorMemberId}`, { state: location.state });
            }}
          />
        </MetaModal>
      ) : null}
      {consultOpen ? (
        <MetaModal
          title={`Consultation form — ${memberName}`}
          onClose={() => setConsultOpen(false)}
        >
          <ConsultationSection
            memberId={memberId}
            metadata={metadata}
            displayName={displayName}
            forms={consultationForms}
            onSaved={() => onChanged()}
          />
        </MetaModal>
      ) : null}
      {enrichOpen ? (
        <MetaModal
          title={`AI enrichment — ${memberName}`}
          onClose={() => setEnrichOpen(false)}
        >
          <AiEnrichmentPanel
            memberId={memberId}
            enrichment={readAiEnrichment(metadata)}
            onEnriched={() => onChanged()}
          />
        </MetaModal>
      ) : null}
      {exportOpen ? (
        <ActionModal
          action="export_member_data"
          onClose={() => setExportOpen(false)}
          modalTitle={`Export member data — ${memberName}`}
          description="GDPR data export: every canonical row held for this member, as JSON."
          submitLabel="Generate export"
          prefill={{ member_id: memberId }}
          renderSuccess={(resp) => (
            <ExportDownload data={resp} memberId={memberId} />
          )}
        />
      ) : null}
      {couponOpen ? (
        couponCatalog !== null ? (
          <ActionModal
            action="create_stripe_coupon"
            onClose={() => setCouponOpen(false)}
            modalTitle="Create a discount code"
            description="Mint a percentage-off code in Stripe. Members can quote it in chat or enter it on the booking site; staff can apply it to a payment link. A fully-ticked group covers everything in it, including items added later; deselect anything and the code is pinned to exactly the ticked items."
            submitLabel="Create coupon"
            fields={couponFields(memberId, memberName, couponCatalog)}
            validate={makeCouponValidate(
              new Set(couponCatalog.memberships.map((m) => m.value)),
            )}
            renderSuccess={(resp) => <CouponCreated response={resp} />}
          />
        ) : (
          <MetaModal
            title="Create a discount code"
            onClose={() => setCouponOpen(false)}
          >
            {couponCatalogError !== null ? (
              <p className="action-modal-error">
                Couldn&apos;t load the catalog: {couponCatalogError}
              </p>
            ) : (
              <p className="empty">Loading the catalog…</p>
            )}
          </MetaModal>
        )
      ) : null}
      {deleteOpen ? (
        <ActionModal
          action="delete_member"
          onClose={() => {
            setDeleteOpen(false);
            if (deletedRef.current) navigate(membersListHref(location.state));
          }}
          modalTitle={`Delete member (GDPR) — ${memberName}`}
          description="Art. 17 erasure: permanently clears this member's name, email, phone, metadata and consent, scrubs their stored message history (WhatsApp, email and Instagram bodies, delivery events and chat-agent logs) for identifiers no other member shares, removes their session notes, feedback comments and chat-channel links, and marks the profile deleted. Bookings, payments and audit history are kept, anonymised, for reconciliation. Their comments on the studio's Instagram posts and their Instagram DM-thread record are kept as they are, by decision. This cannot be undone."
          submitLabel="Delete member"
          prefill={{ member_id: memberId }}
          fields={DELETE_MEMBER_FIELDS}
          validate={validateDeleteConfirm}
          transformInput={stripDeleteConfirm}
          // NOT onChanged(): the page under the modal would refetch a scrubbed
          // shell. The flag makes Close navigate to /members instead.
          onDone={() => {
            deletedRef.current = true;
          }}
          renderSuccess={(resp) => <DeleteMemberDone response={resp} />}
        />
      ) : null}
      {bookOpen ? (
        <BookSlotPicker
          memberId={memberId}
          memberName={memberName}
          onClose={() => setBookOpen(false)}
          onBooked={() => {
            setBookOpen(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/** The two catalog lists the coupon form's "Applies to" is built from. */
interface CouponCatalog {
  readonly packages: ReadonlyArray<{ value: string; label: string }>;
  readonly memberships: ReadonlyArray<{ value: string; label: string }>;
}

/** Mirror of `create_stripe_coupon`'s CUSTOM_CODE_PATTERN. */
const COUPON_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{2,63}$/;

/**
 * The `create_stripe_coupon` form.
 *
 * The scope and the audience are LAYER concepts, not Stripe ones: packages
 * are billed as inline `price_data` with no Product ids, so Stripe's own
 * `applies_to[products]` has nothing to bind to, and Stripe has no notion of a
 * code belonging to one customer at all. Both ride the coupon's metadata and are
 * enforced at every redemption path. `membership_months` IS native Stripe — it
 * maps onto `duration`/`duration_in_months`, which is why the form asks for
 * months rather than making staff pick a Stripe duration enum.
 *
 * "Applies to" is a grouped MULTISELECT over the real catalog (2026-08-14):
 * a fully-ticked selection collapses server-side to the class summary (so it
 * keeps covering items added later), while any subset pins the code to the
 * ticked slugs — "everything except the trial" and "trial only" are both one
 * gesture. It defaults to everything ticked, which is exactly the old
 * `applies_to: "both"` default.
 *
 * The audience is a `select` whose "anyone" option is the EMPTY string, because
 * `ActionModal` drops empty optional fields before dispatch — so "Anyone" mints
 * an unbound code with no special-casing anywhere. It defaults to this member:
 * the form is opened from their page, so a code scoped to someone by accident is
 * a likelier mistake than a campaign code scoped by accident.
 *
 * The caps are TWO fields (2026-08-14): "per member" (Layer-enforced;
 * defaults to 1 so an anyone-code is once-per-member out of the box) and
 * "overall" (Stripe's total counter + Layer's ledger; defaults to unlimited).
 * The old single field was Stripe's TOTAL — a "1" there killed a campaign
 * code after its first redemption by anyone.
 */
function couponFields(
  memberId: string,
  memberName: string,
  catalog: CouponCatalog,
): ReadonlyArray<ActionField> {
  return [
    ...COUPON_FIELDS_HEAD,
    {
      kind: "select",
      name: "member_id",
      label: "Who can use it",
      required: false,
      defaultValue: memberId,
      options: [
        { value: memberId, label: `Only ${memberName || "this member"}` },
        { value: "", label: "Anyone" },
      ],
    },
    {
      kind: "multiselect",
      name: "applies_to_slugs",
      label: "Applies to",
      required: true,
      defaultValue: JSON.stringify(
        [...catalog.packages, ...catalog.memberships]
          .map((o) => o.value)
          .sort(),
      ),
      groups: [
        { label: "Packages", options: catalog.packages },
        { label: "Memberships", options: catalog.memberships },
      ],
    },
    ...COUPON_FIELDS_TAIL,
  ];
}

const COUPON_FIELDS_HEAD: ReadonlyArray<ActionField> = [
  {
    kind: "number",
    name: "percent_off",
    label: "Percent off (1–100)",
    required: true,
    min: 1,
    max: 100,
    placeholder: "e.g. 20",
  },
  // Stripe's own cap on a coupon's name; the action refuses a longer one.
  {
    kind: "text",
    name: "name",
    label: `Name (optional, up to ${STRIPE_COUPON_NAME_MAX_LENGTH} characters)`,
    maxLength: STRIPE_COUPON_NAME_MAX_LENGTH,
  },
  {
    kind: "text",
    name: "code",
    label: "Custom code (optional — saved in UPPERCASE)",
    maxLength: 64,
    placeholder: "e.g. SUMMER25 — leave blank for a random code",
  },
];

const COUPON_FIELDS_TAIL: ReadonlyArray<ActionField> = [
  {
    kind: "number",
    name: "membership_months",
    label: "Membership months (blank = every month, forever)",
    min: 1,
    max: 36,
    placeholder: "e.g. 3",
  },
  {
    kind: "number",
    name: "max_redemptions_per_member",
    label: "Max uses per member (blank = unlimited)",
    min: 1,
    max: 1000,
    defaultValue: "1",
  },
  {
    kind: "number",
    name: "max_redemptions",
    label: "Max redemptions overall (blank = unlimited)",
    min: 1,
    max: 100_000,
  },
];

/**
 * Cross-field rules the schema cannot express. Months are meaningless when no
 * MEMBERSHIP is in scope (a one-off payment is discounted once by
 * construction), and silently ignoring them would let staff believe they had
 * set a limit that never applied. The custom-code shape is checked here so
 * staff get a sentence instead of a raw Ajv `input_invalid`.
 */
function makeCouponValidate(
  membershipSlugs: ReadonlySet<string>,
): (values: Readonly<Record<string, string | boolean>>) => string | null {
  return (values) => {
    const sel = values["applies_to_slugs"];
    const selected =
      typeof sel === "string" ? parseMultiSelectValue(sel) : new Set<string>();
    const coversAMembership = [...selected].some((s) => membershipSlugs.has(s));
    const months = values["membership_months"];
    if (
      !coversAMembership &&
      typeof months === "string" &&
      months.trim() !== ""
    ) {
      return "“Membership months” only applies to a code that covers memberships — leave it blank for a packages-only code.";
    }
    const code = values["code"];
    if (
      typeof code === "string" &&
      code.trim() !== "" &&
      !COUPON_CODE_PATTERN.test(code.trim())
    ) {
      return "Codes are 3–64 characters — letters, numbers, hyphens and underscores (saved in UPPERCASE).";
    }
    return null;
  };
}

/**
 * Success body for the coupon modal — the code is the thing staff need, so it
 * carries a copy button (the `PurchaseCreditsModal` payment-link clipboard
 * pattern: guarded `navigator.clipboard`, 1.5 s "Copied ✓" flash). On a
 * blocked clipboard nothing is stranded — the code is already on screen.
 */
function CouponCreated({ response }: { readonly response: unknown }): JSX.Element {
  const code =
    response !== null && typeof response === "object"
      ? (response as Record<string, unknown>)["code"]
      : undefined;
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyFlashRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copyFlashRef.current !== null)
        window.clearTimeout(copyFlashRef.current);
    },
    [],
  );
  const copyCode = (): void => {
    if (typeof code !== "string" || code.length === 0) return;
    const clip = navigator.clipboard;
    if (clip === undefined || typeof clip.writeText !== "function") {
      setCopyFailed(true);
      return;
    }
    void clip.writeText(code).then(
      () => {
        setCopyFailed(false);
        setCopied(true);
        if (copyFlashRef.current !== null)
          window.clearTimeout(copyFlashRef.current);
        copyFlashRef.current = window.setTimeout(() => {
          setCopied(false);
          copyFlashRef.current = null;
        }, 1500);
      },
      () => setCopyFailed(true),
    );
  };
  return (
    <>
      {typeof code === "string" && code.length > 0 ? (
        <p>
          Discount code: <strong>{code}</strong>{" "}
          <button
            type="button"
            className="row-action-btn"
            onClick={copyCode}
            title="Copy the code to the clipboard"
          >
            {copied ? "Copied ✓" : "Copy code"}
          </button>
          {copyFailed ? (
            <span className="action-modal-error"> Copy the code manually.</span>
          ) : null}
          <br />
          Members can quote this in chat or enter it on the booking site.
        </p>
      ) : null}
      <pre className="action-result-json">
        {JSON.stringify(response, null, 2)}
      </pre>
    </>
  );
}

/**
 * Success body for the `export_member_data` modal — turns the returned JSON tree
 * into a downloadable file (Blob URL) plus an inline preview.
 */
function ExportDownload({
  data,
  memberId,
}: {
  readonly data: unknown;
  readonly memberId: string;
}): JSX.Element {
  const json = JSON.stringify(data, null, 2);
  const href = `data:application/json;charset=utf-8,${encodeURIComponent(json)}`;
  return (
    <div>
      <p>Export ready.</p>
      <p>
        <a href={href} download={`member-${memberId.slice(0, 8)}-export.json`}>
          Download JSON
        </a>
      </p>
      <details className="collapsible">
        <summary>Preview</summary>
        <pre className="action-result-json">{json}</pre>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The `delete_member` (Art. 17 erasure) modal pieces.
// ---------------------------------------------------------------------------

/**
 * The optional audit rationale plus the type-DELETE gate — the same
 * confirmation `CONFIRM_DISPATCH_ACTIONS.delete_member` gives the `/status`
 * dispatch form, expressed as an `ActionModal` field + validate pair.
 */
const DELETE_MEMBER_FIELDS: ReadonlyArray<ActionField> = [
  {
    kind: "textarea",
    name: "reason",
    label: "Reason (optional)",
    maxLength: 500,
    placeholder: "e.g. erasure request received by email on …",
  },
  {
    kind: "text",
    name: "confirm",
    label: "Type DELETE to confirm",
    required: true,
    placeholder: "DELETE",
  },
];

function validateDeleteConfirm(
  values: Readonly<Record<string, string | boolean>>,
): string | null {
  const v = values["confirm"];
  return typeof v === "string" && v.trim() === "DELETE"
    ? null
    : "Type DELETE (in capitals) to confirm.";
}

/**
 * `delete_member`'s schema is `additionalProperties: false` — the UI-only
 * confirm field must not reach the wire.
 */
function stripDeleteConfirm(
  input: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const rest: Record<string, unknown> = { ...input };
  delete rest["confirm"];
  return rest;
}

/**
 * Success body for the `delete_member` modal — the erasure receipt. Reads the
 * action's `cleared_columns` counters defensively (the response is untyped
 * JSON) and says what was scrubbed vs what deliberately survives.
 */
function DeleteMemberDone({
  response,
}: {
  readonly response: unknown;
}): JSX.Element {
  const counters: Array<{ label: string; n: number }> = [];
  let sharedKinds: string[] = [];
  if (response !== null && typeof response === "object") {
    const cc = (response as Record<string, unknown>)["cleared_columns"];
    if (cc !== null && typeof cc === "object") {
      const sk = (cc as Record<string, unknown>)[
        "shared_identifier_kinds_skipped"
      ];
      if (Array.isArray(sk)) {
        sharedKinds = sk.filter((v): v is string => typeof v === "string");
      }
      const rows: ReadonlyArray<readonly [string, string]> = [
        ["payments_notes_cleared", "payment notes cleared"],
        ["payments_metadata_scrubbed", "custom package names cleared from payments"],
        ["conversations_cleared", "conversations scrubbed"],
        ["tasks_cleared", "tasks scrubbed"],
        ["channel_links_unlinked", "channel links removed"],
        ["channel_bindings_unlinked", "channel bindings removed"],
        ["session_notes_cleared", "session notes scrubbed"],
        ["session_feedback_cleared", "feedback comments cleared"],
        ["member_memory_cleared", "memory notes erased"],
        ["purchase_attempts_scrubbed", "purchase attempt details cleared"],
        ["purchase_attempts_unlinked_scrubbed", "unlinked checkout details cleared (matched by email)"],
        ["member_flows_exited", "marketing flows ended"],
        ["poll_participants_scrubbed", "poll invitations scrubbed"],
        ["event_participants_scrubbed", "event invitations scrubbed"],
        ["member_packages_metadata_scrubbed", "package notes and custom names cleared"],
        ["member_package_credit_revocations_scrubbed", "reasons and notes on credits taken off cleared"],
        ["member_membership_credit_revocations_scrubbed", "reasons and notes on membership credits taken off cleared"],
        ["inbound_messages_scrubbed", "received messages scrubbed"],
        ["outbound_messages_scrubbed", "sent messages scrubbed"],
        ["resend_events_scrubbed", "email delivery events scrubbed"],
        ["conversation_log_scrubbed", "chat-agent log entries scrubbed"],
        ["whatsapp_held_messages_deleted", "held WhatsApp messages deleted"],
        ["verification_sessions_deleted", "login sessions deleted"],
        ["blocked_email_senders_deleted", "email sender blocks removed"],
        ["member_sessions_deleted", "signed-in devices logged out"],
        ["whatsapp_newsletter_receipts_deleted", "WhatsApp newsletter receipts deleted"],
        ["email_newsletter_receipts_deleted", "email newsletter receipts deleted"],
        ["instagram_funnel_sends_scrubbed", "Instagram funnel sends anonymised"],
        ["picture_intakes_deleted", "consultation form photos deleted"],
        ["chat_runs_erased", "of their chat-agent conversations erased (queued for anonymisation)"],
        ["chat_traces_erased", "chat-agent turns blanked"],
        ["chat_scores_erased", "chat review notes blanked"],
      ];
      for (const [key, label] of rows) {
        const v = (cc as Record<string, unknown>)[key];
        if (typeof v === "number" && v > 0) counters.push({ label, n: v });
      }
    }
  }
  return (
    <div>
      <p>
        Member deleted. Their personal data is erased; bookings, payments and
        audit history remain, anonymised. Their comments on the studio&apos;s
        Instagram posts and their Instagram DM-thread record are kept, by
        decision.
      </p>
      <p>
        Other people&apos;s chat-agent conversations that mention them (a staff
        chat that looked them up, say) are blanked by the hourly job, normally
        within the hour; a long chat history, or several deletions at once,
        can take a few hours, and the status page shows an alarm if any is
        still waiting after 26 hours.
      </p>
      {counters.length > 0 ? (
        <ul>
          {counters.map((c) => (
            <li key={c.label}>
              {c.n} {c.label}
            </li>
          ))}
        </ul>
      ) : null}
      {sharedKinds.length > 0 ? (
        <p>
          Kept: the {sharedKinds.join(" and ")} message history — another
          member uses the same {sharedKinds.join(" / ")}, so that thread is
          their data too.
        </p>
      ) : null}
      <p>Closing this returns to the members list.</p>
    </div>
  );
}


/** Card width by content — big/nested fields break to their own row. */
function metaCardClass(k: string, v: unknown): string {
  if (k === "onboarding" || k === "consultation" || k === "consultation_extraction") {
    return "meta-card meta-card-full";
  }
  if (k === "feedback") {
    // Flattened to a plain scalar value — keep it small + even with the others.
    return "meta-card";
  }
  if (Array.isArray(v)) {
    return v.length > 3 ? "meta-card meta-card-full" : "meta-card meta-card-wide";
  }
  if (v !== null && typeof v === "object") {
    return Object.keys(v).length > 2
      ? "meta-card meta-card-full"
      : "meta-card meta-card-wide";
  }
  const s = scalarToString(v);
  return s.length > 80
    ? "meta-card meta-card-full"
    : s.length > 28
      ? "meta-card meta-card-wide"
      : "meta-card";
}

/** Join scalar items to a comma-separated string (drops blanks). */
/** Shorten the United-Kingdom country value to "UK" for the country card. */
function displayCountry(v: unknown): string {
  const s = scalarToString(v).trim();
  if (/^(united kingdom|great britain|england|gb|gbr|uk)$/i.test(s)) return "UK";
  return s;
}

function joinScalars(items: ReadonlyArray<unknown>): string {
  return items
    .filter(isScalar)
    .map((x) => scalarToString(x).trim())
    .filter((s) => s.length > 0)
    .join(", ");
}


/** Low-signal technical / ID fields — hidden behind the "More" card. */
const META_MORE_KEYS = new Set([
  "klaviyo_id",
  "codexfit_id",
  "import_source",
  "referral_code",
  "initial_source",
  "klaviyo_status",
]);

/** The big object cards — rendered last, each on its own full-width row. */
const META_BIG_KEYS = ["onboarding", "consultation", "consultation_extraction"];

/**
 * Split a member's loose metadata into the three things the profile card does
 * with it.
 *
 * `members.metadata` is free-form jsonb — the keys are whatever a vendor
 * import, a flow or a staff edit has put there, so there is no fixed schema to
 * lay out. Hence the split rather than a hand-written row per field:
 *
 *  - `scalar` — plain values (locale, occupation, date of birth …). One
 *    ordinary field row each, identical to "Member since", because that is what
 *    they are. Only keys that EXIST appear, which is inherent: an unset key
 *    isn't in the object.
 *  - `more`   — low-signal vendor ids, kept behind the "More" card so they
 *    don't outnumber the fields that matter.
 *  - `big`    — nested blobs (onboarding, consultation) that need a whole card.
 */
function partitionMetadata(metadata: Record<string, unknown>): {
  readonly scalar: ReadonlyArray<[string, unknown]>;
  readonly more: ReadonlyArray<[string, unknown]>;
  readonly big: ReadonlyArray<[string, unknown]>;
} {
  const entries = Object.entries(metadata);
  const main = entries.filter(
    ([k]) =>
      !META_MORE_KEYS.has(k) &&
      !META_BIG_KEYS.includes(k) &&
      // Rendered in its own bespoke "AI enrichment" panel, not as a raw card.
      k !== "ai_enrichment",
  );
  return {
    scalar: main.filter(([, v]) => isScalar(v) || Array.isArray(v)),
    more: entries.filter(([k]) => META_MORE_KEYS.has(k)),
    big: [
      ...main.filter(([, v]) => !isScalar(v) && !Array.isArray(v)),
      ...META_BIG_KEYS.map((bk) => entries.find(([k]) => k === bk)).filter(
        (e): e is [string, unknown] => e !== undefined,
      ),
    ],
  };
}

/**
 * Metadata paths whose derived `path · join` label reads worse than a plain
 * statement, keyed on the dotted path.
 *
 * `verification_already_complete` is the INPUT field's name — accurate at the
 * dispatch boundary, but as a row label it was a mouthful that wrapped onto a
 * second line and still said less than "Consent verified". The fact staff need
 * off that row is whether the member's identity check is done, not which flag
 * carried it. Keyed on the PATH, not the leaf key, so an unrelated
 * `verification_already_complete` elsewhere in metadata keeps its own label.
 * Both routes into the consent block — the `members.consent` column and the
 * `metadata.consent` blob, which is what most members actually carry — flatten
 * from the same `["consent"]` root, so one entry covers both.
 */
const META_ROW_LABELS: Readonly<Record<string, string>> = {
  "consent.verification_already_complete": "Consent verified",
};

/**
 * Flatten anything metadata can hold into `[label, text]` row pairs, walking
 * nested objects and joining the path with "·" (`consent.verification_already
 * _complete` → "Consent · Verification already complete").
 *
 * Replaces the nested mini-cards these keys used to render as. A card inside a
 * card, holding one labelled value, was heavier furniture than the fact it
 * carried — and it made an imported `klaviyo_id` look like a different KIND of
 * thing from a locale, when both are just a field this member happens to have.
 * Arrays join with ", "; an empty object is dropped rather than rendering a
 * label with nothing after it.
 */
function flattenMetaRows(
  value: unknown,
  path: ReadonlyArray<string> = [],
): ReadonlyArray<[string, string]> {
  const label =
    META_ROW_LABELS[path.join(".")] ??
    path.map((p) => prettyMetaKey(p)).join(" · ");
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const text = value.map((v) => scalarToString(v)).join(", ");
    return text.length > 0 ? [[label, text]] : [];
  }
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) =>
      flattenMetaRows(v, [...path, k]),
    );
  }
  const text = scalarToString(value);
  return text.length > 0 ? [[label, text]] : [];
}

/** The non-scalar metadata, as ordinary profile rows. */
function metadataDetailRows(
  metadata: Record<string, unknown>,
): ReadonlyArray<[string, string]> {
  const { more, big } = partitionMetadata(metadata);
  return [...more, ...big].flatMap(([k, v]) => flattenMetaRows(v, [k]));
}

/**
 * Member journey — one chronological timeline of everything that happened to
 * this member: registration, bookings, payments, every WhatsApp, email and
 * Instagram message in or out, the Stripe checkout dead ends, each flow they
 * entered and how it ended, and account changes (profile / memory / credit
 * edits). Each row reads `date · time` (left) · the action (middle) · the
 * connector/actor (grey, right) — on a message a flow sent, the flow's name.
 * Newest first, capped server-side at 300 events.
 */
/** Journey events rendered per page — the first screenful, and each further
 *  batch appended as the reader reaches the bottom. */
const JOURNEY_PAGE = 20;

/**
 * The Journey card's type filter — a burger button in the card header opening a
 * checkbox list of the event kinds in `JOURNEY_KINDS`.
 *
 * Was a row of toggle chips above the timeline. They cost a full line of the
 * card's fixed height before a single event was shown, and the card is one of a
 * matched pair whose height is budgeted (see `CARD_HEIGHT_MATCHES`) — so the
 * filter was spending scarce vertical space to display state that is almost
 * always "everything on". A header control spends none until it is opened,
 * which is the same trade the Conversation card's search icon makes two cards
 * down. (Counted "six" until 2026-09-12, when `checkout` made seven — hence the
 * reference to the array rather than a number that rots on the next kind.)
 */
function JourneyKindMenu({
  kinds,
  onToggleKind,
}: {
  readonly kinds: ReadonlyArray<JourneyKind>;
  readonly onToggleKind: (kind: JourneyKind) => void;
}): JSX.Element {
  const all = kinds.length === JOURNEY_KINDS.length;
  return (
    <PopMenu
      label={
        all
          ? "Filter journey by type"
          : `Filter journey by type — ${kinds.length} of ${JOURNEY_KINDS.length} shown`
      }
      triggerClassName={`journey-kind-btn${all ? "" : " journey-kind-btn-filtered"}`}
      trigger={
        <span className="journey-burger" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      }
    >
      {() => (
        <>
          {JOURNEY_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="menuitemcheckbox"
              aria-checked={kinds.includes(k)}
              className="journey-kind-row"
              // Stays open: picking types is a multi-select, and closing on the
              // first tick would make choosing three kinds three round-trips.
              onClick={() => onToggleKind(k)}
            >
              <span className="journey-kind-tick" aria-hidden="true">
                {kinds.includes(k) ? "✓" : ""}
              </span>
              {JOURNEY_KIND_LABEL[k]}
            </button>
          ))}
        </>
      )}
    </PopMenu>
  );
}

function Journey({
  events,
  kinds,
}: {
  readonly events: ReadonlyArray<MemberDetailJourneyEvent>;
  /** Event kinds currently shown (the staff-chosen Journey filter). */
  readonly kinds: ReadonlyArray<JourneyKind>;
}): JSX.Element {
  // Infinite scroll instead of a "Show all N" button: the card is a fixed
  // height with a scrolling body, so reaching the bottom is the natural way to
  // ask for more. The server caps the whole history at 300 events, so this
  // only ever pages through what is already in memory — there is no fetch.
  const [limit, setLimit] = useState<number>(JOURNEY_PAGE);
  const kindSet = new Set<string>(kinds);
  const filtered = events.filter((e) => kindSet.has(e.kind));
  const shown = filtered.slice(0, limit);
  // Changing the filter re-slices from the top; without this a reader who had
  // paged deep would land mid-list on a shorter filtered set.
  useEffect(() => {
    setLimit(JOURNEY_PAGE);
  }, [kinds]);
  const onScroll = (e: ReactUIEvent<HTMLOListElement>): void => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      setLimit((n) => (n >= filtered.length ? n : n + JOURNEY_PAGE));
    }
  };
  return (
    <>
      {/* No chip row and no per-kind colour: the filter moved to the header
          burger, and the event's own label already says what it is — a swatch
          repeating that in six hues was decoration competing with the text. */}
      {events.length === 0 ? (
        <p className="empty">No journey events yet.</p>
      ) : filtered.length === 0 ? (
        <p className="empty">No journey events match the selected types.</p>
      ) : (
        <>
          <ol className="journey" onScroll={onScroll}>
            {shown.map((e, i) => (
              <li
                key={`${e.ts}:${i}`}
                className={`journey-row journey-${e.kind}`}
              >
                <div className="journey-when">
                  <span className="journey-date">{fmtDate(e.ts)}</span>
                  <span className="journey-time">{fmtTime(e.ts)}</span>
                </div>
                <div className="journey-main">
                  <span className="journey-label">{e.label}</span>
                  {e.detail !== null && e.detail.trim().length > 0 ? (
                    <span className="journey-detail"> · {e.detail}</span>
                  ) : null}
                </div>
                <div className="journey-actor">{e.actor}</div>
              </li>
            ))}
          </ol>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Redesign building blocks (2026-06-25) — the two-column CRM layout.
// ---------------------------------------------------------------------------

/** Wrap each case-insensitive occurrence of `term` in `body` with a <mark>. */
/**
 * Conversation card — channels + the WhatsApp/email message log, with a search
 * box that filters the thread to messages containing the query (case-insensitive,
 * matched substring highlighted). Owns its own scroll: pinned to the newest
 * message when browsing, jumped to the first (oldest) match while searching, and
 * re-pinned whenever the card is resized (the height cap shifts as the journey
 * column / active-flows card change). Flex-fills the left column so its bottom
 * aligns with the right column's Journey card.
 */
function ConversationPanel({
  memberId,
  memberName,
  memberPhone,
  memberEmail,
  messages,
}: {
  readonly memberId: string;
  /** Threaded through only so the AI pill's confirmation can name the person. */
  readonly memberName: string | null;
  readonly memberPhone: string | null;
  readonly memberEmail: string | null;
  readonly messages: MemberDetailResponse["messages"];
}): JSX.Element {
  const [query, setQuery] = useState<string>("");
  // The search is collapsed to a magnifier icon by default; clicking it reveals
  // the input. It re-collapses on Escape or when blurred while empty.
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  /** Gmail message id of the email being viewed in full, or null when closed. */
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Scroll-preservation state for the 5s poll: only auto-scroll on member change
  // / first load / a genuinely new message while already at the bottom. A plain
  // refresh (or the reader having scrolled up) must NOT move the scroll position.
  const prevMemberRef = useRef<string>("");
  const prevMsgCountRef = useRef<number>(0);
  const atBottomRef = useRef<boolean>(true);
  const handleChatScroll = (): void => {
    const el = chatRef.current;
    if (el !== null) {
      atBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    }
  };

  // Live take-over state. The member-detail aggregate seeds the FIRST paint
  // (the `messages`/`conversations` props) so the panel never flashes empty;
  // the dedicated `GET /api/members/:id/conversation` endpoint then provides the
  // live thread + per-channel `agent_paused` + recipient phone, which we
  // re-fetch after every reply / pause toggle.
  const [live, setLive] = useState<MemberConversationResponse | null>(null);
  const refetch = useCallback(async (): Promise<void> => {
    if (memberId === "") return;
    try {
      setLive(await api.memberConversation(memberId));
    } catch {
      // Keep the current view on a transient error.
    }
  }, [memberId]);
  useEffect(() => {
    void refetch();
  }, [refetch]);

  // #14 (2026-07-15) — auto-refresh the thread every 5s so a new inbound (member
  // reply, AI reply, or a staff send from another surface) surfaces WITHOUT a
  // manual reload. Paused while the tab is hidden (no wasted polls / battery) and
  // an immediate catch-up refetch fires on re-focus. Cleans up on unmount /
  // member change. (Follow-up: an event-driven Supabase Realtime subscription
  // could replace the poll — see lib/realtime.tsx.)
  useEffect(() => {
    if (memberId === "") return;
    const POLL_MS = 5000;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = (): void => {
      if (timer === null) timer = setInterval(() => void refetch(), POLL_MS);
    };
    const stop = (): void => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = (): void => {
      if (document.hidden) {
        stop();
      } else {
        void refetch();
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [memberId, refetch]);

  const liveMessages: ReadonlyArray<MemberDetailMessage> =
    live?.messages ?? messages;
  // The per-channel AI pills (2026-08-06). The server computes the effective
  // state — the member's own switch combined with the studio's connector
  // setting — and omits channels the member is not reachable on. Empty until
  // the first live fetch lands, so the header simply has no pills for a beat
  // rather than flashing a wrong state.
  const channelAi = live?.channel_ai ?? [];
  // The three reply targets — live values from the conversation endpoint, each
  // falling back to the seeded member prop (email) / null (igsid; the endpoint
  // is the only source). The composer picks a channel from whichever are present.
  const replyPhone = live?.recipient_phone ?? memberPhone;
  const replyEmail = live?.recipient_email ?? memberEmail;
  const replyIgsid = live?.recipient_igsid ?? null;
  // Default email subject: "Re: <last inbound email subject>" when one exists,
  // else a generic studio default (the composer keeps it editable).
  const emailSubjectDefault = deriveEmailReplySubject(liveMessages);

  const term = query.trim().toLowerCase();
  const openSearch = (): void => {
    setSearchOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const closeIfEmpty = (): void => {
    if (query.trim() === "") setSearchOpen(false);
  };
  // Server returns newest-first; show oldest-first like a chat log.
  const ordered = liveMessages.slice().reverse();
  const shown =
    term.length === 0
      ? ordered
      : ordered.filter((msg) => (msg.body ?? "").toLowerCase().includes(term));

  // Auto-scroll rules that preserve the reader's position across the 5s poll:
  //  • searching → jump to the first (oldest) match (top)
  //  • member change / first load → pin to the newest
  //  • a NEW message arrives while already at the bottom → follow it
  //  • otherwise (plain refresh, or scrolled up to read history) → leave scroll be
  useEffect(() => {
    const el = chatRef.current;
    if (el === null) return;
    if (term.length > 0) {
      el.scrollTop = 0;
      return;
    }
    const memberChanged = prevMemberRef.current !== memberId;
    const grew = liveMessages.length > prevMsgCountRef.current;
    prevMemberRef.current = memberId;
    prevMsgCountRef.current = liveMessages.length;
    if (memberChanged || (grew && atBottomRef.current)) {
      el.scrollTop = el.scrollHeight;
      atBottomRef.current = true;
    }
  }, [term, liveMessages, memberId]);

  // Keep the log pinned to the bottom as the card is resized (the height cap
  // shifts with the journey column / active-flows card) — but only when the
  // reader is already at the bottom, so a resize never yanks them down.
  useEffect(() => {
    const el = chatRef.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (term.length === 0 && atBottomRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [term]);

  return (
    <ConversationView
      // Remount per member, deliberately. `ConversationView` preserves the
      // reader's scroll across refreshes by comparing the message count to the
      // previous render — which is right within one thread and wrong across
      // two: switching to a member with FEWER messages reads as "did not grow",
      // so the log would stay wherever the previous member's thread was rather
      // than pinning to newest. The inline panel this replaced tracked a
      // `prevMemberRef` to reset that; a `key` does the same job with React's
      // own semantics, and also clears a stale search term and any open email
      // modal, neither of which should follow you to a different person.
      key={memberId}
      messages={liveMessages}
      phone={replyPhone}
      email={replyEmail}
      igsid={replyIgsid}
      emailSubjectDefault={emailSubjectDefault}
      onSent={() => void refetch()}
      inboundLabel="Member"
      // 2026-08-06 — one pill per AI-capable channel the member is reachable
      // on (WhatsApp · Instagram · Email), replacing the square "AI" chip and
      // the Instagram-only toggle beside it. The server decides which pills
      // exist and what each one's effective state is; the pill only renders it
      // and dispatches the per-member decision.
      handoverSlot={
        <>
          {channelAi.map((state) => (
            <ChannelAiPill
              key={state.channel}
              memberId={memberId}
              memberLabel={memberName}
              state={state}
              onChanged={() => void refetch()}
            />
          ))}
        </>
      }
    />
  );
}

/**
 * One channel's AI pill — "WhatsApp AI: On" / "Instagram AI: Off" / "Email
 * AI: On" — in the Conversation card's header (2026-08-06; replaced the square
 * "AI" chip, which said only "AI" and governed only WhatsApp without saying so).
 *
 * The pill reports the EFFECTIVE answer: whether the assistant may answer THIS
 * member on THIS channel right now, combining their own switch with the
 * studio's connector setting. Clicking it writes an explicit per-member
 * decision — `set_conversation_handover({member_id, channel, paused})` — then
 * re-fetches, so the state always reports the server rather than an optimistic
 * guess.
 *
 * Precedence the tooltip has to explain, because it is not guessable:
 *   - a paused connector DISABLES the pill. An individual "on" is not possible
 *     while the whole channel is off; the connector always has the final say.
 *   - "On" against a studio setting of `notify` means this person is opted in
 *     individually — the assistant answers them and nobody else.
 *   - "Off" sticks. No studio setting re-promotes someone a human switched off.
 */
function ChannelAiPill({
  memberId,
  memberLabel,
  state,
  onChanged,
}: {
  readonly memberId: string;
  /** Who the pill governs, so the confirmation names them. */
  readonly memberLabel?: string | null;
  readonly state: MemberChannelAi;
  readonly onChanged: () => void;
}): JSX.Element {
  const dispatch = useDispatch<
    {
      readonly member_id: string;
      readonly paused: boolean;
      readonly channel: "whatsapp" | "instagram" | "email";
    },
    { readonly paused: boolean }
  >("set_conversation_handover");
  const toggle = async (): Promise<void> => {
    if (dispatch.loading || memberId === "" || !state.connector_enabled) return;
    // 2026-09-23 — confirm BOTH directions. An unconfirmed click on this pill
    // is what armed the email assistant for a member on 18 Sep and went
    // unnoticed until it answered her four days later. Cancelling writes
    // nothing: the guard is BEFORE the dispatch, not a compensation after it.
    if (
      !window.confirm(
        channelAiConfirmMessage({
          label: state.label,
          channel: state.channel,
          effectiveOn: state.effective_on,
          memberLabel,
        }),
      )
    ) {
      return;
    }
    const r = await dispatch.run({
      member_id: memberId,
      channel: state.channel,
      paused: state.effective_on,
    });
    if (r.ok) onChanged();
  };

  const title = ((): string => {
    if (!state.connector_enabled) {
      return `${state.label} is unavailable: the connector is switched off for the whole studio on the config page. Turn it back on there first — a connector that is off always has the final say.`;
    }
    if (state.effective_on) {
      const why =
        state.override === "on" && state.studio_mode === "notify"
          ? " This person is switched on individually — the assistant answers them and nobody else."
          : "";
      return `The assistant answers this person here.${why} Click to switch it off for them only; nothing changes for anyone else, and no studio setting will switch them back on.`;
    }
    const why =
      state.override === "off"
        ? " You switched it off for this person, so it stays off whatever the studio setting says."
        : MEMBER_AI_UNSET_DEFAULT_OFF_CHANNELS.has(state.channel)
          ? " Off by default on this channel — answering here is per-person opt-in, whatever the studio setting says. Staff are still alerted when they write."
          : ` The studio setting for this channel is "notify only", so the assistant answers nobody here by default.`;
    return `The assistant does not answer this person here.${why} Click to switch it on for them.`;
  })();

  const cls = !state.connector_enabled
    ? "ig-agent-toggle-off"
    : state.effective_on
      ? "ig-agent-toggle-on"
      : "ig-agent-toggle-off";

  return (
    <>
      <button
        type="button"
        className={`ig-agent-toggle ${cls}`}
        aria-pressed={state.effective_on}
        disabled={dispatch.loading || !state.connector_enabled}
        title={dispatch.error !== null ? dispatch.error.message : title}
        aria-label={title}
        onClick={() => void toggle()}
      >
        {state.label}:{" "}
        {dispatch.loading
          ? "…"
          : !state.connector_enabled
            ? "Off"
            : state.effective_on
              ? "On"
              : "Off"}
      </button>
      {/* 2026-09-24 — a REFUSED toggle has to say so ON the page.

          This was the only dispatch on the member page that surfaced its
          error through the button's `title` alone, and a tooltip is not a
          message: when the admin gate began refusing every arm, the whole
          visible result was a confirmation dialog followed by a pill that
          did not move. The refusal was sitting in `dispatch.error` the
          entire time. The page's own idiom for this is `flag-card-err`
          (used by the flag card, the inline field editor and the profile
          rows); the pill now uses it too, with `role="alert"` so a refusal
          is announced rather than merely drawn. */}
      {dispatch.error !== null ? (
        <span className="flag-card-err" role="alert">
          {dispatch.error.message}
        </span>
      ) : null}
    </>
  );
}

/** snake_case flow name → Title Case ("speed_to_lead_welcome" → "Speed To Lead Welcome"). */
function prettyFlowName(name: string): string {
  return name
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A settled money-in payment (charge or invoice, status active). */
function isSuccessfulPayment(p: MemberDetailPayment): boolean {
  return (
    p.status === "active" && (p.kind === "charge" || p.kind === "invoice")
  );
}

/** Metadata keys that get their own collapsible section (so they're excluded from "Profile details"). */
export const SECTIONED_META_KEYS = new Set([
  "onboarding",
  "consultation",
  "consultation_extraction",
  "staff_flag",
  // Rendered as the "Session feedback" toggle in the Profile details block, so
  // it must not ALSO appear as a raw metadata card.
  "session_feedback_opt_in",
  // Rendered as the "Gender" + "Post-session capture" controls in the Profile
  // details block (not as raw metadata cards).
  "gender",
  "send_post_session_message",
  // Rendered as the always-present "Source" row (see below): an ESSENTIAL
  // profile field must be visible and fillable even when the member has no
  // value, which the loose-scalar loop cannot do — it iterates the keys the
  // member actually carries, so an absent key renders nothing at all.
  "source",
  // C2 — rendered as the "Contact via" checkboxes, so they must not ALSO
  // appear as raw metadata cards (two controls for one fact, one of them
  // read-only, is how a stale value gets believed).
  "email_opt_in",
  "whatsapp_opt_in",
  // Rendered as the "Marketing" row (MarketingOptOutRow), the global switch
  // and every per-flow unsubscribe. As raw rows it was read-only, and staff had
  // no way to resubscribe a member (flow-loops-whatsapp, 2026-10-01).
  "flow_opt_outs",
  // Rendered as the "Instagram" row (MemberInstagramEditor writes exactly this
  // key), so listing it again at the bottom of the same block gave the profile
  // TWO Instagram rows — an editable "@epulsive" near the top and a read-only
  // "Instagram handle · epulsive" under Last updated. Same fact, two places,
  // and the read-only one is the copy that goes stale between renders.
  "instagram_handle",
  // The Codex importer stores the same address under TWO keys — `address` (the
  // display form) and `address_lines` (the raw Address 1–4 lines, kept
  // lossless; scripts/import-codex-epulsive/profiles.mjs) — so every imported
  // profile showed an "Address" row and an identical "Address Lines" row. The
  // "Address" row is the single surface for both: its editor writes the two
  // keys together (see MetadataFieldEditor) so the hidden copy can't go stale.
  // In prod the keys travel strictly together (208 members carry both, none
  // carries only one — verified 2026-08-29), so suppressing this one never
  // hides an address the "Address" row wouldn't show.
  "address_lines",
  // Rendered as always-present profile rows (see below), for the same reason
  // `source` is: these are fields staff must be able to FILL, and the
  // loose-scalar loop at the foot of the card cannot offer that — it iterates
  // the keys the member ALREADY carries, so an unset key renders nothing at
  // all and the field is unreachable from the dashboard precisely for the
  // members who are missing it. Of 2923 live members (verified 2026-09-23):
  // 811 carry `vest`, 805 `trouser`, 417 `dob`, 208 `address`, 30 `postcode`.
  //
  // `vest` and `trouser` are the sharpest case — both are
  // ESSENTIAL_MEMBER_PROFILE_FIELDS, so the T3 post-session prompt names them
  // to a trainer after every session with a member who lacks one, and until
  // now the profile page that trainer would open had no row to put the answer
  // in. They were the last two essential fields without a fixed row; the other
  // seven (display_name, email, phone, source, gender, CF_3_Goal,
  // CF_5_FocusArea) all already had one.
  //
  // `address` / `postcode` are written by the member API's
  // `update-account-details` from the booking site, and home-session travel
  // turns on the postcode — a field only the MEMBER could fill is the wrong
  // asymmetry. Listed here so the members who DO carry a value get the fixed
  // row and not a second copy from the loop.
  "address",
  "postcode",
  "dob",
  "vest",
  "trouser",
  // Rendered by the "Onboarding answers" section (the `"Onboarding - "` keys,
  // via PreferencesOnboardingBody) — before this it appeared here as one raw
  // JSON blob row while the purpose-built section sat empty, because that
  // section read only the legacy `onboarding` key no live member carries.
  "preferences",
]);

/** The metadata minus the keys rendered in their own sections. */
function generalMetadata(
  metadata: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(metadata).filter(([k]) => !SECTIONED_META_KEYS.has(k)),
  );
}

/** Read a nested plain-object metadata value, or null. */
function readMetaObject(
  metadata: Record<string, unknown>,
  key: string,
): Record<string, unknown> | null {
  const v = metadata[key];
  return v !== null && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

interface Segment {
  readonly key: string;
  readonly label: string;
  readonly count: string | number;
  readonly render: () => JSX.Element;
}

/**
 * A card with a row of number-buttons (Sessions: Upcoming/Past/Purchased,
 * Payments: Successful/Lifetime/Upcoming). Each button shows a headline figure;
 * clicking one expands its detail BELOW the row. Collapsed by default, one open
 * at a time, click-again closes — so the page stays compact until staff drill in.
 *
 * `openOnArrival` (payment-received-credits push 2, 2026-11-04) is the one
 * exception: a deep link that names something inside a segment opens that
 * segment, so what it names is on screen. The alert's `?add_credits=` link
 * opens Payments → Successful, whose row then opens its form.
 */
function SegmentedDetailCard({
  title,
  segments,
  openOnArrival = null,
}: {
  readonly title: string;
  readonly segments: ReadonlyArray<Segment>;
  readonly openOnArrival?: string | null;
}): JSX.Element {
  const [open, setOpen] = useState<string | null>(openOnArrival);
  // Also when the link arrives after the card is on screen (the SPA moving to
  // another member's alert link): a named segment opens; nothing closes one.
  useEffect(() => {
    if (openOnArrival !== null) setOpen(openOnArrival);
  }, [openOnArrival]);
  const active = segments.find((s) => s.key === open) ?? null;
  return (
    <section className="panel seg-card">
      <h2>{title}</h2>
      <div className="seg-buttons">
        {segments.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`seg-btn${open === s.key ? " seg-btn-active" : ""}`}
            onClick={() => setOpen(open === s.key ? null : s.key)}
          >
            <span className="seg-btn-num">{s.count}</span>
            <span className="seg-btn-label">{s.label}</span>
          </button>
        ))}
      </div>
      {active !== null ? <div className="seg-detail">{active.render()}</div> : null}
    </section>
  );
}

/**
 * Inline editor for ONE free-form `members.metadata` key — the locale /
 * occupation / date-of-birth rows.
 *
 * Same Edit affordance as the fixed fields above it, over `update_member`'s
 * shallow metadata merge, so writing one key can't disturb the others. An
 * emptied value is stored as `null` rather than deleted: the merge has no
 * delete, and a key left in place keeps its row on screen to be re-filled.
 * A value that arrived as an ARRAY (`klaviyo_tags`) goes back as one — editing
 * it as comma-separated text and saving a bare string would silently change
 * the field's type under whatever else reads it.
 */
/**
 * Consultation-field row — an inline editor for ONE key inside the nested
 * `metadata.consultation` object.
 *
 * WHY IT IS NOT `MetadataFieldEditor`: `update_member` merges `metadata` at the
 * TOP level only, so sending `{consultation: {CF_8_Injuries: "Yes"}}` REPLACES
 * the whole consultation object and deletes the goal and focus areas beside it
 * (proved against real Postgres in
 * `packages/canonical-actions/tests/actions/update-member-nested-metadata-assumptions.integration.test.ts`).
 * This editor therefore sends the COMPLETE object back with only its own key
 * changed — the same read-before-you-replace discipline the chat agent is now
 * told to follow.
 *
 * Editable, not read-only, on purpose: the whole point of surfacing these is
 * that staff can correct what the OCR or the agent got wrong.
 */
function ConsultationFieldEditor({
  memberId,
  consultation,
  metaKey,
  onSaved,
}: {
  readonly memberId: string;
  /** The member's CURRENT complete `metadata.consultation` object. */
  readonly consultation: Record<string, unknown>;
  readonly metaKey: string;
  readonly onSaved: () => void;
}): JSX.Element {
  const raw = scalarToString(consultation[metaKey]);
  const shown = raw === "—" ? "" : raw;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(shown);
  const dispatch = useDispatch<
    { readonly member_id: string; readonly patch: Record<string, unknown> },
    Record<string, unknown>
  >("update_member");

  const save = async (): Promise<void> => {
    if (dispatch.loading || memberId === "") return;
    const trimmed = draft.trim();
    // FULL object, one key changed. A blank CLEARS the key rather than storing
    // an empty string, so the profile-gap checker still reads it as absent.
    const next: Record<string, unknown> = { ...consultation };
    if (trimmed === "") delete next[metaKey];
    else next[metaKey] = trimmed;
    const r = await dispatch.run({
      member_id: memberId,
      patch: { metadata: { consultation: next } },
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  return (
    <ProfileEditRow
      editing={editing}
      onEdit={() => {
        setDraft(shown);
        dispatch.reset();
        setEditing(true);
      }}
      display={shown.length > 0 ? shown : "—"}
      editor={
        <input
          className="profile-field-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") {
              dispatch.reset();
              setEditing(false);
            }
          }}
        />
      }
      onSave={() => void save()}
      onCancel={() => {
        dispatch.reset();
        setEditing(false);
      }}
      loading={dispatch.loading}
      error={dispatch.error?.message ?? null}
    />
  );
}

/**
 * The consultation keys to render on the Profile details card, in a fixed,
 * meaningful order rather than jsonb key order — goal, then focus areas, then
 * injuries. Keys the member does not carry are still rendered (as "—") when
 * they are part of the ESSENTIAL profile set, because an EMPTY essential field
 * is the thing staff need to see and fill; the optional extras appear only when
 * the member actually has them.
 */
export const CONSULTATION_ROW_ORDER: ReadonlyArray<{
  readonly key: string;
  readonly alwaysShow: boolean;
}> = [
  { key: "CF_3_Goal", alwaysShow: true },
  { key: "CF_3_Goal_1", alwaysShow: false },
  { key: "CF_3_Goal_2", alwaysShow: false },
  { key: "CF_3_Goal_3", alwaysShow: false },
  { key: "CF_5_FocusArea", alwaysShow: true },
  { key: "CF_5_FocusArea_1", alwaysShow: false },
  { key: "CF_5_FocusArea_2", alwaysShow: false },
  { key: "CF_8_Injuries", alwaysShow: true },
  { key: "CF_8_Injuries_Text", alwaysShow: true },
  { key: "CF_11_TrainingPlan_WeeklySessions", alwaysShow: false },
  { key: "CF_11_TrainingPlan_SpecificGoal", alwaysShow: false },
];

/** Which consultation rows to render for this member, in display order. */
function consultationRowKeys(
  consultation: Record<string, unknown>,
): ReadonlyArray<string> {
  const ordered = CONSULTATION_ROW_ORDER.filter(
    (r) => r.alwaysShow || isPresentConsultationValue(consultation[r.key]),
  ).map((r) => r.key);
  // Any CF_* key the studio's form grows that this list doesn't know about
  // still renders — an unlisted field is a field nobody can see.
  const known = new Set(CONSULTATION_ROW_ORDER.map((r) => r.key));
  const extras = Object.keys(consultation).filter((k) => !known.has(k));
  return [...ordered, ...extras];
}

function isPresentConsultationValue(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
}

function MetadataFieldEditor({
  memberId,
  metaKey,
  value,
  onSaved,
}: {
  readonly memberId: string;
  readonly metaKey: string;
  readonly value: unknown;
  readonly onSaved: () => void;
}): JSX.Element {
  const wasArray = Array.isArray(value);
  const shown = wasArray
    ? joinScalars(value)
    : metaKey === "country"
      ? displayCountry(value)
      : scalarToString(value);
  // `scalarToString` renders an absent value as the em-dash placeholder "—".
  // Right for DISPLAY, but it must never seed the EDITOR: the always-present
  // Source row (2026-09-03) is the first MetadataFieldEditor to receive an
  // undefined value, so the input would open pre-filled with "—", and a staff
  // member who saved without clearing would write the literal placeholder.
  // `cleanProfileFieldValue` counts "—" as a real answer (neither blank nor
  // the `skipped` sentinel), so that save would silently close the profile gap
  // with garbage and stop the post-session prompt on a non-answer.
  const editSeed = value === null || value === undefined ? "" : shown;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(editSeed);
  const dispatch = useDispatch<
    { readonly member_id: string; readonly patch: Record<string, unknown> },
    Record<string, unknown>
  >("update_member");

  const save = async (): Promise<void> => {
    if (dispatch.loading || memberId === "") return;
    const trimmed = draft.trim();
    const next: unknown =
      trimmed === ""
        ? null
        : wasArray
          ? trimmed
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
          : trimmed;
    // `address` has a hidden twin: the importer's lossless `address_lines`
    // array (suppressed from the card via SECTIONED_META_KEYS). This row is
    // the only surface for both, so a save must carry the twin along — an
    // address edit that left the array behind would recreate the exact
    // stale-copy hazard the instagram_handle entry documents.
    const patch: Record<string, unknown> =
      metaKey === "address"
        ? {
            address: next,
            address_lines:
              typeof next === "string"
                ? next
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0)
                : null,
          }
        : { [metaKey]: next };
    const r = await dispatch.run({
      member_id: memberId,
      patch: { metadata: patch },
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  return (
    <ProfileEditRow
      editing={editing}
      onEdit={() => {
        setDraft(editSeed);
        dispatch.reset();
        setEditing(true);
      }}
      display={shown.length > 0 ? shown : "—"}
      editor={
        <input
          className="profile-field-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") {
              dispatch.reset();
              setEditing(false);
            }
          }}
        />
      }
      onSave={() => void save()}
      onCancel={() => {
        dispatch.reset();
        setEditing(false);
      }}
      loading={dispatch.loading}
      error={dispatch.error?.message ?? null}
    />
  );
}

/**
 * The flow picker — the rows of the "+" dropdown on the Active flows card,
 * listing every STAFF-ENROLLABLE flow the member is NOT already in (from the
 * dashboard catalog, `GET /api/catalog`). Clicking one enrols them.
 *
 * Filters on `staff_enrollable`, NOT on `enabled`. The two are orthogonal:
 * `enabled` is the `/flows` AUTOMATIC-enrolment toggle, so filtering on it hid
 * exactly the flows a human most wants to fire by hand (the win-back and
 * expiry nudges ship toggled OFF on purpose, so nothing mass-enrols on
 * deploy). `start_member_flow` admits a manual enrolment into a toggled-off
 * flow when it is `staff_enrollable`, so every row shown here works.
 *
 * Names only: this is a menu, so the trigger condition each flow used to show
 * beside its name belongs on `/flows`, not in a picker whose whole job is
 * "which one".
 */
function FlowPicker({
  enrolled,
  onPick,
}: {
  readonly enrolled: ReadonlySet<string>;
  readonly onPick: (flowName: string) => void;
}): JSX.Element {
  const { data: catalog, error } = useCachedResource("catalog", () =>
    api.catalog(),
  );
  if (error !== null) {
    return <p className="flow-picker-empty">Couldn&apos;t load flows.</p>;
  }
  if (catalog === undefined) {
    return <p className="flow-picker-empty">Loading flows…</p>;
  }
  const available = catalog.marketing_flows.filter(
    (f) => f.staff_enrollable && !enrolled.has(f.name),
  );
  if (available.length === 0) {
    return <p className="flow-picker-empty">No more flows to add.</p>;
  }
  return (
    <>
      {available.map((f) => (
        <button key={f.name} type="button" onClick={() => onPick(f.name)}>
          {prettyFlowName(f.name)}
        </button>
      ))}
    </>
  );
}

/**
 * Active flows panel — the member's live flow enrolments (status active), with a
 * count badge, a "+" to manually enrol them in another flow (`start_member_flow`)
 * and an "×" per card to remove them (`exit_member_flow`, reason
 * `staff_terminated`). Both dispatch through the chokepoint; on success the page
 * silently refreshes.
 */
function ActiveFlowsPanel({
  memberId,
  flows,
  onChanged,
}: {
  readonly memberId: string;
  readonly flows: ReadonlyArray<MemberDetailFlow>;
  readonly onChanged: () => void;
}): JSX.Element {
  const startFlow = useDispatch<
    { readonly member_id: string; readonly flow_name: string },
    { readonly flow_id: string; readonly existed_before: boolean }
  >("start_member_flow");
  const exitFlow = useDispatch<
    {
      readonly member_id: string;
      readonly flow_name: string;
      readonly reason: string;
    },
    {
      readonly flow_id: string | null;
      readonly existed_before: boolean;
      readonly exited: boolean;
    }
  >("exit_member_flow");

  const activeFlows = flows.filter((f) => f.status === "active");
  const enrolled = new Set(activeFlows.map((f) => f.flow_name));
  const busy = startFlow.loading || exitFlow.loading;
  const err = startFlow.error ?? exitFlow.error;

  const onAdd = async (flowName: string): Promise<void> => {
    const r = await startFlow.run({ member_id: memberId, flow_name: flowName });
    if (r.ok) onChanged();
  };
  const onRemove = async (flowName: string): Promise<void> => {
    const r = await exitFlow.run({
      member_id: memberId,
      flow_name: flowName,
      reason: "staff_terminated",
    });
    if (r.ok) onChanged();
  };

  return (
    <section className="panel">
      <div className="active-flows-head">
        <h2>Active flows</h2>
        <PopMenu
          label="Add this member to a flow"
          triggerClassName="flow-add-btn"
          trigger="+"
          disabled={busy}
        >
          {(close) => (
            <FlowPicker
              enrolled={enrolled}
              onPick={(n) => {
                close();
                void onAdd(n);
              }}
            />
          )}
        </PopMenu>
      </div>
      {err !== null ? <p className="cf-error">{err.message}</p> : null}
      {/* No "not enrolled" line — an empty card already says it. */}
      {activeFlows.length === 0 ? null : (
        <div className="active-flow-list">
          {activeFlows.map((f) => (
            <div key={f.id} className="active-flow-card">
              <div className="active-flow-main">
                <div className="active-flow-name">
                  {prettyFlowName(f.flow_name)}
                </div>
                <div className="active-flow-meta">
                  Step {f.current_step_index + 1} · since {fmtDate(f.started_at)}
                  {f.last_step_at !== null
                    ? ` · last ${fmtDate(f.last_step_at)}`
                    : ""}
                </div>
              </div>
              <button
                type="button"
                className="flow-remove-btn"
                title="Remove from this flow"
                disabled={busy}
                onClick={() => void onRemove(f.flow_name)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Session notes — the Active-flows card's twin: same panel, same title bar,
 * same "+" affordance, same stacked row cards.
 *
 * It was a collapsed `<details>` wrapping a 4-column table, which put the
 * newest note behind two clicks and made the card a different species from the
 * one directly above it. A note is a short piece of prose with a date and an
 * author — the same shape as a flow enrolment — so it gets the same row.
 */
function SessionNotesPanel({
  memberId,
  memberName,
  notes,
  onChanged,
}: {
  readonly memberId: string;
  readonly memberName: string;
  readonly notes: ReadonlyArray<MemberDetailSessionNote>;
  readonly onChanged: () => void;
}): JSX.Element {
  /** The note currently open in the inline editor, if any. */
  const [editingId, setEditingId] = useState<string | null>(null);
  /** The note a dispatch is in flight for — locks every control while set. */
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Opening the editor makes this card size to its content instead of holding
  // its half-band (see `.session-note-editor` in styles.css), so the card's own
  // height changes without anything above the grid moving. Nothing the height
  // hook watches would notice — least of all on CLOSE, when the right column is
  // left the taller one — so say it explicitly.
  useEffect(() => {
    announceCardContentResized();
  }, [editingId]);

  const save = async (noteId: string, body: string): Promise<void> => {
    if (busyId !== null) return;
    setBusyId(noteId);
    setErr(null);
    try {
      const r = await dispatchAction<
        { session_note_id: string; body: string },
        { member_id: string; updated_at: string }
      >("edit_session_note", { session_note_id: noteId, body });
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      setEditingId(null);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "edit failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (noteId: string, hasFlags: boolean): Promise<void> => {
    if (busyId !== null) return;
    // `delete_session_note` is a HARD delete with no undo, so it asks once. A
    // flagged note gets a sharper prompt: deleting it also removes a safety
    // flag from the banner every trainer reads before a session.
    if (!window.confirm(sessionNoteDeleteConfirmMessage(hasFlags))) return;
    setBusyId(noteId);
    setErr(null);
    try {
      const r = await dispatchAction<
        { session_note_id: string },
        { deleted: boolean; member_id: string }
      >("delete_session_note", { session_note_id: noteId });
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="panel">
      <div className="active-flows-head">
        <h2>Session notes</h2>
        <ActionModalButton
          action="record_session_notes"
          buttonLabel="+"
          buttonClassName="flow-add-btn"
          buttonTitle="Add a session note"
          modalTitle={`Add session note — ${memberName}`}
          description="Record a note about this member. Visible here and on the trainer's page."
          submitLabel="Save note"
          prefill={{ member_id: memberId }}
          fields={[
            {
              kind: "textarea",
              name: "body",
              label: "Note",
              required: true,
              maxLength: 4000,
              placeholder: "What happened in the session…",
            },
          ]}
          onDone={() => onChanged()}
        />
      </div>
      {err !== null ? <p className="cf-error">{err}</p> : null}
      {/* No "none recorded" line — an empty card already says it, same as
          Active flows above. */}
      {notes.length === 0 ? null : (
        <div className="active-flow-list">
          {notes.map((n) => {
            const flags = extractFlags(n.structured);
            return (
              <div
                key={n.id}
                className={`active-flow-card${flags.length > 0 ? " note-flagged" : ""}`}
              >
                <div className="active-flow-main">
                  {editingId === n.id ? (
                    <SessionNoteEditor
                      initialBody={n.body}
                      busy={busyId === n.id}
                      onCancel={() => {
                        setEditingId(null);
                        setErr(null);
                      }}
                      onSave={(body) => void save(n.id, body)}
                    />
                  ) : (
                    <div className="session-note-body">{n.body}</div>
                  )}
                  <div className="active-flow-meta">
                    {fmtDateTime(n.created_at)}
                    {n.trainer_id !== null ? (
                      <>
                        {" · "}
                        <Link
                          to={`/trainers/${encodeURIComponent(n.trainer_id)}`}
                          className="data-member-link"
                        >
                          {n.trainer_name ?? n.trainer_id.slice(0, 8)}
                        </Link>
                      </>
                    ) : null}
                  </div>
                  {flags.length > 0 ? (
                    <div className="session-note-flags">
                      {flags.map((f, i) => (
                        <span key={i} className="session-note-flag">
                          <Tag value={f.type} /> {f.summary}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                {/* Hidden while that note is open for editing — the editor
                    carries its own Save / Cancel, and leaving these up would
                    offer two competing ways out of the same state. */}
                {editingId === n.id ? null : (
                  <div className="session-note-actions">
                    <button
                      type="button"
                      className="flow-remove-btn"
                      title="Edit this note"
                      disabled={busyId !== null}
                      onClick={() => {
                        setErr(null);
                        setEditingId(n.id);
                      }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="flow-remove-btn"
                      title="Delete this note"
                      disabled={busyId !== null}
                      onClick={() => void remove(n.id, flags.length > 0)}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}



/**
 * payment-received-credits push 2 (2026-11-04) — a payment's short id with a
 * copy button. The Link payment form tells staff to copy a payment's id from
 * this panel, and the "no credits added" alert links to the payment by it.
 * `CouponCreated`'s guarded clipboard and 1.5 s "Copied ✓" flash; the full id
 * is the cell's title and shows on a blocked clipboard, so nothing is stranded.
 */
function PaymentIdCell({ id }: { readonly id: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const flashRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (flashRef.current !== null) window.clearTimeout(flashRef.current);
    },
    [],
  );
  const copy = (): void => {
    const clip = navigator.clipboard;
    if (clip === undefined || typeof clip.writeText !== "function") {
      setCopyFailed(true);
      return;
    }
    void clip.writeText(id).then(
      () => {
        setCopyFailed(false);
        setCopied(true);
        if (flashRef.current !== null) window.clearTimeout(flashRef.current);
        flashRef.current = window.setTimeout(() => {
          setCopied(false);
          flashRef.current = null;
        }, 1500);
      },
      () => setCopyFailed(true),
    );
  };
  return (
    <span title={id}>
      <code>{id.slice(0, 8)}…</code>{" "}
      <button
        type="button"
        className="row-action-btn"
        onClick={copy}
        title="Copy the payment's full id"
      >
        {copied ? "Copied ✓" : "Copy id"}
      </button>
      {copyFailed ? <span className="cal-action-error"> {id}</span> : null}
    </span>
  );
}

/**
 * Why a payment the "no credits added" alert links to (`?add_credits=<id>`)
 * takes no credits now; null when it does. The page's own words for what
 * `add_payment_credits` would refuse, read off the same row flags.
 */
function addCreditsBlockedReason(
  p: MemberDetailPayment | undefined,
  refundedMinor: number,
): string | null {
  if (p === undefined) {
    return "That payment isn't on this member's page: it may be linked to another member, or to nobody yet. Find it on /data/payments and link it first.";
  }
  if (p.creditable) return null;
  if (p.credits_added) return "This payment's credits are on now — nothing to add.";
  if (refundedMinor > 0) return "This payment was refunded, so no credits go on it.";
  return "Clavis doesn't add credits to this payment: it was sold at the desk, sold through Clavis as an amount with no pack, or recorded before 4 Nov 2026.";
}

/**
 * payment-received-credits push 2 (2026-11-04) — "Add credits" on a payment
 * that added none (`add_payment_credits`). A payment that named no pack takes
 * the pack staff choose; one whose pack never went on is restarted as sold.
 * The host owns `open` only through `autoOpen`, so the alert's link can open
 * the form on arrival; `onClosed` lets it drop the link from the address.
 */
function AddCreditsAction({
  payment,
  memberId,
  autoOpen,
  onClosed,
  onDone,
}: {
  readonly payment: MemberDetailPayment;
  readonly memberId: string;
  readonly autoOpen: boolean;
  readonly onClosed?: () => void;
  readonly onDone: () => void;
}): JSX.Element | null {
  const [open, setOpen] = useState(autoOpen);
  const [packs, setPacks] = useState<ReadonlyArray<{ readonly value: string; readonly label: string }> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    if (!open || packs !== null) return;
    let cancelled = false;
    api.lookupOptions("packages").then(
      (r) => {
        if (!cancelled) setPacks(r.options);
      },
      (e: unknown) => {
        if (!cancelled) setLoadError((e as Error).message);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, packs]);
  if (payment.credit_kind === null) return null;
  const restart = payment.credit_kind === "restart";
  const packName = (slug: string | null): string =>
    packs?.find((o) => o.value === slug)?.label ?? slug ?? "its pack";
  const close = (): void => {
    setOpen(false);
    onClosed?.();
  };
  const paid = `${fmtMoney(payment.amount_minor, payment.currency)} payment on ${fmtDateTime(payment.occurred_at)}`;
  const twinWarning = payment.twin_sale
    ? " ⚠ This member has a desk sale for the same amount within a week of it: most likely this same purchase, recorded twice. Its credits are on the desk sale already, so tick the box only if it really was a second purchase."
    : "";
  const fields: ActionField[] = [
    ...(restart
      ? []
      : ([
          {
            kind: "select",
            name: "package_slug",
            label: "Pack it paid for",
            required: true,
            options: packs ?? [],
          },
          {
            kind: "number",
            name: "sessions_override",
            label: "Custom number of credits (optional)",
            min: 1,
            max: 200,
            step: 1,
          },
          {
            kind: "number",
            name: "validity_days",
            label: "Valid for (days) — with a custom number only",
            min: 1,
            max: 3650,
            step: 1,
          },
        ] satisfies ActionField[])),
    ...(payment.twin_sale
      ? ([
          {
            kind: "checkbox",
            name: "allow_twin_sale",
            label: "It was a second purchase, not the desk sale for the same amount",
          },
        ] satisfies ActionField[])
      : []),
    { kind: "text", name: "note", label: "Note (optional)", maxLength: 500 },
  ];
  return (
    <>
      <button
        type="button"
        className="row-action-btn"
        onClick={() => setOpen(true)}
        title={restart ? "Restart the pack this payment paid for" : "Add the pack this payment paid for"}
      >
        {restart ? "Restart pack" : "Add credits"}
      </button>
      {open && loadError !== null ? (
        <span className="cal-action-error"> Couldn&apos;t load the packs: {loadError}</span>
      ) : null}
      {open && packs !== null ? (
        <ActionModal
          action="add_payment_credits"
          modalTitle={restart ? `Restart "${packName(payment.credit_package_slug)}"` : "Add credits"}
          description={
            restart
              ? `This ${paid} paid for "${packName(payment.credit_package_slug)}", but the pack never went on. Restarting adds it as it was sold, and the member gets the usual purchase receipt.${twinWarning}`
              : `This ${paid} named no pack, so no credits went on. Choose the pack it paid for; the member gets the usual purchase receipt.${twinWarning}`
          }
          prefill={{ payment_id: payment.id, member_id: memberId }}
          fields={fields}
          validate={(v) => {
            if (!restart && v["validity_days"] !== "" && v["sessions_override"] === "") {
              return "A validity goes with a custom number of credits: enter both, or neither.";
            }
            if (payment.twin_sale && v["allow_twin_sale"] !== true) {
              return "Tick the box only if this really was a second purchase; otherwise its credits are on the desk sale already.";
            }
            return null;
          }}
          transformInput={(input) => {
            // The box is only ever sent ticked.
            const { allow_twin_sale: twin, ...rest } = input;
            return twin === true ? { ...rest, allow_twin_sale: true } : rest;
          }}
          submitLabel={restart ? "Restart the pack" : "Add credits"}
          renderSuccess={(response) => {
            const out = (response ?? {}) as { already_credited?: unknown };
            return out.already_credited === true ? (
              <p>This payment&apos;s credits were on already — nothing was added.</p>
            ) : (
              <p>
                Done — the pack appears on this page within a few seconds, and the
                member gets their purchase receipt.
              </p>
            );
          }}
          onDone={() => onDone()}
          onClose={close}
        />
      ) : null}
    </>
  );
}

/** The full payment history table (date / type / amount / method / status / note). */
function PaymentsTable({
  rows,
  emptyText,
  onChanged,
  refundedByCharge,
  memberId,
  addCreditsFor,
  onAddCreditsClosed,
}: {
  readonly rows: ReadonlyArray<MemberDetailPayment>;
  readonly emptyText: string;
  /** When provided, refundable charges show a Refund action and refetch on success. */
  readonly onChanged?: () => void;
  /** charge payment id → minor units already refunded against it. */
  readonly refundedByCharge?: ReadonlyMap<string, number>;
  /** The member, for "Add credits" on a payment that added none. */
  readonly memberId?: string;
  /** The payment the alert's `?add_credits=` link names: its form opens on arrival. */
  readonly addCreditsFor?: string | null;
  readonly onAddCreditsClosed?: () => void;
}): JSX.Element {
  if (rows.length === 0) return <p className="empty">{emptyText}</p>;
  const showActions = onChanged !== undefined;
  return (
    <div className="table-scroll-x">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Method</th>
            <th>Status</th>
            <th>Note</th>
            <th>ID</th>
            {showActions ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <td data-label="Date">{fmtDateTime(p.occurred_at)}</td>
              <td data-label="Type">
                <Tag value={p.kind} />
              </td>
              <td data-label="Amount">
                {p.kind === "refund" ? "−" : ""}
                {fmtMoney(p.amount_minor, p.currency)}
              </td>
              <td data-label="Method">{p.payment_method ?? "—"}</td>
              <td data-label="Status">{p.status}</td>
              <td data-label="Note">{p.note ?? "—"}</td>
              <td data-label="ID">
                <PaymentIdCell id={p.id} />
              </td>
              {showActions ? (
                <td className="data-td-actions">
                  {p.creditable && memberId !== undefined ? (
                    <AddCreditsAction
                      payment={p}
                      memberId={memberId}
                      autoOpen={addCreditsFor === p.id}
                      {...(onAddCreditsClosed !== undefined ? { onClosed: onAddCreditsClosed } : {})}
                      onDone={() => onChanged?.()}
                    />
                  ) : null}
                  {(() => {
                    const alreadyRefunded = refundedByCharge?.get(p.id) ?? 0;
                    const remaining = p.amount_minor - alreadyRefunded;
                    const refundable =
                      isSuccessfulPayment(p) &&
                      p.kind === "charge" &&
                      remaining > 0;
                    if (!refundable) {
                      // Fully refunded → show a quiet marker instead of the button.
                      return alreadyRefunded > 0 && p.kind === "charge" ? (
                        <span className="row-refunded-tag">refunded</span>
                      ) : null;
                    }
                    const partial = alreadyRefunded > 0;
                    return (
                      <ActionModalButton
                        action="refund_payment"
                        buttonLabel="Refund"
                        buttonClassName="row-action-btn row-action-btn-danger"
                        modalTitle="Refund payment"
                        description={`Refund up to ${fmtMoney(remaining, p.currency)}${partial ? ` remaining (of ${fmtMoney(p.amount_minor, p.currency)})` : ""} charged on ${fmtDateTime(p.occurred_at)}. Leave amount blank for a full refund of the remaining balance.`}
                        submitLabel="Issue refund"
                        prefill={{ payment_id: p.id }}
                        fields={[
                          {
                            kind: "number",
                            name: "amount_minor",
                            label: "Partial amount (£) — blank = full",
                            min: 0.01,
                            step: 0.01,
                            minorUnits: true,
                            placeholder: fmtMoney(remaining, p.currency),
                          },
                          {
                            kind: "select",
                            name: "reason",
                            label: "Reason",
                            defaultValue: "requested_by_customer",
                            options: [
                              { value: "requested_by_customer", label: "Requested by customer" },
                              { value: "duplicate", label: "Duplicate" },
                              { value: "fraudulent", label: "Fraudulent" },
                            ],
                          },
                          { kind: "text", name: "note", label: "Note (optional)", maxLength: 500 },
                        ]}
                        onDone={() => onChanged?.()}
                      />
                    );
                  })()}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The upcoming-charges table — next renewal per active membership. */
function UpcomingPaymentsTable({
  rows,
  currency,
}: {
  readonly rows: ReadonlyArray<MemberDetailUpcomingPayment>;
  readonly currency: string | null;
}): JSX.Element {
  if (rows.length === 0) return <p className="empty">No upcoming charges.</p>;
  return (
    <div>
      <p className="member-note">
        Expected = charges left × per-cycle amount. Standard plans price from
        the catalog; custom plans from the live Stripe subscription (or the
        amount stored at the sale). A fixed-term plan is charged until its end
        date and then stops — the date itself is never a charge.
      </p>
      <div className="table-scroll-x">
        <table>
          <thead>
            <tr>
              <th>Next charge</th>
              <th>Plan</th>
              <th>Amount</th>
              <th title="Charges still to come in the committed term, the next one included.">
                Charges left
              </th>
              <th title="A fixed-term plan ends on this date; blank for an ongoing plan.">
                Ends
              </th>
              <th>Sessions left</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.membership_id}>
                <td data-label="Next charge">{fmtDate(p.current_period_end)}</td>
                <td data-label="Plan">
                  <code>{p.membership_slug}</code>
                </td>
                <td data-label="Amount">
                  {p.per_cycle_minor !== null
                    ? fmtMoney(p.per_cycle_minor, currency)
                    : "—"}
                </td>
                <td data-label="Charges left">{p.remaining_cycles}</td>
                <td data-label="Ends">
                  {p.term_ends_at !== null ? fmtDate(p.term_ends_at) : "—"}
                </td>
                <td data-label="Sessions left">{p.remaining_sessions ?? "∞"}</td>
                <td data-label="State">
                  <Tag value={p.lifecycle_state} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The "Purchased" detail — memberships + packages (the credit grants). */
/**
 * "Reactivate" on a cancelled membership row. A `cancel_member_membership` is a
 * HARD cancel (the Stripe subscription is deleted), so there's no literal undo —
 * reactivating means selling/comping a FRESH membership, which the shared
 * `PurchaseCreditsModal` already does. Opens that modal in a portaled MetaModal.
 */
function ReactivateMembershipButton({
  memberId,
  memberName,
  onChanged,
}: {
  readonly memberId: string;
  readonly memberName: string;
  readonly onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="row-action-btn"
        title="A cancelled membership can't be un-cancelled — set up a fresh one"
        onClick={() => setOpen(true)}
      >
        Reactivate
      </button>
      {open ? (
        <MetaModal
          title={`Reactivate membership — ${memberName}`}
          onClose={() => setOpen(false)}
        >
          <p className="modal-subtitle">
            A cancelled membership can&apos;t be un-cancelled (its Stripe
            subscription is gone). Set up a fresh membership:
          </p>
          <PurchaseCreditsModal
            memberId={memberId}
            onPurchased={() => {
              onChanged();
              setOpen(false);
            }}
          />
        </MetaModal>
      ) : null}
    </>
  );
}

// --- Membership pause / change (manage_member_membership) ------------------
// Standard pause windows, in months. Matches the front-end account widget's
// supported durations; the dashboard computes resumes_at = start + N months.
const PAUSE_MONTHS = [1, 3, 6, 12] as const;

/** YYYY-MM-DD → ISO instant at STUDIO start-of-day (not the browser's). */
function isoAtStartOfDay(yyyyMmDd: string): string {
  return zonedDatetimeInputToIso(`${yyyyMmDd}T00:00`);
}
/** Add whole months to a YYYY-MM-DD, returning the ISO instant at studio
 * start-of-day of the resulting date (day-of-month overflow rolls forward,
 * matching `Date#setMonth`). */
function addMonthsIso(yyyyMmDd: string, months: number): string {
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return isoAtStartOfDay(d.toISOString().slice(0, 10));
}
function todayYmd(): string {
  return isoToZonedDateKey(new Date().toISOString());
}
function fmtYmd(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    timeZone: studioTimeZone(),
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * Pause a membership for a standard duration (1/3/6/12 months from a start
 * date) OR a custom date range. For a Stripe-backed membership the "Also pause
 * Stripe billing" box drives the live subscription; un-check it (or it defaults
 * off for a system-only / legacy membership with no Stripe sub) to pause purely
 * in Layer. Dispatches `manage_member_membership{operation:'pause'}`.
 */
function PauseMembershipModal({
  membership,
  onChanged,
}: {
  readonly membership: MemberDetailMembership;
  readonly onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"duration" | "range">("duration");
  const [months, setMonths] = useState<number>(1);
  const [startDate, setStartDate] = useState<string>(todayYmd());
  const [rangeStart, setRangeStart] = useState<string>(todayYmd());
  const [rangeEnd, setRangeEnd] = useState<string>("");
  const [manageStripe, setManageStripe] = useState<boolean>(
    membership.is_stripe_backed,
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const dispatch = useDispatch<Record<string, unknown>, unknown>(
    "manage_member_membership",
  );

  const close = (): void => {
    setOpen(false);
    setLocalError(null);
    dispatch.reset();
  };

  const resumesPreview =
    mode === "duration"
      ? addMonthsIso(startDate, months)
      : rangeEnd !== ""
        ? isoAtStartOfDay(rangeEnd)
        : null;

  const submit = async (): Promise<void> => {
    setLocalError(null);
    let pauseFrom: string;
    let resumesAt: string;
    if (mode === "duration") {
      if (startDate === "") {
        setLocalError("Pick a start date.");
        return;
      }
      pauseFrom = isoAtStartOfDay(startDate);
      resumesAt = addMonthsIso(startDate, months);
    } else {
      if (rangeStart === "" || rangeEnd === "") {
        setLocalError("Pick both a start and an end date.");
        return;
      }
      if (Date.parse(rangeEnd) <= Date.parse(rangeStart)) {
        setLocalError("The end date must be after the start date.");
        return;
      }
      pauseFrom = isoAtStartOfDay(rangeStart);
      resumesAt = isoAtStartOfDay(rangeEnd);
    }
    const r = await dispatch.run({
      member_membership_id: membership.id,
      operation: "pause",
      pause_from: pauseFrom,
      resumes_at: resumesAt,
      manage_stripe_subscription: manageStripe,
    });
    if (r.ok) {
      onChanged();
      close();
    }
  };

  return (
    <>
      <button
        type="button"
        className="row-action-btn"
        onClick={() => setOpen(true)}
      >
        Pause
      </button>
      {open ? (
        <MetaModal title="Pause membership" onClose={close}>
          <p className="modal-subtitle">
            Pause for a standard period or a custom date range.
            {membership.is_stripe_backed
              ? " Stripe pauses billing immediately; the start date is recorded as the effective marker."
              : " This membership has no Stripe subscription, so it pauses in Clavis only."}
          </p>

          <div className="pause-mode-tabs">
            <button
              type="button"
              className={mode === "duration" ? "pause-tab active" : "pause-tab"}
              onClick={() => setMode("duration")}
            >
              Standard duration
            </button>
            <button
              type="button"
              className={mode === "range" ? "pause-tab active" : "pause-tab"}
              onClick={() => setMode("range")}
            >
              Custom range
            </button>
          </div>

          {mode === "duration" ? (
            <>
              <div className="pause-pills">
                {PAUSE_MONTHS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={months === m ? "pause-pill active" : "pause-pill"}
                    onClick={() => setMonths(m)}
                  >
                    {m === 12 ? "1 year" : `${m} month${m > 1 ? "s" : ""}`}
                  </button>
                ))}
              </div>
              <label className="modal-field">
                <span>Start date</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
            </>
          ) : (
            <div className="pause-range-row">
              <label className="modal-field">
                <span>From</span>
                <input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                />
              </label>
              <label className="modal-field">
                <span>Until</span>
                <input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                />
              </label>
            </div>
          )}

          {resumesPreview !== null ? (
            <p className="pause-resume-preview">
              Resumes <strong>{fmtYmd(resumesPreview)}</strong>
            </p>
          ) : null}

          <label className="action-modal-checkbox">
            <input
              type="checkbox"
              checked={manageStripe}
              onChange={(e) => setManageStripe(e.target.checked)}
            />{" "}
            Also pause the Stripe subscription
            {!membership.is_stripe_backed ? " (no Stripe sub on this one)" : ""}
          </label>

          {localError !== null ? (
            <p className="action-modal-error">{localError}</p>
          ) : null}
          {dispatch.error !== null ? (
            <p className="action-modal-error">
              {dispatch.error.message} ({dispatch.error.code})
            </p>
          ) : null}

          <div className="modal-actions">
            <button
              type="button"
              className="row-edit-modal-cancel"
              onClick={close}
              disabled={dispatch.loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="row-edit-modal-save"
              onClick={() => void submit()}
              disabled={dispatch.loading}
            >
              {dispatch.loading ? "Pausing…" : "Pause membership"}
            </button>
          </div>
        </MetaModal>
      ) : null}
    </>
  );
}

/** A membership plan offering from `list_memberships` (catalog). */
interface MembershipPlanOption {
  readonly slug: string;
  readonly name?: string;
  /**
   * Already RESOLVED for the studio's outbound Stripe account (live vs
   * sandbox) by `list_memberships` — a Price id is account-scoped, so the
   * choice is made server-side and this is always the id for the account the
   * subscription actually lives on. Absent ⇒ that account has no Price
   * pinned for this plan, and a Stripe-backed switch must refuse.
   */
  readonly stripe_price_id?: string;
  readonly purchasable?: boolean;
}

/**
 * Change a membership's plan (the `switch` op). Loads the studio's membership
 * catalog and lets staff pick a target plan. For a Stripe-backed subscription
 * the target plan's `stripe_price_id` retargets billing; for a system-only
 * membership only the canonical `membership_slug` is updated.
 */
function ChangeMembershipModal({
  membership,
  onChanged,
}: {
  readonly membership: MemberDetailMembership;
  readonly onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState<ReadonlyArray<MembershipPlanOption> | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [target, setTarget] = useState<string>("");
  const [localError, setLocalError] = useState<string | null>(null);
  const dispatch = useDispatch<Record<string, unknown>, unknown>(
    "manage_member_membership",
  );

  useEffect(() => {
    if (!open || plans !== null) return;
    void (async () => {
      const r = await dispatchAction<
        Record<string, unknown>,
        { memberships: ReadonlyArray<MembershipPlanOption> }
      >("list_memberships", { limit: 200 });
      if (r.ok) {
        setPlans(
          r.response.memberships.filter((m) => m.purchasable !== false),
        );
      } else {
        setLoadError(`${r.message} (${r.code})`);
      }
    })();
  }, [open, plans]);

  const close = (): void => {
    setOpen(false);
    setTarget("");
    setLocalError(null);
    dispatch.reset();
  };

  const submit = async (): Promise<void> => {
    setLocalError(null);
    if (target === "" || target === membership.membership_slug) {
      setLocalError("Pick a different plan.");
      return;
    }
    const offering = (plans ?? []).find((p) => p.slug === target);
    const input: Record<string, unknown> = {
      member_membership_id: membership.id,
      operation: "switch",
      new_membership_slug: target,
    };
    if (membership.is_stripe_backed) {
      if (offering?.stripe_price_id === undefined) {
        setLocalError(
          "That plan has no Stripe price configured, so a Stripe-backed membership can't switch to it.",
        );
        return;
      }
      input["new_price_id"] = offering.stripe_price_id;
    }
    const r = await dispatch.run(input);
    if (r.ok) {
      onChanged();
      close();
    }
  };

  return (
    <>
      <button
        type="button"
        className="row-action-btn"
        onClick={() => setOpen(true)}
      >
        Change
      </button>
      {open ? (
        <MetaModal title="Change membership plan" onClose={close}>
          <p className="modal-subtitle">
            Current plan: <code>{membership.membership_slug}</code>.
            {membership.is_stripe_backed
              ? " Switching retargets the Stripe subscription's price."
              : " This membership has no Stripe subscription, so only the plan is updated in Clavis."}
          </p>
          {loadError !== null ? (
            <p className="action-modal-error">
              Couldn&apos;t load plans: {loadError}
            </p>
          ) : plans === null ? (
            <p className="empty">Loading plans…</p>
          ) : (
            <div className="pause-pills">
              {plans.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  className={
                    target === p.slug
                      ? "pause-pill active"
                      : p.slug === membership.membership_slug
                        ? "pause-pill current"
                        : "pause-pill"
                  }
                  disabled={p.slug === membership.membership_slug}
                  onClick={() => setTarget(p.slug)}
                  title={
                    membership.is_stripe_backed &&
                    p.stripe_price_id === undefined
                      ? "No Stripe price configured"
                      : ""
                  }
                >
                  {p.name ?? p.slug}
                  {p.slug === membership.membership_slug ? " (current)" : ""}
                </button>
              ))}
            </div>
          )}
          {localError !== null ? (
            <p className="action-modal-error">{localError}</p>
          ) : null}
          {dispatch.error !== null ? (
            <p className="action-modal-error">
              {dispatch.error.message} ({dispatch.error.code})
            </p>
          ) : null}
          <div className="modal-actions">
            <button
              type="button"
              className="row-edit-modal-cancel"
              onClick={close}
              disabled={dispatch.loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="row-edit-modal-save"
              onClick={() => void submit()}
              disabled={dispatch.loading || target === ""}
            >
              {dispatch.loading ? "Switching…" : "Switch plan"}
            </button>
          </div>
        </MetaModal>
      ) : null}
    </>
  );
}

/**
 * The Packages table's `Expires` cell — click the date to amend it in place.
 *
 * The THIRD inline-edit chrome on the dashboard, and it exists because the
 * other two are both profile-row shaped: `ProfileEditRow` renders a bare `<dd>`
 * for a `<dl class="kvs">`, and `InlineFieldEditor` prints its own
 * `<strong>label:</strong>` — which in a table would repeat the `<th>` that
 * already names the column. A `<td>` is also `vertical-align: top` at 11px
 * inside `.table-scroll-x`, so the flex chrome those two use does not sit right
 * in one (the same cell-box trap `.row-actions` was introduced to dodge).
 *
 * Editable ONLY on an `active` package (Mario, 2026-09-03). A revoked row has
 * `remaining_sessions = 0` and is excluded by `usablePackageSql` anyway, so
 * amending its expiry would file an audited change that moves nothing — and
 * revoked rows are the MAJORITY of what this table shows (855 of 976 live
 * rows), which is why the affordance is gated rather than merely discouraged.
 *
 * The consequence line under the input is the point of the whole feature:
 * moving a date across `now()` moves real bookable credit in one write, and
 * this is the only place that is stated before the write happens. It is also
 * why the action is absent from every chat tier — see `CHAT_NEVER_TOOL_NAMES`.
 */
function PackageExpiryCell({
  memberPackageId,
  expiresAt,
  remainingSessions,
  editable,
  status,
  onSaved,
}: {
  readonly memberPackageId: string;
  readonly expiresAt: string | null;
  readonly remainingSessions: number;
  readonly editable: boolean;
  readonly status: string;
  readonly onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<ExpiryDraft>(() =>
    initialExpiryDraft(expiresAt),
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const dispatch = useDispatch<
    { readonly member_package_id: string; readonly expires_on: string | null },
    Record<string, unknown>
  >("set_member_package_expiry");

  const shown = fmtDate(expiresAt);

  if (!editable) {
    return (
      <td
        data-label="Expires"
        title={`A ${status} package's expiry has no effect — its credits are already gone.`}
      >
        {shown}
      </td>
    );
  }

  const begin = (): void => {
    setDraft(initialExpiryDraft(expiresAt));
    setParseError(null);
    dispatch.reset();
    setEditing(true);
  };
  const cancel = (): void => {
    setParseError(null);
    dispatch.reset();
    setEditing(false);
  };
  const save = async (): Promise<void> => {
    if (dispatch.loading) return;
    const parsed = parseExpiryDraft(draft);
    if (!parsed.ok) {
      setParseError(parsed.message);
      return;
    }
    setParseError(null);
    // A re-picked identical day is a no-op, not an audit row asserting a change
    // that did not happen — and re-saving the same DAY would silently rewrite
    // the stored time to end-of-day, which the operator did not ask for.
    if (isExpiryUnchanged(expiresAt, parsed.value)) {
      setEditing(false);
      return;
    }
    const r = await dispatch.run({
      member_package_id: memberPackageId,
      expires_on: parsed.value,
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  if (!editing) {
    return (
      <td className="pkg-expiry-cell" data-label="Expires">
        <button
          type="button"
          className="pkg-expiry-trigger"
          title="Click to change the expiry date"
          onClick={begin}
        >
          {shown}
        </button>
      </td>
    );
  }

  const parsed = parseExpiryDraft(draft);
  const consequence = parsed.ok
    ? describeExpiryConsequence({
        currentIso: expiresAt,
        next: parsed.value,
        remainingSessions,
        nowMs: Date.now(),
      })
    : null;
  const err = parseError ?? dispatch.error?.message ?? null;

  return (
    <td className="pkg-expiry-cell pkg-expiry-editing">
      <div className="pkg-expiry-row">
        <input
          type="date"
          className="pkg-expiry-input"
          // Load-bearing, not a nicety: clicking the trigger UNMOUNTS it, so
          // without this focus lands on <body> and the Escape/Enter handlers
          // below never fire — the editor would advertise a keyboard contract
          // it does not honour (caught in the §4 walk, 2026-09-03).
          autoFocus
          value={draft.date}
          disabled={draft.noExpiry}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") cancel();
          }}
        />
        <button
          type="button"
          className="action-dispatch-btn pkg-expiry-save"
          disabled={dispatch.loading}
          onClick={() => void save()}
        >
          {dispatch.loading ? "…" : "Save"}
        </button>
        <button
          type="button"
          className="pkg-expiry-cancel"
          disabled={dispatch.loading}
          onClick={cancel}
        >
          Cancel
        </button>
      </div>
      {/* An empty date box is an ERROR, never a silent clear — removing an
          expiry means the credits never expire, which is an unbounded grant
          and has to be a deliberate tick. */}
      <label className="pkg-expiry-never">
        <input
          type="checkbox"
          checked={draft.noExpiry}
          onChange={(e) => setDraft({ ...draft, noExpiry: e.target.checked })}
        />{" "}
        No expiry
      </label>
      {consequence !== null ? (
        <div className="pkg-expiry-consequence">{consequence}</div>
      ) : null}
      {err !== null ? <div className="flag-card-err">{err}</div> : null}
    </td>
  );
}

/**
 * per-credit-revoke (2026-10-29) — the "Sessions (left / total)" cell is a
 * trigger (the expiry cell's dotted-underline affordance, so the column does
 * not read as a row of buttons) that opens the package's per-credit list:
 * one row per credit, ✕ on each still-available one, Restore on each the
 * team took off. The list stays open across a change — staff cross out two
 * credits in a row — and re-renders from the refetched row.
 */
function PackageCreditsCell({
  pkg,
  editable,
  onChanged,
}: {
  readonly pkg: MemberDetailPackage;
  readonly editable: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <td data-label="Sessions (left / total)" className="pkg-credits-cell">
      <button
        type="button"
        className="pkg-expiry-trigger pkg-credits-trigger"
        title="Click to see every credit — used, taken off, still available"
        onClick={() => setOpen(true)}
      >
        {fmtLeftOfTotal(pkg.remaining_sessions, pkg.total_sessions)}
      </button>
      {open ? (
        <MetaModal title={`Credits — ${pkg.label}`} onClose={() => setOpen(false)}>
          <PackageCreditsList
            pkg={pkg}
            editable={editable}
            onChanged={onChanged}
          />
        </MetaModal>
      ) : null}
    </td>
  );
}

/**
 * Live = entity `status` AND `lifecycle_state` both `active` — mirrors
 * `membershipIsLive` in `revoke-membership-credit.ts` (the `activeMembershipSql`
 * reading), which is what the two actions refuse on; a cancel-at-period-end
 * row keeps both columns `active` (its flag lives in metadata) and stays live.
 * Mirrored here because the SPA does not import the actions package.
 */
function membershipIsLive(mm: MemberDetailMembership): boolean {
  return mm.status === "active" && mm.lifecycle_state === "active";
}

/**
 * membership-per-credit-revoke (2026-10-29) — the membership row's "Sessions
 * (left / cycle)" cell is the same kind of trigger as the package cell above,
 * opening the balance's per-credit list with ✕ and Restore. An unlimited plan
 * (∞) has no credits to list, so its cell stays plain text.
 */
function MembershipCreditsCell({
  membership,
  editable,
  onChanged,
}: {
  readonly membership: MemberDetailMembership;
  readonly editable: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const text = fmtLeftOfTotal(
    membership.remaining_sessions,
    membership.sessions_per_cycle,
  );
  if (membership.remaining_sessions === null) {
    return <td data-label="Sessions (left / cycle)">{text}</td>;
  }
  return (
    <td data-label="Sessions (left / cycle)" className="pkg-credits-cell">
      <button
        type="button"
        className="pkg-expiry-trigger pkg-credits-trigger"
        title="Click to see every credit — used this cycle, taken off, still available"
        onClick={() => setOpen(true)}
      >
        {text}
      </button>
      {open ? (
        <MetaModal
          title={`Credits — ${membership.membership_slug}`}
          onClose={() => setOpen(false)}
        >
          <MembershipCreditsList
            membership={membership}
            editable={editable}
            onChanged={onChanged}
          />
        </MetaModal>
      ) : null}
    </td>
  );
}

function PurchasedDetail({
  memberships,
  packages,
  onChanged,
  memberId,
  memberName,
  lateChangeWaiver,
}: {
  readonly memberships: ReadonlyArray<MemberDetailMembership>;
  readonly packages: ReadonlyArray<MemberDetailPackage>;
  /**
   * 2026-10-06 (first-late-cancel-waiver) — the member's standing for the
   * studio's free first late change; one line under the Packages heading.
   * `null`/absent, or `configured: false`, renders nothing.
   */
  readonly lateChangeWaiver?: MemberLateChangeWaiver | null;
  /** When provided, active rows show Cancel/Pause/Revoke actions + refetch. */
  readonly onChanged?: () => void;
  /** Needed for the cancelled-row "Reactivate" (sell/comp a fresh membership). */
  readonly memberId?: string;
  readonly memberName?: string;
}): JSX.Element {
  const showActions = onChanged !== undefined;
  // Carried onto the transferred-from member's page, so its "← Members"
  // returns to the list this profile was opened from.
  const location = useLocation();
  return (
    <div>
      <h3 className="seg-subtitle">Memberships ({memberships.length})</h3>
      {memberships.length === 0 ? (
        <p className="empty">No memberships.</p>
      ) : (
        <div className="table-scroll-x">
          <table>
            <thead>
              <tr>
                <th>Plan</th>
                <th>State</th>
                <th>Sessions (left / cycle)</th>
                {/* Not "Renews". The cell is `current_period_end`, which is
                    the renewal date only while the membership is still
                    running; on a cancelled one it is when the paid-up period
                    RUNS OUT, and the old header stated the opposite of what
                    happens (Mario, 2026-09-12: "it still says renews ... that
                    sounds wrong"). The State column already says which of the
                    two this row is, so the header names both and the cell
                    stays one date. */}
                <th title="While a membership is running this is its next renewal date; once cancelled it is the date the paid-up period ends.">
                  Renews / ends
                </th>
                <th>Status</th>
                <th>Started</th>
                {showActions ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {memberships.map((mm) => (
                <tr key={mm.id}>
                  <td data-label="Plan">
                    <code>{mm.membership_slug}</code>
                  </td>
                  <td data-label="State">
                    {/* `lifecycle_state` is the cancellation source of truth
                        (the cancel actions write it), so it renders directly —
                        the old `mm.status === "cancelled"` override existed
                        only because those writers used to set the entity
                        `status` instead, which is what made this pill disagree
                        with the Next-payment card above it. `paused` still
                        lives in metadata, not the enum. */}
                    <span>
                      <Tag value={mm.paused ? "paused" : mm.lifecycle_state} />
                      {mm.paused && mm.pause_resumes_at !== null ? (
                        <span className="pause-resume-hint">
                          {" "}
                          ↻ {fmtDate(mm.pause_resumes_at)}
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <MembershipCreditsCell
                    membership={mm}
                    editable={showActions && membershipIsLive(mm)}
                    onChanged={() => onChanged?.()}
                  />
                  <td data-label="Renews / ends">
                    {fmtDate(mm.current_period_end)}
                    {/* A fixed term (custom-membership-fixed-term, 2026-10-29):
                        the date Stripe will end the subscription on, after
                        its last paid cycle runs out. The cell's first date
                        stays the current cycle's end, as for every row. */}
                    {mm.term_ends_at !== null ? (
                      <span
                        className="pause-resume-hint"
                        title={
                          mm.term_months !== null
                            ? `${mm.term_months}-month term — the plan ends on this date.`
                            : "The plan ends on this date."
                        }
                      >
                        {" "}
                        · ends {fmtDate(mm.term_ends_at)}
                      </span>
                    ) : null}
                  </td>
                  <td data-label="Status">{mm.status}</td>
                  <td data-label="Started">{fmtDate(mm.created_at)}</td>
                  {showActions ? (
                    <td className="data-td-actions">
                      <div className="row-actions">
                        {/* `lifecycle_state`, not `status` — the same fix the
                            State pill above already got. The cancel actions
                            write `lifecycle_state` and leave the entity
                            `status` at "active", so this guard kept offering
                            Pause / Change / Cancel on an already-cancelled
                            membership and hid Reactivate behind them. */}
                        {mm.lifecycle_state !== "cancelled" ? (
                          <>
                            {mm.paused ? (
                              <ActionModalButton
                                action="manage_member_membership"
                                buttonLabel="Resume"
                                buttonClassName="row-action-btn"
                                modalTitle="Resume membership"
                                description={
                                  mm.pause_resumes_at !== null
                                    ? `Resume this paused membership now (was set to resume ${fmtDate(mm.pause_resumes_at)}).`
                                    : "Resume this paused membership now."
                                }
                                submitLabel="Resume"
                                prefill={{
                                  member_membership_id: mm.id,
                                  operation: "resume",
                                  manage_stripe_subscription:
                                    mm.is_stripe_backed,
                                }}
                                onDone={() => onChanged?.()}
                              />
                            ) : (
                              <PauseMembershipModal
                                membership={mm}
                                onChanged={() => onChanged?.()}
                              />
                            )}
                            <ChangeMembershipModal
                              membership={mm}
                              onChanged={() => onChanged?.()}
                            />
                            <ActionModalButton
                              action="cancel_member_membership"
                              buttonLabel="Cancel"
                              buttonClassName="row-action-btn row-action-btn-danger"
                              modalTitle="Cancel membership"
                              description="End this recurring membership."
                              submitLabel="Cancel membership"
                              prefill={{ member_membership_id: mm.id }}
                              fields={[
                                {
                                  kind: "text",
                                  name: "cancellation_reason",
                                  label: "Reason (optional)",
                                  maxLength: 500,
                                },
                                {
                                  kind: "checkbox",
                                  name: "cancel_stripe_subscription",
                                  label: "Also cancel the Stripe subscription",
                                  defaultValue: mm.is_stripe_backed,
                                },
                              ]}
                              onDone={() => onChanged?.()}
                            />
                          </>
                        ) : !mm.is_stripe_backed ? (
                          <ActionModalButton
                            action="manage_member_membership"
                            buttonLabel="Reactivate"
                            buttonClassName="row-action-btn"
                            modalTitle="Reactivate membership"
                            description="Revert this cancelled membership back to active. (No Stripe subscription — Clavis-only change.)"
                            submitLabel="Reactivate"
                            prefill={{
                              member_membership_id: mm.id,
                              operation: "reactivate",
                            }}
                            onDone={() => onChanged?.()}
                          />
                        ) : memberId !== undefined ? (
                          <ReactivateMembershipButton
                            memberId={memberId}
                            memberName={memberName ?? "this member"}
                            onChanged={() => onChanged?.()}
                          />
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3 className="seg-subtitle">Packages ({packages.length})</h3>
      {lateChangeWaiver != null && lateChangeWaiver.configured ? (
        <p className="subtle late-change-waiver-line">
          Free late change:{" "}
          {lateChangeWaiver.available
            ? `available${lateChangeWaiver.free > 1 ? ` (${lateChangeWaiver.free - lateChangeWaiver.used} of ${lateChangeWaiver.free} left)` : ""} — the next late cancel or move inside the cancellation window keeps the credit.`
            : !lateChangeWaiver.eligible
              ? `not offered at the ${lateChangeWaiver.stage ?? "current"} stage.`
              : lateChangeWaiver.free === 0
                ? "none offered (the studio's count is 0)."
                : // A prior forfeit counts as used (D3), so `used` can exceed `free` — say only that it is spent.
                  "used — the next late change follows the policy."}
        </p>
      ) : null}
      {packages.length === 0 ? (
        <p className="empty">No packages.</p>
      ) : (
        <div className="table-scroll-x">
          <table>
            <thead>
              <tr>
                <th>Package</th>
                <th>Sessions (left / total)</th>
                <th>Source</th>
                <th>Owes</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Granted</th>
                {showActions ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {packages.map((pk) => (
                <tr key={pk.id}>
                  <td data-label="Package">
                    <span title={pk.package_slug}>{pk.label}</span>
                  </td>
                  <PackageCreditsCell
                    pkg={pk}
                    // Same gate as the expiry cell and the Revoke button: the
                    // list is always viewable, ✕ / Restore only with a refetch
                    // hook and on an active package.
                    editable={showActions && pk.status === "active"}
                    onChanged={() => onChanged?.()}
                  />
                  <td data-label="Source">
                    {pk.source === "transfer" &&
                    pk.transfer_from_member_id ? (
                      <span>
                        Transferred from{" "}
                        <Link
                          to={`/members/${pk.transfer_from_member_id}`}
                          state={location.state}
                        >
                          {pk.transfer_from_member_name ?? "member"}
                        </Link>
                      </span>
                    ) : pk.paid_via ? (
                      `${pk.source} · ${pk.paid_via}`
                    ) : (
                      pk.source
                    )}
                  </td>
                  <td data-label="Owes">
                    {pk.owed_minor === null ? (
                      "—"
                    ) : pk.settled_at === null ? (
                      <span className="pkg-owes">
                        {fmtMoney(pk.owed_minor, pk.owed_currency)}
                      </span>
                    ) : (
                      <span
                        className="pkg-owes-settled"
                        title={`Settled ${fmtDateTime(pk.settled_at)}`}
                      >
                        {fmtMoney(pk.owed_minor, pk.owed_currency)} ·{" "}
                        {pk.settled_as === "waive" ? "waived" : "paid"}
                      </span>
                    )}
                  </td>
                  <PackageExpiryCell
                    memberPackageId={pk.id}
                    expiresAt={pk.expires_at}
                    remainingSessions={pk.remaining_sessions}
                    // Same gate as the Revoke button below: an inert edit is
                    // not offered. `showActions` additionally keeps the cell
                    // read-only wherever the table renders without a refetch
                    // hook (there would be nothing to refresh after a save).
                    editable={showActions && pk.status === "active"}
                    status={pk.status}
                    onSaved={() => onChanged?.()}
                  />
                  <td data-label="Status">{pk.status}</td>
                  <td data-label="Granted">{fmtDate(pk.granted_at)}</td>
                  {showActions ? (
                    <td className="data-td-actions">
                      {/* An open debt is the row's most urgent affordance, so
                          it precedes Revoke. Both can be live at once: an
                          unpaid multi-session pack with credits left can be
                          settled AND revoked. */}
                      {pk.owed_minor !== null && pk.settled_at === null ? (
                        <>
                          <ActionModalButton
                            action="settle_member_package_debt"
                            buttonLabel="Settle"
                            buttonClassName="row-action-btn"
                            modalTitle="Record the payment"
                            description={`${fmtMoney(pk.owed_minor, pk.owed_currency)} is outstanding on "${pk.label}". Records the money against this debt — it does NOT change the member's remaining credits.`}
                            submitLabel="Record payment"
                            prefill={{ member_package_id: pk.id }}
                            fields={[
                              {
                                kind: "select",
                                name: "payment_method",
                                label: "Paid by",
                                required: true,
                                defaultValue: "cash",
                                options: [
                                  { value: "cash", label: "Cash" },
                                  {
                                    value: "bank_transfer",
                                    label: "Bank transfer",
                                  },
                                  { value: "other", label: "Other" },
                                ],
                              },
                              {
                                kind: "number",
                                name: "amount_minor",
                                label: "Amount taken",
                                required: true,
                                minorUnits: true,
                                step: 0.01,
                                defaultValue: (pk.owed_minor / 100).toString(),
                              },
                              {
                                kind: "text",
                                name: "note",
                                label: "Note (optional)",
                                maxLength: 500,
                              },
                            ]}
                            onDone={() => onChanged?.()}
                          />
                          {/* Rail (b) — link money that ALREADY landed (a
                              settle-up pay link's Stripe charge). Its own
                              button because recording a cash settle for money
                              that arrived via Stripe would MINT A SECOND
                              payments row and double-count the revenue —
                              which is the exact trap this rail exists to
                              avoid. The payment id is on this same page's
                              Payments panel. */}
                          <ActionModalButton
                            action="settle_member_package_debt"
                            buttonLabel="Link payment"
                            buttonClassName="row-action-btn"
                            modalTitle="Link an existing payment"
                            description={`Settles the ${fmtMoney(pk.owed_minor, pk.owed_currency)} owed on "${pk.label}" with money Clavis has ALREADY recorded (e.g. a settle-up pay link the member paid). Copy the payment's id from the Payments panel below — recording it as cash instead would double-count the revenue.`}
                            submitLabel="Link payment"
                            prefill={{ member_package_id: pk.id }}
                            fields={[
                              {
                                kind: "text",
                                name: "payment_id",
                                label: "Payment id",
                                required: true,
                              },
                            ]}
                            onDone={() => onChanged?.()}
                          />
                          {/* A separate button, not a checkbox inside the one
                              above: waiving is its own rail at the action (it
                              must send no payment fields at all), and it is
                              also the irreversible choice — writing the debt
                              off turns the grant into a comp. */}
                          <ActionModalButton
                            action="settle_member_package_debt"
                            buttonLabel="Waive"
                            buttonClassName="row-action-btn row-action-btn-danger"
                            modalTitle="Write this debt off"
                            description={`Gives up on collecting the ${fmtMoney(pk.owed_minor, pk.owed_currency)} owed on "${pk.label}". The grant becomes a comp, so payroll re-prices EVERY session delivered on it to zero — including already-delivered ones in any month not yet finalised, so a trainer's pay for those sessions falls retroactively. Credits are unchanged.`}
                            submitLabel="Write it off"
                            prefill={{ member_package_id: pk.id, waive: true }}
                            fields={[
                              {
                                kind: "text",
                                name: "note",
                                label: "Why (optional)",
                                maxLength: 500,
                              },
                            ]}
                            onDone={() => onChanged?.()}
                          />
                        </>
                      ) : null}
                      {pk.status === "active" && pk.remaining_sessions > 0 ? (
                        <ActionModalButton
                          action="revoke_member_package"
                          buttonLabel="Revoke"
                          buttonClassName="row-action-btn row-action-btn-danger"
                          modalTitle="Revoke package credits"
                          description={`Revoke the ${pk.remaining_sessions} remaining credit(s) on "${pk.label}".`}
                          submitLabel="Revoke credits"
                          prefill={{ member_package_id: pk.id }}
                          fields={[
                            {
                              kind: "text",
                              name: "reason",
                              label: "Reason (optional)",
                              maxLength: 500,
                            },
                          ]}
                          onDone={() => onChanged?.()}
                        />
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Customisable layout (2026-06-28) — staff can reorder / resize / hide the
// member-page cards + filter the Journey. The chosen arrangement is saved to
// the staff user's account via the `save_dashboard_layout` canonical action
// (read back by `GET /api/dashboard-layout`), with a localStorage mirror for an
// instant first paint. Discrete grid: width snaps to 1 or 2 columns; scrollable
// cards (conversation / journey / session notes) get a height preset. Models the
// native drag + localStorage pattern already used by the `/data` table.
// ===========================================================================

/** AI enrichment and the consultation form are deliberately absent: they are
 *  occasional, read-then-close things, so they open from the header's "+"
 *  rather than each holding a permanent card in a column. */
type CardId =
  | "active_flows"
  | "conversation"
  | "sessions"
  | "payments"
  | "profile"
  | "onboarding"
  | "memory"
  | "consent"
  | "session_notes"
  | "journey";

/** A card lives in the left column, the right column, or a full-width row
 *  beneath the two columns. The two columns pack INDEPENDENTLY (no shared grid
 *  rows), so a short card on one side never leaves a gap because the other side
 *  is tall, and a card can be dragged to any slot in either column. */
type CardPlacement = "left" | "right" | "full";

interface CardDef {
  readonly id: CardId;
  readonly title: string;
  readonly defaultPlacement: CardPlacement;
  /** Whether the card can be hidden via the Cards menu (profile is the
   *  always-on identity anchor). */
  readonly hideable: boolean;
  /** Whether the card offers a height preset (its body scrolls). */
  readonly resizableHeight: boolean;
}

/** The registry order is the default WITHIN-zone order; defaultPlacement picks
 *  the zone (left / right column or a full-width row).
 *
 *  This IS the member page's design now that staff no longer rearrange it, so
 *  the order below is the one staff see: the fixed-height summaries and the
 *  collapsible detail sections down the LEFT, the two long scrollable
 *  narratives (Journey then Conversation) down the RIGHT. Changing it means
 *  bumping `LAYOUT_VERSION` — otherwise every staff account keeps the
 *  arrangement it saved under the old one. */
const CARD_REGISTRY: ReadonlyArray<CardDef> = [
  { id: "journey", title: "Journey", defaultPlacement: "right", hideable: true, resizableHeight: true },
  { id: "conversation", title: "Conversation", defaultPlacement: "right", hideable: true, resizableHeight: true },
  { id: "payments", title: "Payments", defaultPlacement: "left", hideable: true, resizableHeight: false },
  { id: "sessions", title: "Sessions", defaultPlacement: "left", hideable: true, resizableHeight: false },
  { id: "active_flows", title: "Active flows", defaultPlacement: "left", hideable: true, resizableHeight: true },
  { id: "session_notes", title: "Session notes", defaultPlacement: "left", hideable: true, resizableHeight: true },
  // Retired as a card — consent now renders as rows inside Profile details.
  // Kept in the registry (never rendered) so a saved layout naming it stays
  // parseable; `cardAvailable` pins it false.
  { id: "consent", title: "Consent", defaultPlacement: "full", hideable: true, resizableHeight: false },
  // Full width, beneath both columns: it is a wide field grid, not a column
  // card, and squeezing it into half the page is what made it unreadable.
  { id: "profile", title: "Profile details", defaultPlacement: "full", hideable: false, resizableHeight: false },
  // The full-width reference stack, in the order a person is understood:
  // the fields we hold, then what they told us at signup, then what the agent
  // has learned since.
  { id: "onboarding", title: "Onboarding answers", defaultPlacement: "full", hideable: true, resizableHeight: false },
  { id: "memory", title: "Member memory", defaultPlacement: "full", hideable: true, resizableHeight: false },
];

const PLACEMENTS: ReadonlyArray<CardPlacement> = ["left", "right", "full"];

const CARD_IDS: ReadonlyArray<CardId> = CARD_REGISTRY.map((c) => c.id);
const CARD_BY_ID: Record<CardId, CardDef> = Object.fromEntries(
  CARD_REGISTRY.map((c) => [c.id, c]),
) as Record<CardId, CardDef>;
const HIDEABLE_IDS = new Set<CardId>(
  CARD_REGISTRY.filter((c) => c.hideable).map((c) => c.id),
);

/** Height presets for scrollable cards (px; 0 = Auto → CSS default). */
const HEIGHT_PRESETS: ReadonlyArray<{ readonly label: string; readonly value: number }> = [
  { label: "Auto", value: 0 },
  { label: "Short", value: 300 },
  { label: "Medium", value: 480 },
  { label: "Tall", value: 720 },
];

/** The Journey's event kinds, in filter-menu order. Must equal the kinds the
 *  backend's journey query emits — `journey-kinds-match-backend.test.ts`
 *  derives those from the SQL and fails on any difference, because a kind
 *  missing here is not just missing a filter row: `Journey` drops its events. */
export const JOURNEY_KINDS = [
  "registration",
  "booking",
  "payment",
  "checkout",
  "comms_out",
  "comms_in",
  "flow",
  "account",
] as const;
type JourneyKind = (typeof JOURNEY_KINDS)[number];
const JOURNEY_KIND_LABEL: Record<JourneyKind, string> = {
  registration: "Registration",
  booking: "Bookings",
  payment: "Payments",
  // The Stripe funnel's dead ends — abandoned / expired / declined. Filed
  // apart from Payments deliberately: no money moved, and staff hiding
  // Payments must not also lose the reason a payment never arrived.
  checkout: "Checkout",
  // "Outgoing", not "Sent": the kind carries queued and FAILED sends too, and
  // a filter named for the happy path hides the rows staff most need.
  comms_out: "Outgoing",
  comms_in: "Received",
  // Entering and leaving a flow (journey-flow-events, 2026-10-12). The
  // messages a flow sends stay under Outgoing, naming their flow on the
  // right, so hiding this row hides the enrolments and none of the messages.
  flow: "Flows",
  account: "Account",
};

const MEMBER_PAGE = "member-detail";
const LS_MEMBER_LAYOUT_CACHE = "layer:member-layout-cache";

/** Stamped into every saved blob. A stored layout carrying an UNKNOWN version
 *  was arranged against a different card set / order and is discarded rather
 *  than migrated — bump this whenever CARD_REGISTRY's zones or order change,
 *  or staff keep seeing the arrangement they saved under the old design.
 *  Bump it too when a journey kind is added, WITH an entry in
 *  `MIGRATABLE_LAYOUT_VERSIONS` below, or every saved filter keeps the new
 *  kind hidden (v9 `checkout`, v10 `flow`). */
export const LAYOUT_VERSION = 10;

/** Older versions whose blobs are MIGRATED rather than dropped, each mapped to
 *  the journey kinds that did not exist when such a blob was written.
 *
 *  A stored `journeyKinds` list is a record of what staff chose to HIDE, so a
 *  kind added afterwards must arrive switched ON — nobody unticks a filter row
 *  that does not exist yet, and reading its absence as a deliberate exclusion
 *  is how a new timeline source ships invisible. Dropping the blob instead (the
 *  pre-v9 behaviour for ANY mismatch) would also reset card order, hidden cards
 *  and heights: a far larger change than the one row actually being added, and
 *  not one staff asked for. Only add an entry here when the card set itself is
 *  UNCHANGED between the two versions — otherwise the old arrangement is
 *  genuinely meaningless and dropping it is correct.
 *
 *  Each entry lists EVERY kind added since that version, not just the next
 *  one: a v8 blob predates both `checkout` (v9) and `flow` (v10). */
const MIGRATABLE_LAYOUT_VERSIONS: Readonly<
  Record<number, ReadonlyArray<JourneyKind>>
> = {
  8: ["checkout", "flow"],
  9: ["flow"],
};

interface MemberLayout {
  readonly order: ReadonlyArray<CardId>;
  readonly hidden: ReadonlyArray<CardId>;
  readonly placement: Readonly<Partial<Record<CardId, CardPlacement>>>;
  readonly height: Readonly<Partial<Record<CardId, number>>>;
  readonly journeyKinds: ReadonlyArray<JourneyKind>;
}

function defaultLayout(): MemberLayout {
  return {
    order: [...CARD_IDS],
    hidden: [],
    placement: {},
    height: {},
    journeyKinds: [...JOURNEY_KINDS],
  };
}

/** Coerce an unknown stored/loaded blob into a valid layout — keep known cards
 *  in their stored order, append any new registry cards at defaults, drop
 *  unknown ids, never hide a non-hideable card, clamp placements/heights/kinds.
 *  A blob from a MIGRATABLE older version keeps its arrangement and gains the
 *  journey kinds added since; one from any other version is dropped whole. */
export function normalizeLayout(raw: unknown): MemberLayout {
  if (raw === null || typeof raw !== "object") return defaultLayout();
  const r = raw as Record<string, unknown>;
  // Kinds introduced since the stored blob was written — [] for a current
  // blob, null when the version is unknown and the blob must be dropped.
  const storedVersion = r["v"];
  const kindsAddedSince: ReadonlyArray<JourneyKind> | null =
    storedVersion === LAYOUT_VERSION
      ? []
      : typeof storedVersion === "number"
        ? (MIGRATABLE_LAYOUT_VERSIONS[storedVersion] ?? null)
        : null;
  if (kindsAddedSince === null) return defaultLayout();
  const isCardId = (x: unknown): x is CardId =>
    typeof x === "string" && (CARD_IDS as readonly string[]).includes(x);

  const storedOrder = Array.isArray(r["order"])
    ? (r["order"] as unknown[]).filter(isCardId)
    : [];
  const seen = new Set(storedOrder);
  const order: CardId[] = [
    ...storedOrder,
    ...CARD_IDS.filter((id) => !seen.has(id)),
  ];

  const hidden = Array.isArray(r["hidden"])
    ? (r["hidden"] as unknown[]).filter(
        (x): x is CardId => isCardId(x) && HIDEABLE_IDS.has(x),
      )
    : [];

  // Placement (left / right / full) — the stored map where it names a real
  // zone, the registry default otherwise (a card added since the blob was
  // written).
  const placement: Partial<Record<CardId, CardPlacement>> = {};
  const storedPlacement =
    r["placement"] !== null && typeof r["placement"] === "object"
      ? (r["placement"] as Record<string, unknown>)
      : null;
  for (const id of order) {
    const stored = storedPlacement?.[id];
    placement[id] =
      stored === "left" || stored === "right" || stored === "full"
        ? stored
        : CARD_BY_ID[id].defaultPlacement;
  }

  const height: Partial<Record<CardId, number>> = {};
  if (r["height"] !== null && typeof r["height"] === "object") {
    for (const [k, v] of Object.entries(
      r["height"] as Record<string, unknown>,
    )) {
      if (isCardId(k) && typeof v === "number" && v >= 0 && v <= 4000) {
        height[k] = v;
      }
    }
  }

  const jk = Array.isArray(r["journeyKinds"])
    ? (r["journeyKinds"] as unknown[]).filter(
        (x): x is JourneyKind =>
          typeof x === "string" &&
          (JOURNEY_KINDS as readonly string[]).includes(x),
      )
    : [];
  const journeyKinds =
    jk.length > 0
      ? [...jk, ...kindsAddedSince.filter((k) => !jk.includes(k))]
      : [...JOURNEY_KINDS];

  return { order, hidden, placement, height, journeyKinds };
}

function serializeLayout(l: MemberLayout): Record<string, unknown> {
  return {
    v: LAYOUT_VERSION,
    order: l.order,
    hidden: l.hidden,
    placement: l.placement,
    height: l.height,
    journeyKinds: l.journeyKinds,
  };
}

function loadCachedLayout(): MemberLayout {
  try {
    const raw = localStorage.getItem(LS_MEMBER_LAYOUT_CACHE);
    if (raw !== null) return normalizeLayout(JSON.parse(raw));
  } catch {
    // localStorage unavailable / malformed — fall through to defaults.
  }
  return defaultLayout();
}

function saveCachedLayout(l: MemberLayout): void {
  try {
    localStorage.setItem(LS_MEMBER_LAYOUT_CACHE, JSON.stringify(serializeLayout(l)));
  } catch {
    // Non-fatal (private mode / quota).
  }
}

/**
 * Owns the member-page layout state. Seeds synchronously from the localStorage
 * paint cache (instant first paint), hydrates from the DB on mount (source of
 * truth — follows the staff user across devices), and persists every mutation
 * to BOTH the cache and the account (via `save_dashboard_layout`).
 */
function useMemberLayout(): {
  readonly layout: MemberLayout;
  readonly moveCard: (
    fromId: CardId,
    placement: CardPlacement,
    beforeId: CardId | null,
  ) => void;
  readonly setPlacement: (id: CardId, placement: CardPlacement) => void;
  readonly setHeight: (id: CardId, height: number) => void;
  readonly toggleHidden: (id: CardId) => void;
  readonly toggleJourneyKind: (kind: JourneyKind) => void;
  readonly reset: () => void;
} {
  const [layout, setLayout] = useState<MemberLayout>(() => loadCachedLayout());
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const save = useDispatch<
    { readonly page: string; readonly preferences: Record<string, unknown> },
    { readonly saved: boolean }
  >("save_dashboard_layout");
  const saveRef = useRef(save);
  saveRef.current = save;

  // Hydrate from the DB on mount — the account is the source of truth, so it
  // wins over the paint cache (which may be stale or from another device).
  useEffect(() => {
    let cancelled = false;
    void api
      .dashboardLayout(MEMBER_PAGE)
      .then((res) => {
        if (cancelled || res.preferences === null) return;
        const norm = normalizeLayout(res.preferences);
        setLayout(norm);
        saveCachedLayout(norm);
      })
      .catch(() => {
        // Keep the cached/default layout on a transient read error.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback((next: MemberLayout): void => {
    setLayout(next);
    saveCachedLayout(next);
    void saveRef.current.run({
      page: MEMBER_PAGE,
      preferences: serializeLayout(next),
    });
  }, []);

  const mutate = useCallback(
    (fn: (prev: MemberLayout) => MemberLayout): void => {
      persist(fn(layoutRef.current));
    },
    [persist],
  );

  // Move a card into a target zone (left / right / full), positioned before
  // `beforeId` within that zone (or appended to the zone's end when null).
  const moveCard = useCallback(
    (fromId: CardId, placement: CardPlacement, beforeId: CardId | null): void => {
      mutate((prev) => {
        const nextPlacement = { ...prev.placement, [fromId]: placement };
        const placeOf = (id: CardId): CardPlacement =>
          nextPlacement[id] ?? CARD_BY_ID[id].defaultPlacement;
        const without = prev.order.filter((x) => x !== fromId);
        let insertAt: number;
        if (beforeId !== null && beforeId !== fromId) {
          const idx = without.indexOf(beforeId);
          insertAt = idx < 0 ? without.length : idx;
        } else {
          // Append to the end of the target zone (after its last card).
          let last = -1;
          without.forEach((id, i) => {
            if (placeOf(id) === placement) last = i;
          });
          insertAt = last + 1;
        }
        return {
          ...prev,
          placement: nextPlacement,
          order: [...without.slice(0, insertAt), fromId, ...without.slice(insertAt)],
        };
      });
    },
    [mutate],
  );

  // The L / R / Full buttons — drop the card at the end of the chosen zone.
  const setPlacement = useCallback(
    (id: CardId, placement: CardPlacement): void => moveCard(id, placement, null),
    [moveCard],
  );

  const setHeight = useCallback(
    (id: CardId, height: number): void =>
      mutate((prev) => ({ ...prev, height: { ...prev.height, [id]: height } })),
    [mutate],
  );

  const toggleHidden = useCallback(
    (id: CardId): void =>
      mutate((prev) => ({
        ...prev,
        hidden: prev.hidden.includes(id)
          ? prev.hidden.filter((x) => x !== id)
          : [...prev.hidden, id],
      })),
    [mutate],
  );

  const toggleJourneyKind = useCallback(
    (kind: JourneyKind): void =>
      mutate((prev) => ({
        ...prev,
        journeyKinds: prev.journeyKinds.includes(kind)
          ? prev.journeyKinds.filter((x) => x !== kind)
          : [...prev.journeyKinds, kind],
      })),
    [mutate],
  );

  const reset = useCallback((): void => persist(defaultLayout()), [persist]);

  return {
    layout,
    moveCard,
    setPlacement,
    setHeight,
    toggleHidden,
    toggleJourneyKind,
    reset,
  };
}

/** Pointer-based drag (grip only), modelled on the `/data` table's column drag.
 *  Zone-aware: figures out which zone (left / right column or the full-width
 *  row) the pointer is over and which card to drop before WITHIN that zone, so a
 *  card can be moved to any slot in either column. The zones pack independently,
 *  so this is what lets e.g. Member memory move to the right between Journey and
 *  Session notes. */
function useCardReorder(
  containerRef: RefObject<HTMLDivElement | null>,
  onDrop: (
    fromId: CardId,
    placement: CardPlacement,
    beforeId: CardId | null,
  ) => void,
): {
  readonly drag: {
    readonly id: CardId;
    readonly placement: CardPlacement;
    readonly beforeId: CardId | null;
    readonly x: number;
    readonly y: number;
  } | null;
  readonly startDrag: (e: ReactMouseEvent, id: CardId) => void;
} {
  const [drag, setDrag] = useState<{
    id: CardId;
    placement: CardPlacement;
    beforeId: CardId | null;
    x: number;
    y: number;
  } | null>(null);
  const geom = useRef<{ id: CardId; startX: number; startY: number; active: boolean } | null>(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  const startDrag = useCallback(
    (e: ReactMouseEvent, id: CardId): void => {
      if (e.button !== 0) return;
      e.preventDefault();
      geom.current = { id, startX: e.clientX, startY: e.clientY, active: false };

      const compute = (
        ev: MouseEvent,
      ): { placement: CardPlacement; beforeId: CardId | null } => {
        const root = containerRef.current;
        if (root === null) return { placement: "left", beforeId: null };
        // Which zone is the pointer over? Full-width row if it's below the two
        // columns' band; otherwise left/right by the two-column midpoint.
        const twocol = root
          .querySelector(".member-twocol")
          ?.getBoundingClientRect();
        const fullZone = root
          .querySelector('[data-zone="full"]')
          ?.getBoundingClientRect();
        let placement: CardPlacement;
        if (fullZone !== undefined && ev.clientY >= fullZone.top) {
          placement = "full";
        } else if (twocol !== undefined) {
          placement =
            ev.clientX < twocol.left + twocol.width / 2 ? "left" : "right";
        } else {
          placement = "left";
        }
        // Which card in that zone to drop before (first whose centre is below
        // the pointer); null → append to the zone's end.
        const zoneEl = root.querySelector(`[data-zone="${placement}"]`);
        const cards = Array.from(
          zoneEl?.querySelectorAll<HTMLElement>(".member-card") ?? [],
        );
        for (const el of cards) {
          const cid = el.dataset["cardId"] as CardId | undefined;
          if (cid === undefined || cid === id) continue;
          const rect = el.getBoundingClientRect();
          if (ev.clientY < rect.top + rect.height / 2) {
            return { placement, beforeId: cid };
          }
        }
        return { placement, beforeId: null };
      };

      const onMove = (ev: MouseEvent): void => {
        const g = geom.current;
        if (g === null) return;
        if (!g.active) {
          if (Math.abs(ev.clientX - g.startX) < 4 && Math.abs(ev.clientY - g.startY) < 4) {
            return;
          }
          g.active = true;
        }
        const { placement, beforeId } = compute(ev);
        setDrag({ id: g.id, placement, beforeId, x: ev.clientX, y: ev.clientY });
      };

      const onUp = (): void => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        const g = geom.current;
        geom.current = null;
        setDrag((cur) => {
          if (g?.active === true && cur !== null) {
            onDropRef.current(g.id, cur.placement, cur.beforeId);
          }
          return null;
        });
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [containerRef],
  );

  return { drag, startDrag };
}

/** Below this the match isn't worth making — treat it as "no match" and let the
 *  card size to its content. */
const CARD_MATCH_MIN_PX = 240;

/** Floor for the band-equalisation height, which is one band MINUS the card
 *  above it and so is legitimately much smaller than a whole-band match. */
const CARD_BAND_MIN_PX = 100;

/**
 * Left-column cards whose bottom edge is pinned to a right-column card's, so
 * the two columns read as bands rather than two independent stacks.
 *
 * `card` must be in one column and `endsWith` in the other, and `card`'s body
 * must scroll (`resizableHeight`) — it is being told a height, not asked for
 * one. The pairs below assume the CARD_REGISTRY order: Journey spans
 * Payments+Sessions, Conversation spans Active flows+Session notes.
 */
const CARD_HEIGHT_MATCHES: ReadonlyArray<{
  readonly card: CardId;
  readonly endsWith: CardId;
}> = [
  { card: "journey", endsWith: "sessions" },
  { card: "conversation", endsWith: "session_notes" },
];

/**
 * "A card just changed its own height — re-measure the bands."
 *
 * Dispatched on `window` by any card whose content can grow or shrink in place.
 * See the long note on {@link useCardHeightMatches} for why the hook's other
 * three triggers cannot see this: none of them fires when the SHORTER column
 * changes, because the page container stays as tall as the taller one.
 *
 * A plain window event rather than props because the height hook lives at page
 * level and the card that changes is several levels down; threading a callback
 * through every card would make the whole registry aware of a concern that is
 * one card's business.
 */
const CARD_CONTENT_RESIZED = "layer:member-card-content-resized";

/** Announce a card's own height change to {@link useCardHeightMatches}. */
function announceCardContentResized(): void {
  window.dispatchEvent(new Event(CARD_CONTENT_RESIZED));
}

/**
 * Heights for every pair in {@link CARD_HEIGHT_MATCHES}, so each left-column
 * card ends exactly where its right-column partner ends.
 *
 * The two columns pack INDEPENDENTLY — deliberately, so a short card never
 * leaves a gap because the opposite card is tall — which means no CSS rule can
 * end a card in one column at a card in the other. So measure: the card's
 * height is the distance from its own top to the bottom of its partner, and its
 * body scrolls inside that.
 *
 * A pair yields 0 (→ `--card-h: auto`) whenever the two aren't side by side: a
 * phone stacks the columns, either card can be hidden, and either can be
 * absent for a given member.
 *
 * WHAT IT WATCHES, and why it is not just the right column. The quantity is
 * `sessions.bottom - journey.top`, and BOTH ends move when anything ABOVE the
 * grid changes height — the KPI strip gaining a taller card, the title row
 * wrapping, a safety-flag banner appearing. None of that resizes the right
 * column, so watching only that column left a stale pixel height pinned to the
 * card and the match silently drifted. So:
 *   - a layout effect on EVERY render, which covers anything React re-rendered;
 *   - a ResizeObserver on the page container (catches async changes React
 *     didn't drive — web fonts swapping in, an image decoding);
 *   - window resize.
 *
 * No feedback loop, despite the page container containing the matched cards
 * themselves: a matched card's own height cannot move its own top (nothing
 * below it in its column feeds back upward), and the columns pack independently
 * so it cannot move its partner's bottom either. A re-measure after this hook
 * changes a height therefore computes the SAME number, and setting state to an
 * unchanged value bails out of re-rendering. Pairs settle in order — Journey
 * fixes Conversation's top, so Conversation's own match resolves on the next
 * pass rather than fighting it.
 *
 * ONE CASE THE THREE TRIGGERS ABOVE MISS, and why {@link CARD_CONTENT_RESIZED}
 * exists. All three ultimately watch things ABOVE or AROUND the grid; the
 * ResizeObserver watches the page container. A card whose own content changes
 * height WITHOUT changing the page container's height fires none of them — and
 * that is exactly what happens when the shorter column changes: the page is as
 * tall as the TALLER column, so shrinking the other one moves nothing the
 * observer is watching. Concretely (2026-08-06): opening the Session-notes
 * editor grows that card, Conversation is pinned to its bottom and grows with
 * it — and on close the notes card shrank back while Conversation stayed
 * stretched, because the right column was now the taller one and the page
 * container never moved. It only corrected on the next window resize. Any card
 * that can change its own height must therefore SAY so.
 */
function useCardHeightMatches(
  pageRef: RefObject<HTMLDivElement | null>,
  gridRef: RefObject<HTMLDivElement | null>,
  /** False while the page is still loading / errored, when there is no grid to
   *  measure. A ref alone can't drive this: it is null on the loading render
   *  and mutating it later re-runs no effect. */
  ready: boolean,
): Readonly<Partial<Record<CardId, number>>> {
  const [heights, setHeights] = useState<Partial<Record<CardId, number>>>({});

  const measure = useCallback((): void => {
    const grid = gridRef.current;
    const next: Partial<Record<CardId, number>> = {};
    if (ready && grid !== null) {
      const card = (id: CardId): DOMRect | null =>
        grid
          .querySelector<HTMLElement>(`[data-card-id="${id}"]`)
          ?.getBoundingClientRect() ?? null;

      // BAND EQUALISATION, first: the right column is two bands — Payments +
      // Sessions, then Active flows + Session notes. The second band is given
      // the height of the first, SPLIT EVENLY between its two cards, so each
      // holds its half whether it is full or empty and the column never
      // reflows as content arrives. Both bodies scroll inside their half.
      //
      // The pair matches below then follow from it: Journey already ends at
      // Sessions, and Conversation ends at Session notes, so equal bands make
      // Conversation exactly as tall as Journey by construction rather than by
      // a third measurement.
      const payments = card("payments");
      const sessions = card("sessions");
      const flows = card("active_flows");
      const notes = card("session_notes");
      if (payments !== null && sessions !== null && flows !== null && notes !== null) {
        // Round each EDGE to the pixel grid before subtracting. Rounding the
        // difference instead let sub-pixel rects land the two bands a pixel
        // apart, which then carried into the Conversation match below.
        const bandOne = Math.round(sessions.bottom) - Math.round(payments.top);
        const gap = Math.round(notes.top) - Math.round(flows.bottom);
        // The halves must sum EXACTLY to the band, so the second takes the
        // remainder rather than the same floored figure.
        const half = Math.floor((bandOne - gap) / 2);
        const rest = bandOne - gap - half;
        // A SMALLER floor than the pair matches use: these heights are half a
        // band. Reusing the pair floor silently skipped the rule.
        if (half >= CARD_BAND_MIN_PX) {
          next["active_flows"] = half;
          next["session_notes"] = rest;
        }
      }

      for (const pair of CARD_HEIGHT_MATCHES) {
        const el = grid.querySelector<HTMLElement>(
          `[data-card-id="${pair.card}"]`,
        );
        const anchor = grid.querySelector<HTMLElement>(
          `[data-card-id="${pair.endsWith}"]`,
        );
        if (el === null || anchor === null) continue;
        const a = el.getBoundingClientRect();
        const b = anchor.getBoundingClientRect();
        // Same left edge → the columns are stacked (phone), not side by side.
        if (Math.abs(a.left - b.left) <= 1) continue;
        const h = Math.round(b.bottom) - Math.round(a.top);
        if (h >= CARD_MATCH_MIN_PX) next[pair.card] = h;
      }
    }
    setHeights((prev) => {
      const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
      for (const k of keys) {
        if (prev[k as CardId] !== next[k as CardId]) return next;
      }
      return prev; // unchanged — keep the identity so React bails out
    });
  }, [gridRef, ready]);

  // Every render — the cheap, reliable trigger for anything React just changed
  // above the grid. `useLayoutEffect` so the corrected height is committed
  // before the browser paints, otherwise the card visibly jumps.
  useLayoutEffect(measure);

  // Async layout changes React never re-rendered for.
  useEffect(() => {
    const page = pageRef.current;
    if (!ready || page === null) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(page);
    window.addEventListener("resize", measure);
    window.addEventListener(CARD_CONTENT_RESIZED, measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener(CARD_CONTENT_RESIZED, measure);
    };
  }, [pageRef, ready, measure]);

  return heights;
}

/** Crisp inline magnifier — the dashboard ships no icon library. */
const PLACEMENT_BUTTONS: ReadonlyArray<{
  readonly value: CardPlacement;
  readonly glyph: string;
  readonly title: string;
}> = [
  { value: "left", glyph: "◧", title: "Left column" },
  { value: "right", glyph: "◨", title: "Right column" },
  { value: "full", glyph: "▭▭", title: "Full width" },
];

/**
 * The cards that hold the member's own record — every control in them writes
 * it, through actions that refuse an erased member with `member_erased` (box
 * 17, 2026-10-31), so `RecordLock` switches them off on an erased member's
 * page. The other cards read, or act on bookings, money and flows.
 */
const LOCKED_WHEN_ERASED: ReadonlySet<CardId> = new Set<CardId>([
  "profile",
  "onboarding",
  "session_notes",
]);

/** One card cell: the existing card JSX + (in edit mode) a drag grip + a
 *  left/right/full placement control + a height preset. */
function MemberCard({
  def,
  placement,
  height,
  editMode,
  dragging,
  dropTarget,
  onGripDown,
  onPlacementChange,
  onHeightChange,
  children,
}: {
  readonly def: CardDef;
  readonly placement: CardPlacement;
  readonly height: number;
  readonly editMode: boolean;
  readonly dragging: boolean;
  readonly dropTarget: boolean;
  readonly onGripDown: (e: ReactMouseEvent, id: CardId) => void;
  readonly onPlacementChange: (id: CardId, placement: CardPlacement) => void;
  readonly onHeightChange: (id: CardId, height: number) => void;
  readonly children: ReactNode;
}): JSX.Element {
  const cls =
    "member-card" +
    (editMode ? " member-card-edit" : "") +
    (dragging ? " member-card-dragging" : "") +
    (dropTarget ? " member-card-drop-target" : "");
  const style =
    height > 0 ? ({ "--card-h": `${height}px` } as CSSProperties) : undefined;
  return (
    <div className={cls} data-card-id={def.id} style={style}>
      {editMode ? (
        <div className="member-card-bar">
          <span
            className="member-card-grip"
            title="Drag to reorder"
            onMouseDown={(e) => onGripDown(e, def.id)}
          >
            ⠿
          </span>
          <span className="member-card-bar-title">{def.title}</span>
          <span className="member-card-sizers">
            {PLACEMENT_BUTTONS.map((b) => (
              <button
                key={b.value}
                type="button"
                className={placement === b.value ? "on" : ""}
                title={b.title}
                onClick={() => onPlacementChange(def.id, b.value)}
              >
                {b.glyph}
              </button>
            ))}
            {def.resizableHeight ? (
              <select
                className="member-card-height"
                value={height}
                title="Card height"
                onChange={(e) => onHeightChange(def.id, Number(e.target.value))}
              >
                {HEIGHT_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            ) : null}
          </span>
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** The "Cards" show/hide checklist — clones the `/data` table column picker. */
function CardsMenu({
  hidden,
  availability,
  onToggle,
}: {
  readonly hidden: ReadonlyArray<CardId>;
  readonly availability: Record<CardId, boolean>;
  readonly onToggle: (id: CardId) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDocDown);
    return () => window.removeEventListener("mousedown", onDocDown);
  }, [open]);
  const hideable = CARD_REGISTRY.filter((c) => c.hideable);
  const shownCount = hideable.filter((c) => !hidden.includes(c.id)).length;
  return (
    <div className="data-add-field member-cards-menu" ref={ref}>
      <button
        type="button"
        className="data-add-field-btn"
        onClick={() => setOpen((v) => !v)}
      >
        Cards ({shownCount}/{hideable.length})
      </button>
      {open ? (
        <div className="data-add-field-menu" role="menu">
          <div className="data-add-field-menu-head">Show cards</div>
          {hideable.map((c) => (
            <label key={c.id} className="data-add-field-option">
              <input
                type="checkbox"
                checked={!hidden.includes(c.id)}
                onChange={() => onToggle(c.id)}
              />
              <span>
                {c.title}
                {availability[c.id] ? "" : " (no data)"}
              </span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function MemberDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  // "← Members" returns to the list as it was left — its ticked filters
  // included — when the profile was opened from it; plain /members otherwise.
  const backToMembers = membersListHref(useLocation().state);
  // payment-received-credits push 2 (2026-11-04) — the "no credits added"
  // alert links here with `?add_credits=<payment id>`: that payment's form
  // opens on arrival, or the Payments card says why it takes no credits.
  const [searchParams, setSearchParams] = useSearchParams();
  const addCreditsFor = searchParams.get("add_credits");
  const dropAddCreditsLink = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("add_credits");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const [data, setData] = useState<MemberDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (id === undefined) {
      setError("missing_member_id");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .member(id)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? e.code ?? `http_${e.status}`
            : e.message,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Silent re-fetch after an in-page mutation (e.g. saving the staff flag) — no
  // `loading` flip, so the page doesn't blink back to "Loading…".
  const refresh = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    try {
      setData(await api.member(id));
    } catch {
      // Keep the current view on a transient refetch error.
    }
  }, [id]);

  // The card layout — now a designed default (see CARD_REGISTRY) rather than
  // something staff arrange themselves, still account-backed via
  // save_dashboard_layout for the journey filter, with a localStorage paint
  // cache.
  // TODO(kinetic-skin): Mario asked for the "Customize layout" entry point to
  // go, so nothing flips `editMode` any more and the drag / resize / hide
  // machinery below it is unreachable. Removing that machinery (useCardReorder,
  // CardsMenu, the placement + height controls and their persistence) is a
  // scheduled follow-up slice, not part of this re-skin.
  const { layout, moveCard, setPlacement, setHeight, toggleJourneyKind } =
    useMemberLayout();
  const editMode = false;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const { drag, startDrag } = useCardReorder(gridRef, moveCard);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const matchedHeights = useCardHeightMatches(pageRef, gridRef, data !== null);

  if (loading) {
    return <p className="empty">Loading member…</p>;
  }
  if (error !== null) {
    const notFound = error === "member_not_found";
    return (
      <div>
        <p className="page-title">
          <Link to={backToMembers} className="data-member-link">
            ← Members
          </Link>
        </p>
        <p className="empty">
          {notFound
            ? "Member not found in this studio."
            : `Couldn't load this member (${error}).`}
        </p>
      </div>
    );
  }
  if (data === null) {
    return <p className="empty">Member not found.</p>;
  }

  const m = data.member;
  const title =
    m.display_name ?? m.email ?? m.phone ?? `Member ${m.id.slice(0, 8)}`;
  // Erased by `delete_member` (box 17): the notice says so, and the record's
  // editors are switched off (`RecordLock`, `MemberActions`).
  const erased = m.erased_at !== null;

  // Net refunds against their charge (via `parent_payment_id`): how much of each
  // charge has been refunded, so the Refund button hides once a charge is fully
  // refunded and the "Successful" tab drops fully-refunded charges.
  const refundedByCharge = new Map<string, number>();
  for (const p of data.past_payments) {
    if (p.kind === "refund" && p.parent_payment_id !== null) {
      refundedByCharge.set(
        p.parent_payment_id,
        (refundedByCharge.get(p.parent_payment_id) ?? 0) + p.amount_minor,
      );
    }
  }
  const isFullyRefunded = (p: MemberDetailPayment): boolean =>
    p.kind === "charge" &&
    (refundedByCharge.get(p.id) ?? 0) >= p.amount_minor;
  const successfulPayments = data.past_payments.filter(
    (p) => isSuccessfulPayment(p) && !isFullyRefunded(p),
  );
  const addCreditsBlocked =
    addCreditsFor === null
      ? null
      : addCreditsBlockedReason(
          data.past_payments.find((p) => p.id === addCreditsFor),
          refundedByCharge.get(addCreditsFor) ?? 0,
        );

  // Each card's content (the existing JSX, unchanged) keyed by id, plus whether
  // it has data to show. Conditional cards (memory / consent / onboarding) are
  // "unavailable" — kept in the order but not rendered — when their data is
  // absent, matching the prior inline `data.memory !== null ? …` gates.
  const onboardingAvailable =
    readMetaObject(m.metadata, "onboarding") !== null ||
    onboardingPreferences(m.metadata) !== null;
  const cardContent: Record<CardId, ReactNode> = {
    active_flows: (
      <ActiveFlowsPanel
        memberId={m.id}
        flows={data.flows}
        onChanged={() => void refresh()}
      />
    ),
    conversation: (
      <ConversationPanel
        memberId={m.id}
        memberName={m.display_name}
        memberPhone={m.phone}
        memberEmail={m.email}
        messages={data.messages}
      />
    ),
    sessions: (
      <SegmentedDetailCard
        title="Sessions"
        segments={[
          {
            key: "upcoming",
            label: "Upcoming",
            count: data.upcoming_appointments.length,
            render: () => (
              <AppointmentsTable
                rows={data.upcoming_appointments}
                waiverAvailable={data.late_change_waiver?.available === true}
                showAttendance={false}
                onChanged={() => void refresh()}
                memberId={m.id}
                swapPackages={data.packages}
                swapMemberships={data.memberships}
              />
            ),
          },
          {
            key: "past",
            label: "Past",
            count: data.past_appointments.length,
            render: () => (
              <AppointmentsTable
                rows={data.past_appointments}
                waiverAvailable={data.late_change_waiver?.available === true}
                showAttendance
                onChanged={() => void refresh()}
                memberId={m.id}
                swapPackages={data.packages}
                swapMemberships={data.memberships}
              />
            ),
          },
          {
            key: "purchased",
            label: "Purchased",
            count: m.purchased_credits_total,
            render: () => (
              <PurchasedDetail
                memberships={data.memberships}
                packages={data.packages}
                lateChangeWaiver={data.late_change_waiver}
                onChanged={() => void refresh()}
                memberId={m.id}
                memberName={title}
              />
            ),
          },
        ]}
      />
    ),
    payments: (
      <>
      {addCreditsBlocked !== null ? (
        <p className="member-note" role="status">
          {addCreditsBlocked}{" "}
          <button type="button" className="row-action-btn" onClick={dropAddCreditsLink}>
            Dismiss
          </button>
        </p>
      ) : null}
      <SegmentedDetailCard
        title="Payments"
        // A creditable payment is an active charge with no refund, so it is
        // always in Successful: the alert's link opens that segment.
        openOnArrival={addCreditsFor !== null && addCreditsBlocked === null ? "successful" : null}
        segments={[
          {
            key: "successful",
            label: "Successful",
            count: successfulPayments.length,
            render: () => (
              <PaymentsTable
                rows={successfulPayments}
                emptyText="No successful payments yet."
                onChanged={() => void refresh()}
                refundedByCharge={refundedByCharge}
                memberId={m.id}
                addCreditsFor={addCreditsFor}
                onAddCreditsClosed={dropAddCreditsLink}
              />
            ),
          },
          {
            key: "lifetime",
            label: "Lifetime spend",
            count: fmtMoney(m.lifetime_spent_minor, m.currency),
            render: () => (
              <PaymentsTable
                rows={data.past_payments}
                emptyText="No payments yet."
                onChanged={() => void refresh()}
                refundedByCharge={refundedByCharge}
                memberId={m.id}
                addCreditsFor={addCreditsFor}
                onAddCreditsClosed={dropAddCreditsLink}
              />
            ),
          },
          {
            key: "upcoming",
            label: "Upcoming",
            count: fmtMoney(m.upcoming_revenue_minor, m.currency),
            render: () => (
              <UpcomingPaymentsTable
                rows={data.upcoming_payments}
                currency={data.member.currency}
              />
            ),
          },
        ]}
      />
      </>
    ),
    profile: (
      <section className="panel member-profile-card">
        <div className="member-panel-head">
          <h2>Profile details</h2>
        </div>
        <dl className="kvs member-profile-grid">
          <dt>Member ID</dt>
          <dd>
            <code>{m.id}</code>
          </dd>
          <dt>Name</dt>
          <MemberFieldEditor
            memberId={m.id}
            field="display_name"
            value={m.display_name ?? ""}
            placeholder="Member name"
            onSaved={() => void refresh()}
          />
          <dt>Email</dt>
          <MemberEmailEditor
            memberId={m.id}
            value={m.email}
            onSaved={() => void refresh()}
          />
          <dt>Phone</dt>
          <MemberFieldEditor
            memberId={m.id}
            field="phone"
            value={m.phone ?? ""}
            placeholder="+447700900000 (blank to clear)"
            onSaved={() => void refresh()}
          />
          <dt>Instagram</dt>
          <MemberInstagramEditor
            memberId={m.id}
            value={m.instagram_handle}
            onSaved={() => void refresh()}
          />
          {/* Address, Postcode and Date of birth — the personal details,
              closing the contact block that Email/Phone/Instagram open.
              Rendered ALWAYS (blank and editable when unset), the same
              treatment and for the same reason as the Source row below: all
              three arrive as loose `metadata` keys, and the loose-scalar loop
              at the foot of this card can only render a key the member ALREADY
              has. So the 2715 members with no address, the 2893 with no
              postcode and the 2506 with no date of birth had no way to be
              given one from the dashboard — while the member API's
              `update-account-details` writes address and postcode from the
              booking site, and home-session travel turns on the postcode. A
              field only staff cannot fill is the wrong asymmetry. All three
              are listed in SECTIONED_META_KEYS so the members who DO carry a
              value get this row and not a second copy from the loop. */}
          <dt>Address</dt>
          <MetadataFieldEditor
            memberId={m.id}
            metaKey="address"
            value={m.metadata["address"]}
            onSaved={() => void refresh()}
          />
          <dt>Postcode</dt>
          <MetadataFieldEditor
            memberId={m.id}
            metaKey="postcode"
            value={m.metadata["postcode"]}
            onSaved={() => void refresh()}
          />
          <dt>Date of birth</dt>
          <MetadataFieldEditor
            memberId={m.id}
            metaKey="dob"
            value={m.metadata["dob"]}
            onSaved={() => void refresh()}
          />
          <dt>Tags</dt>
          <TagsEditor
            memberId={m.id}
            tags={data.tags}
            onChanged={() => void refresh()}
          />
          <dt>Staff flag</dt>
          <StaffFlagEditor
            key={readStaffFlag(m.metadata)}
            memberId={m.id}
            initial={readStaffFlag(m.metadata)}
            onSaved={() => void refresh()}
          />
          <dt>Session feedback</dt>
          <SessionFeedbackToggle
            memberId={m.id}
            metadata={m.metadata}
            onChanged={() => void refresh()}
          />
          <dt>Gender</dt>
          <GenderEditor
            memberId={m.id}
            metadata={m.metadata}
            onChanged={() => void refresh()}
          />
          {/* Kit sizing, beside Gender because the three are one set of
              physical attributes and are captured together. Both are
              ESSENTIAL_MEMBER_PROFILE_FIELDS, so — exactly like Source below —
              they render ALWAYS, blank and editable when unset: the T3
              post-session prompt asks a trainer for the vest and trouser size
              of every member missing one, and before this the profile page
              that trainer opens had no row to put the answer in. 2112 and 2118
              of 2923 members were in that state. */}
          <dt>Vest</dt>
          <MetadataFieldEditor
            memberId={m.id}
            metaKey="vest"
            value={m.metadata["vest"]}
            onSaved={() => void refresh()}
          />
          <dt>Trouser</dt>
          <MetadataFieldEditor
            memberId={m.id}
            metaKey="trouser"
            value={m.metadata["trouser"]}
            onSaved={() => void refresh()}
          />
          {/* Source — how the member heard about the studio. An
              ESSENTIAL_MEMBER_PROFILE_FIELDS member since 2026-09-03, so the
              post-session prompt names it for every member who lacks one; it
              is rendered here ALWAYS (blank and editable when unset) because
              an empty essential field is precisely the thing staff need to
              see and fill. Shipping it without this row left 0 of 2856 live
              members with any dashboard route to the field the prompt asks
              everyone for. */}
          <dt>Source</dt>
          <MetadataFieldEditor
            memberId={m.id}
            metaKey="source"
            value={m.metadata["source"]}
            onSaved={() => void refresh()}
          />
          <dt>Contact via</dt>
          <ContactPreferenceToggles
            memberId={m.id}
            metadata={m.metadata}
            onChanged={() => void refresh()}
          />
          {/* Directly under "Contact via" because the two are constantly
              mistaken for one another and are NOT the same thing: the switches
              above are the studio's CHOICE (notifications only, staff-set),
              this row is the recipient's own hardware answering (every send
              including transactional, nobody's choice, self-lapsing). Seeing
              them adjacent is what makes the difference legible. */}
          <dt>Deliverability</dt>
          <DeliverabilityRow deliverability={m.deliverability} />
          <dt>Marketing</dt>
          <MarketingOptOutRow
            memberId={m.id}
            metadata={m.metadata}
            onChanged={() => void refresh()}
          />
          <dt>Newsletter</dt>
          <NewsletterRow
            memberId={m.id}
            standing={m.newsletter}
            join={m.newsletter_join}
            onChanged={() => void refresh()}
          />
          <dt>Post-session capture</dt>
          <PostSessionCaptureToggle
            memberId={m.id}
            metadata={m.metadata}
            onChanged={() => void refresh()}
          />
          <dt>Status</dt>
          <MemberFieldEditor
            memberId={m.id}
            field="status"
            value={m.status}
            options={["active", "suspended"]}
            onSaved={() => void refresh()}
          />
          {/* Consultation form (2026-08-20, ask 4). These ARE persisted — the
              OCR flow and the chat agent both write
              `metadata.consultation.CF_*` — but until now they rendered ONLY
              inside the "+" → Consultation form modal, so the injury and focus
              area a member had given looked unsaved. They stay listed in
              SECTIONED_META_KEYS (so they never ALSO appear as a raw jsonb
              card, which is what gives a page two copies of one fact) and get
              labelled, editable rows here instead — the same treatment
              `gender` and `staff_flag` already get. `consultation_extraction`
              deliberately stays sectioned: OCR confidences and the raw blob
              are provenance, not profile. */}
          {consultationRowKeys(readConsultation(m.metadata)).map((k) => (
            <Fragment key={`consultation.${k}`}>
              <dt title={CF_FIELD_LABEL[k] ?? prettyMetaKey(k)}>
                {CF_FIELD_LABEL[k] ?? prettyMetaKey(k)}
              </dt>
              <ConsultationFieldEditor
                memberId={m.id}
                consultation={readConsultation(m.metadata)}
                metaKey={k}
                onSaved={() => void refresh()}
              />
            </Fragment>
          ))}
          <dt>Favourite trainer</dt>
          <dd>{m.favorite_trainer ?? "—"}</dd>
          <dt>Member since</dt>
          <dd>{fmtDateTime(m.created_at)}</dd>
          <dt>Last updated</dt>
          <dd>{fmtDateTime(m.updated_at)}</dd>
          {/* Memory last recycled — beside the other bookkeeping timestamps
              rather than in the Member memory card, for two reasons. It was
              reaching the SPA and being dropped: the repository selects
              `last_recycled_at` and `MemberDetailMemory` types it, but no
              component rendered it, so the one surface that could have told an
              operator whether the monthly recycler had run showed everything
              about a member's memory EXCEPT that. And the memory card is
              `cardAvailable`-gated, so it is absent for the 2890 members with
              no memory row — putting the field there would have left it
              invisible in exactly the case where "—" is the answer. Read-only
              by design: it is a cron's idempotency key, and a staff edit to it
              would silently skip or re-run a recycle. */}
          <dt title="The monthly recycler folds a member's rolling daily memory entries into long-term memory. “Never” means the member has memory but the job has not folded it yet; “—” means they have no memory row at all.">
            Memory last recycled
          </dt>
          <dd>{memoryRecycledLabel(data.memory)}</dd>
          {/* The member's loose metadata, one ordinary row per key it actually
              carries — same shape as the fixed rows above, because a locale or
              an occupation is no less a profile field for having arrived in a
              free-form jsonb column. */}
          {partitionMetadata(generalMetadata(m.metadata)).scalar.map(
            ([k, v]) => (
              <Fragment key={k}>
                <dt title={prettyMetaKey(k)}>{prettyMetaKey(k)}</dt>
                <MetadataFieldEditor
                  memberId={m.id}
                  metaKey={k}
                  value={v}
                  onSaved={() => void refresh()}
                />
              </Fragment>
            ),
          )}
          {/* Nested metadata blobs + the imported-id pile, flattened to the
              SAME row shape rather than nested mini-cards. Read-only: these
              are vendor/import provenance and structured captures, not fields
              staff hand-edit. */}
          {metadataDetailRows(generalMetadata(m.metadata)).map(([k, v]) => (
            <Fragment key={k}>
              <dt title={k}>{k}</dt>
              <dd>{v}</dd>
            </Fragment>
          ))}
          {/* Consent, when the member carries any — rows, not a card of its
              own, and absent entirely when empty. */}
          {flattenMetaRows(m.consent, ["consent"]).map(([k, v]) => (
            <Fragment key={k}>
              <dt title={k}>{k}</dt>
              <dd>{v}</dd>
            </Fragment>
          ))}
        </dl>
      </section>
    ),
    onboarding: (
      <OnboardingSection
        metadata={m.metadata}
        memberId={m.id}
        onSaved={() => void refresh()}
      />
    ),
    // Full-width, directly under Profile details, and open — not a collapsed
    // strip. What the agent has learned about a member is prose worth reading
    // at the width it was written for, and a "+" to expand it made the page's
    // most human content the one thing you had to ask for.
    memory:
      data.memory !== null ? (
        <section className="panel member-memory-panel">
          <h2>Member memory</h2>
          <p className="member-note">
            {data.memory.rolling_day_count} day(s) in the rolling window · last
            entry {fmtDate(data.memory.last_entry_on)} · updated{" "}
            {fmtDateTime(data.memory.updated_at)}
          </p>
          <div className="member-memory-grid">
            <MemoryBlock label="Summary" body={data.memory.summary_md} />
            <MemoryBlock label="Claude notes" body={data.memory.claude_md} />
            <MemoryBlock label="Long-term" body={data.memory.longterm_md} />
            <MemoryBlock label="Rolling" body={data.memory.rolling_md} />
          </div>
        </section>
      ) : null,
    // Consent is no longer a card — it renders as ordinary rows at the foot of
    // Profile details, appearing only when the member carries any.
    consent: null,
    session_notes: (
      <SessionNotesPanel
        memberId={m.id}
        memberName={title}
        notes={data.session_notes}
        onChanged={() => void refresh()}
      />
    ),
    journey: (
      <section className="panel">
        <div className="member-panel-head">
          <h2>Journey</h2>
          <JourneyKindMenu
            kinds={layout.journeyKinds}
            onToggleKind={toggleJourneyKind}
          />
        </div>
        <Journey events={data.journey} kinds={layout.journeyKinds} />
      </section>
    ),
  };
  const cardAvailable: Record<CardId, boolean> = {
    active_flows: true,
    conversation: true,
    sessions: true,
    payments: true,
    profile: true,
    onboarding: onboardingAvailable,
    memory: data.memory !== null,
    // Never a card any more — see `cardContent.consent`.
    consent: false,
    session_notes: true,
    journey: true,
  };
  const isVisibleCard = (cid: CardId): boolean =>
    !layout.hidden.includes(cid) && cardAvailable[cid];
  const placeOf = (cid: CardId): CardPlacement =>
    layout.placement[cid] ?? CARD_BY_ID[cid].defaultPlacement;
  const zoneIds = (zone: CardPlacement): ReadonlyArray<CardId> =>
    layout.order.filter((cid) => isVisibleCard(cid) && placeOf(cid) === zone);
  const renderCell = (cid: CardId): JSX.Element => (
    <MemberCard
      key={cid}
      def={CARD_BY_ID[cid]}
      placement={placeOf(cid)}
      height={matchedHeights[cid] ?? layout.height[cid] ?? 0}
      editMode={editMode}
      dragging={drag?.id === cid}
      dropTarget={drag !== null && drag.beforeId === cid}
      onGripDown={startDrag}
      onPlacementChange={setPlacement}
      onHeightChange={setHeight}
    >
      <RecordLock locked={erased && LOCKED_WHEN_ERASED.has(cid)}>
        {cardContent[cid]}
      </RecordLock>
    </MemberCard>
  );

  return (
    <div className="member-detail-page" ref={pageRef}>
      <p className="member-breadcrumb">
        <Link to={backToMembers} className="data-member-link">
          ← Members
        </Link>
      </p>
      {/* Name with its status chips stacked beneath it, left-aligned; the
          action cluster pushed to the right of the same row. */}
      <div className="member-title-row">
        <div className="member-title-main">
          <h1 className="page-title member-title">{title}</h1>
          {/* Lifecycle status + email engagement + any channel currently being
              REFUSED. The TAGS moved to their own KPI card below — showing them
              here as well put the same nine chips on screen twice. */}
          <div className="member-title-chips">
            <Tag value={m.status} />
            {m.last_email_event !== null ? (
              <EmailEngagementChip event={m.last_email_event} />
            ) : null}
            {/* Renders nothing unless a send to this member would be refused
                right now — see `DeliverabilityChips`. */}
            <DeliverabilityChips deliverability={m.deliverability} />
          </div>
        </div>
        <MemberActions
          memberId={m.id}
          memberName={title}
          displayName={m.display_name}
          metadata={m.metadata}
          packages={data.packages}
          memberships={data.memberships}
          mergeCounts={{
            bookings:
              data.upcoming_appointments.length + data.past_appointments.length,
            payments: data.past_payments.length,
            packages: data.packages.length,
            memberships: data.memberships.length,
          }}
          consultationForms={data.consultation_forms}
          erased={erased}
          onChanged={() => void refresh()}
        />
      </div>

      {m.erased_at !== null ? (
        <ErasedNotice erasedOn={fmtDate(m.erased_at)} />
      ) : null}

      {/* Safety flags — any injury / medical / caution flagged on a session
          note, surfaced at the top so a trainer sees it before the next
          session. Each flag carries a "×" that clears it (remove_session_note_flag). */}
      <SafetyFlagsBanner
        notes={data.session_notes}
        onChanged={() => void refresh()}
      />

      {/* Staff watch flag — the free-text caution set on this member's profile,
          surfaced here AND in the trainer morning brief. The corner "×" clears it
          (update_member → empty staff_flag). */}
      <StaffFlagBanner
        memberId={m.id}
        flag={readStaffFlag(m.metadata)}
        onChanged={() => void refresh()}
      />

      {/* Headline figures — the 4 KPIs up top. Lifetime spend lives in the
          Payments card; the 4th slot is the editable staff Flag. */}
      <div className="stat-grid member-kpi-strip">
        <StatCard
          label="Available credits"
          value={String(m.available_credits)}
          {...(m.available_credits_home > 0
            ? {
                detail: `${m.available_credits_studio} studio · ${m.available_credits_home} home`,
              }
            : {})}
        />
        <StatCard label="Next session" value={fmtDateTime(m.next_session_at)} />
        <StatCard label="Next payment" value={fmtDate(m.upcoming_payment_at)} />
        <TagsStatCard tags={data.tags} />
      </div>

      <div
        className={`member-layout${editMode ? " member-layout-edit" : ""}`}
        ref={gridRef}
      >
        {/* Two INDEPENDENT columns — each packs on its own (no shared rows), so a
            short card never leaves a gap because the opposite card is tall, and a
            card can be dragged to any slot in either column. */}
        <div className="member-twocol">
          <div className="member-col member-col-left" data-zone="left">
            {zoneIds("left").map(renderCell)}
          </div>
          <div className="member-col member-col-right" data-zone="right">
            {zoneIds("right").map(renderCell)}
          </div>
        </div>
        {/* Full-width row beneath the two columns. */}
        <div className="member-fullcol" data-zone="full">
          {zoneIds("full").map(renderCell)}
        </div>
      </div>
      {drag !== null ? (
        <div
          className="member-drag-ghost"
          style={{ left: drag.x + 14, top: drag.y + 14 }}
        >
          {CARD_BY_ID[drag.id].title}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The `"Onboarding - "` key prefix the webchat FE and
 * `update-account-preferences` use inside `metadata.preferences` — the LIVE
 * home of onboarding answers (the sibling `metadata.onboarding` object is the
 * legacy Hercie-app import shape; zero live members carry it).
 */
const PREFS_ONBOARDING_PREFIX = "Onboarding - ";

/**
 * The raw `metadata.preferences` object, when it carries at least one
 * onboarding answer with a real value (the FE's `skipped` sentinels don't
 * count). Returns the WHOLE raw object — the editor must spread every sibling
 * key back on save, onboarding-prefixed or not, or the shallow metadata merge
 * would drop them.
 */
function onboardingPreferences(
  metadata: Record<string, unknown>,
): Record<string, unknown> | null {
  const prefs = readMetaObject(metadata, "preferences");
  if (prefs === null) return null;
  const hasAnswer = Object.entries(prefs).some(
    ([k, v]) =>
      k.startsWith(PREFS_ONBOARDING_PREFIX) &&
      cleanProfileFieldValue(v) !== undefined,
  );
  return hasAnswer ? prefs : null;
}

/**
 * The webchat-onboarding answers table — the same label/value grid as the
 * legacy body, sourced from `metadata.preferences`. Labels are the key minus
 * the `"Onboarding - "` prefix; values render through the shared FE-sentinel
 * cleaner (JSON-stringified multi-selects flatten, `skipped` never shows).
 * Edits write back through the SAME `preferences` object (shallow-merge-safe).
 */
function PreferencesOnboardingBody({
  prefs,
  memberId,
  onSaved,
}: {
  readonly prefs: Record<string, unknown>;
  readonly memberId: string;
  readonly onSaved: () => void;
}): JSX.Element {
  const answered = Object.keys(prefs).filter(
    (k) =>
      k.startsWith(PREFS_ONBOARDING_PREFIX) &&
      cleanProfileFieldValue(prefs[k]) !== undefined,
  );
  return (
    <dl className="kvs member-profile-grid">
      {answered.map((k) => (
        <Fragment key={k}>
          <dt title={k.slice(PREFS_ONBOARDING_PREFIX.length)}>
            {k.slice(PREFS_ONBOARDING_PREFIX.length)}
          </dt>
          <OnboardingFieldEditor
            memberId={memberId}
            answers={prefs}
            fieldKey={k}
            onSaved={onSaved}
            metaKey="preferences"
            valueText={(v) => cleanProfileFieldValue(v) ?? ""}
          />
        </Fragment>
      ))}
    </dl>
  );
}

/** The onboarding-answers collapsible — renders nothing when there are none. */
function OnboardingSection({
  metadata,
  memberId,
  onSaved,
}: {
  readonly metadata: Record<string, unknown>;
  readonly memberId: string;
  readonly onSaved: () => void;
}): JSX.Element | null {
  const obj = readMetaObject(metadata, "onboarding");
  const prefs = onboardingPreferences(metadata);
  if (obj === null && prefs === null) return null;
  // An open panel like Profile details and Member memory, not a collapsed
  // `<details>` strip: what a member said they want is reference material staff
  // read, and it was the last card still hiding its content behind a click.
  // `member-profile-card` too: it carries the label/value grid's styling, and
  // this section is the same table with a different source of answers.
  // Both bodies can render (distinct key sets); in practice a member has one.
  return (
    <section className="panel member-profile-card">
      <h2>Onboarding answers</h2>
      {obj !== null ? (
        <OnboardingBody data={obj} memberId={memberId} onSaved={onSaved} />
      ) : null}
      {prefs !== null ? (
        <PreferencesOnboardingBody
          prefs={prefs}
          memberId={memberId}
          onSaved={onSaved}
        />
      ) : null}
    </section>
  );
}

/**
 * The consultation forms photographed on WhatsApp about this member (WhatsApp
 * picture intake, 2026-10-14), newest first: where each is, who sent it, who
 * confirmed or filed it, and its photo — kept until the member is deleted
 * once confirmed, deleted after 7 days otherwise. Nothing renders for a
 * member with none.
 */
function WhatsAppConsultationForms({
  forms,
}: {
  readonly forms: ReadonlyArray<MemberDetailConsultationForm>;
}): JSX.Element | null {
  if (forms.length === 0) return null;
  return (
    <div className="pi-member-forms">
      <h4 className="pi-member-forms-title">Sent on WhatsApp</h4>
      <ul className="pi-member-forms-list">
        {forms.map((f) => {
          const ended = consultationFormEndedLine(f);
          return (
            <li key={f.id} className="pi-member-form">
              <div className="pi-member-form-head">
                <strong>{consultationFormStatusLabel(f)}</strong>
                <span className="dc-muted">{fmtDateTime(f.received_at)}</span>
              </div>
              <p className="meta-provenance">
                {consultationFormSentLine(f)}
                {ended !== null ? ` · ${ended}, ${fmtDateTime(f.ended_at)}` : ""}
              </p>
              {f.has_photo ? (
                <PictureIntakePhotoButton intakeId={f.id} />
              ) : (
                <span className="pi-photo-gone">Photo deleted</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The consultation-form collapsible. Shows the saved consultation fields + OCR
 * extraction when on file; otherwise the photo-upload OCR uploader (the same
 * gate the old standalone panel used — extraction present = already on file).
 */
function ConsultationSection({
  memberId,
  metadata,
  displayName,
  forms,
  onSaved,
}: {
  readonly memberId: string;
  readonly metadata: Record<string, unknown>;
  readonly displayName: string | null;
  /** Form photos sent on WhatsApp about this member, newest first. */
  readonly forms: ReadonlyArray<MemberDetailConsultationForm>;
  readonly onSaved: () => void;
}): JSX.Element {
  const consultation = readMetaObject(metadata, "consultation");
  const extraction = readMetaObject(metadata, "consultation_extraction");
  // Rows a save left as they were because the member's answer arrived after
  // the form was read — kept here, since the uploader unmounts on save.
  const [keptLabels, setKeptLabels] = useState<ReadonlyArray<string>>([]);
  // Body only — the header's "+" menu opens this inside a modal that supplies
  // the title and the close control.
  return (
    <>
      <WhatsAppConsultationForms forms={forms} />
      {keptLabels.length > 0 ? (
        <p className="member-note">
          Kept the member&apos;s own {keptLabels.join(", ")} — filled in after
          the form was read, so the form&apos;s value was not saved over it.
        </p>
      ) : null}
      {consultation !== null ? (
        <MetaSubGrid obj={consultation} labels={CF_FIELD_LABEL} />
      ) : null}
      {extraction !== null ? (
        <ConsultationExtractionBody data={extraction} />
      ) : (
        <>
          <p className="member-note">
            Upload a photo of the member&apos;s paper consultation form to
            auto-extract their goal, focus areas, injuries, kit sizing, postcode and referral source. You
            review and edit before anything is saved.
          </p>
          <ConsultationFormUploader
            memberId={memberId}
            metadata={metadata}
            hasName={
              typeof displayName === "string" && displayName.trim().length > 0
            }
            onSaved={(kept) => {
              setKeptLabels(kept);
              onSaved();
            }}
          />
        </>
      )}
    </>
  );
}

function StatCard({
  label,
  value,
  detail,
}: {
  readonly label: string;
  readonly value: string;
  /** Optional small sub-line under the value (e.g. a studio/home breakdown). */
  readonly detail?: string;
}): JSX.Element {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className="value member-stat-value">{value}</div>
      {detail !== undefined ? (
        <div style={{ marginTop: 4, fontSize: "0.8rem", opacity: 0.7 }}>
          {detail}
        </div>
      ) : null}
    </div>
  );
}

/** One labelled memory section — an expanded-by-default `<details>` over the
 *  markdown body (staff can collapse it). Renders nothing when the body is
 *  empty. */
function MemoryBlock({
  label,
  body,
}: {
  readonly label: string;
  readonly body: string;
}): JSX.Element | null {
  if (body.trim().length === 0) return null;
  // A labelled block, not a <details>: the card is open by default now, so a
  // disclosure arrow beside an always-open section is a control that does
  // nothing useful.
  return (
    <div className="member-memory-block">
      <div className="member-memory-label">{label}</div>
      <pre className="memory-md">{body}</pre>
    </div>
  );
}

/**
 * Renders a recorded 1–3 session rating (2026-08-04 — the tap-to-rate button
 * scale; 1 = poor, 3 = excellent) as filled/empty stars + the numeric value,
 * with the optional free-text comment surfaced on hover. `—` when the session
 * has not been rated yet.
 */
function RatingStars({
  value,
  comment,
}: {
  readonly value: number | null;
  readonly comment: string | null;
}): JSX.Element {
  if (value === null) return <span className="rating-empty">—</span>;
  const filled = Math.max(0, Math.min(3, Math.round(value)));
  return (
    <span
      className="rating-stars"
      title={comment !== null && comment.trim().length > 0 ? comment : undefined}
    >
      <span className="rating-stars-glyphs" aria-hidden="true">
        {"★".repeat(filled)}
        {"☆".repeat(3 - filled)}
      </span>{" "}
      <span className="rating-stars-num">{value}/3</span>
      {comment !== null && comment.trim().length > 0 ? (
        <span className="rating-stars-note">💬</span>
      ) : null}
    </span>
  );
}

function AppointmentsTable({
  rows,
  showAttendance,
  onChanged,
  memberId,
  swapPackages,
  swapMemberships,
  waiverAvailable = false,
}: {
  readonly rows: ReadonlyArray<MemberDetailAppointment>;
  readonly showAttendance: boolean;
  /**
   * 2026-10-06 (first-late-cancel-waiver) — the member's next late change is
   * free (the detail payload's `late_change_waiver.available`); threaded to
   * the per-row Cancel modal so its late hint says the credit comes back.
   */
  readonly waiverAvailable?: boolean;
  /** When provided (past appointments), each row gets attendance + rating actions. */
  readonly onChanged?: () => void;
  /** Needed for the per-row `rate_session` + `swap_credit` actions. */
  readonly memberId?: string;
  /** #15 — the member's credit sources; present enables the per-row "Swap credit". */
  readonly swapPackages?: ReadonlyArray<MemberDetailPackage>;
  readonly swapMemberships?: ReadonlyArray<MemberDetailMembership>;
}): JSX.Element {
  // #15 — which row's Swap-credit modal is open (booking id), or null.
  const [swapOpenId, setSwapOpenId] = useState<string | null>(null);
  // C3 — which row's Cancel modal is open. Same `cancel_booking` component the
  // calendar's slot popup uses, so the two can't drift.
  const [cancelOpenId, setCancelOpenId] = useState<string | null>(null);
  if (rows.length === 0) {
    return <p className="empty">None.</p>;
  }
  const attendanceActions = showAttendance && onChanged !== undefined;
  // #15 — swap is offered when the member's sources are threaded down + we can
  // refresh. A row is swappable when it still HOLDS a credit against a source:
  // active/completed always, plus a cancelled-but-KEPT (late cancel) row whose
  // credit was forfeited to the studio (still drawn from a source, so which
  // source covers it can be corrected). A deleted tombstone, an early cancel
  // that RETURNED the credit, and a legacy cancel with an unknown outcome are
  // excluded — parity with the backend `swap_credit` guard.
  const swapEnabled =
    swapPackages !== undefined &&
    swapMemberships !== undefined &&
    memberId !== undefined &&
    onChanged !== undefined;
  const rowSwappable = (a: MemberDetailAppointment): boolean =>
    swapEnabled &&
    a.credit_source !== null &&
    a.status !== "deleted" &&
    (a.status !== "cancelled" || a.credit_restored === false);
  // C3 — Cancel is offered on the UPCOMING tab only (`showAttendance` marks the
  // Past one), and only for a still-active booking. Cancelling a session that
  // already happened is what `record_attendance` is for.
  const cancelEnabled = !showAttendance && onChanged !== undefined;
  const rowCancellable = (a: MemberDetailAppointment): boolean =>
    cancelEnabled && a.status === "active";
  const showActions = attendanceActions || swapEnabled || cancelEnabled;
  return (
    <div className="table-scroll-x">
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>Trainer</th>
            <th>Type</th>
            <th>Location</th>
            <th>Status</th>
            {showAttendance ? <th>Attendance</th> : null}
            {showAttendance ? <th>Rating</th> : null}
            {showAttendance ? <th>Credit</th> : null}
            {showActions ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td data-label="When">{fmtDateTime(a.starts_at)}</td>
              <td data-label="Trainer">{a.trainer_name ?? "—"}</td>
              <td data-label="Type">{a.session_type ?? "—"}</td>
              <td data-label="Location">{a.location ?? "—"}</td>
              <td data-label="Status">
                <Tag value={a.status} />
              </td>
              {showAttendance ? (
                <td data-label="Attendance">
                  <Tag value={a.attendance_status} />
                </td>
              ) : null}
              {showAttendance ? (
                <td data-label="Rating">
                  <RatingStars value={a.rating} comment={a.rating_comment} />
                </td>
              ) : null}
              {showAttendance ? (
                <td data-label="Credit">
                  {a.credit_outcome === "returned" ? (
                    <span
                      className="credit-outcome credit-returned"
                      title="Credit was returned to its source on cancel"
                    >
                      ☑ Returned
                    </span>
                  ) : a.credit_outcome === "waived" ? (
                    <span
                      className="credit-outcome credit-waived"
                      title="Late change inside the cancellation window — the credit was returned because this was the member's one free late change, which is now used"
                    >
                      ☑ Returned · first late change waived
                    </span>
                  ) : a.credit_outcome === "kept" ? (
                    <span
                      className="credit-outcome credit-kept"
                      title="Credit was kept (late cancel / staff kept it)"
                    >
                      ☐ Kept
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              ) : null}
              {showActions ? (
                <td className="data-td-actions">
                  <div className="row-actions">
                  {attendanceActions ? (
                  <>
                  <ActionModalButton
                    action="record_attendance"
                    buttonLabel="Attendance"
                    buttonClassName="row-action-btn"
                    modalTitle="Record attendance"
                    description={`Mark attendance for the session on ${fmtDateTime(a.starts_at)}.`}
                    submitLabel="Save attendance"
                    prefill={{ booking_id: a.id }}
                    fields={[
                      {
                        kind: "select",
                        name: "attendance_status",
                        label: "Attendance",
                        required: true,
                        defaultValue:
                          a.attendance_status === "unconfirmed"
                            ? "attended"
                            : a.attendance_status,
                        options: [
                          { value: "attended", label: "Attended" },
                          { value: "no_show", label: "No-show" },
                          { value: "cancelled", label: "Cancelled" },
                          { value: "unconfirmed", label: "Unconfirmed" },
                        ],
                      },
                    ]}
                    onDone={() => onChanged?.()}
                  />
                  {memberId !== undefined ? (
                    <ActionModalButton
                      action="rate_session"
                      buttonLabel={a.rating !== null ? "Re-rate" : "Rate"}
                      buttonClassName="row-action-btn"
                      modalTitle="Record session feedback"
                      description={`Log a 1–3 satisfaction rating for the session on ${fmtDateTime(a.starts_at)}.`}
                      submitLabel="Save rating"
                      prefill={{ member_id: memberId, booking_id: a.id }}
                      fields={[
                        {
                          kind: "select",
                          name: "rating",
                          label: "Rating",
                          required: true,
                          numeric: true,
                          /* The ACTION's scale, not a nicer-looking one.
                             `rate_session`'s input schema is
                             `rating: {type: "integer", minimum: 1, maximum: 3}`
                             and its agentDescription says "1-3 ... (1 = poor,
                             3 = excellent)". This modal offered 1–5, so
                             choosing 4 or 5 — the two options a rater reaches
                             for first — failed validation at the chokepoint
                             and came back as the bare
                             "input_invalid: input body failed JSON Schema"
                             (Mario, 2026-09-12). The sibling that RENDERS a
                             recorded rating already says 1–3; only the writer
                             was left behind. */
                          options: [
                            { value: "3", label: "3 — Excellent" },
                            { value: "2", label: "2 — OK" },
                            { value: "1", label: "1 — Poor" },
                          ],
                        },
                        {
                          kind: "textarea",
                          name: "comment",
                          label: "Comment (optional)",
                          maxLength: 2000,
                        },
                      ]}
                      onDone={() => onChanged?.()}
                    />
                  ) : null}
                  </>
                  ) : null}
                  {rowSwappable(a) ? (
                    <button
                      type="button"
                      className="row-action-btn"
                      onClick={() => setSwapOpenId(a.id)}
                    >
                      Swap credit
                    </button>
                  ) : null}
                  {rowCancellable(a) ? (
                    <button
                      type="button"
                      className="row-action-btn row-action-btn-danger"
                      onClick={() => setCancelOpenId(a.id)}
                    >
                      Cancel
                    </button>
                  ) : null}
                  {/* C3 — open this session's popup on the calendar. `date`
                      anchors the view on the session's STUDIO day; without it
                      a slot outside the current week isn't in the loaded
                      range and there'd be nothing to select. */}
                  <Link
                    to={buildCalendarDeepLink(a.slot_id, a.starts_at)}
                    className="row-action-btn"
                    title="Open this session on the calendar"
                  >
                    Calendar
                  </Link>
                  </div>
                  {cancelOpenId === a.id ? (
                    <MetaModal
                      title="Cancel this session"
                      onClose={() => setCancelOpenId(null)}
                    >
                      <CancelBookingModal
                        bookingId={a.id}
                        startsAt={a.starts_at}
                        waiverAvailable={waiverAvailable}
                        className="cancel-booking-stack"
                        hint={`Cancelling the session on ${fmtDateTime(a.starts_at)}. Pick whether the credit goes back to its source or stays spent.`}
                        onDone={() => {
                          setCancelOpenId(null);
                          onChanged?.();
                        }}
                        onClose={() => setCancelOpenId(null)}
                      />
                    </MetaModal>
                  ) : null}
                  {swapOpenId === a.id ? (
                    <MetaModal
                      title="Swap credit source"
                      onClose={() => setSwapOpenId(null)}
                    >
                      <SwapCreditModal
                        bookingId={a.id}
                        currentCreditSource={a.credit_source}
                        currentSourceId={
                          a.credit_source === "package"
                            ? a.member_package_id
                            : a.member_membership_id
                        }
                        packages={swapPackages ?? []}
                        memberships={swapMemberships ?? []}
                        onDone={() => {
                          setSwapOpenId(null);
                          onChanged?.();
                        }}
                      />
                    </MetaModal>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
